/**
 * BooksPage.tsx — 书籍管理页（books 域，默认导出 BooksPage）
 *
 * 1:1 移植自原型 design/Novel-admin/src/pages/books.jsx（2034 行）。
 * 列表（books / 分类 / 标签三 tab）+ 书籍详情（信息 / 章节 / 数据 / 上下架 / 日志五 tab）+ 客户端预览 / 定时发布弹窗。
 * 机械转换：JS→TS（加严格类型）+ window.X 全局引用 → ESM import：
 *   - 取数 window.NA_DATA / window.SYS_CFG → useBooksPage() / useChapters() / useSysConfig() / useApps() / useDashboard()
 *   - window.toast → ~/ui/Toast 的 toast()
 *   - window.openDialog('confirm', …) → ~/ui/Dialog 的 ConfirmDialog（本页本地 open state）
 *   - window.openDialog('new-book' / 'chapter' / 'category-editor', …) → ./dialogs/* 受控 open state
 *   - window.fmtLang / window.fmtCountry → ~/lib/format
 *   - window.__multiLang（ReasonModal 用）→ ~/ui/MultiLangFormRows
 * JSX / className / inline-style 与原型逐字一致；列表 key 优先 row.id；数组下标访问加守卫（noUncheckedIndexedAccess）。
 */

import type {
  BookListQuery,
  BookOperationLogItem,
  NovelGroupDetail,
  NovelResponse,
  PublishQueueStatus,
  SystemEventLogItem,
  UpdateNovelGroupRequest,
  UpdateNovelRequest,
} from '@novel-admin/shared';
import type { NovelVisibility } from '@novel-admin/shared';
import {
  EDITION_LABEL_MAX_LENGTH,
  initialPriceLevelForLengthClass,
  isTranslationFactoryProviderCode,
  parseEditionLabelDraft,
  resolveEffectiveVisibility,
} from '@novel-admin/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Fragment,
  type ReactNode,
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppScope } from '~/app/app-scope-store';
import {
  type BookRow,
  type BookStatsData,
  type CatTagRow,
  type ChapterRow,
  appQueryKeys,
  useApps,
  useBookAppStatus,
  useBookDetail,
  useBookOperationLogs,
  useBookStats,
  useBooksPage,
  useCategoriesList,
  useChapters,
  useSysConfig,
  useTagsList,
} from '~/data/queries';
import { booksApi, categoriesApi, chaptersApi, tagsApi } from '~/hooks/use-books';
import { isAdminInternalLocale } from '~/lib/admin-internal-locale';
import { humanizeAuditAction } from '~/lib/audit-action-meta';
import {
  AuditDiffTable,
  type AuditFieldDiff,
  auditOperationContext,
  auditOperationSummary,
  auditOperationTimeLabel,
  deriveAuditDiff,
} from '~/lib/audit-display';
import { bookGroupStatusSummary } from '~/lib/book-group-status-summary';
import { BOOK_SEARCH_PLACEHOLDER } from '~/lib/book-identity';
import { delistReasonRequiredErrorMessage } from '~/lib/delist-reason-error';
import {
  fmtCountry,
  fmtDate,
  fmtDateTime,
  fmtDateTimeInTimeZone,
  fmtLang,
  fmtNum,
  langName,
} from '~/lib/format';
import { Icons } from '~/lib/icons';
import {
  DISCOVERY_REACH_LABEL,
  INHERIT_DESC,
  INHERIT_LABEL,
  SOURCE_LAYER_HINT,
  SOURCE_LAYER_LABEL,
  VISIBILITY_CONFIRM_MESSAGE,
  VISIBILITY_META,
  VISIBILITY_OPTIONS,
  discoveryReach,
  visibilityChipClass,
  visibilityDisplayOf,
  visibilityDot,
  visibilityLabel,
} from '~/lib/novel-visibility-display';
import { staleSnapshotErrorMessage } from '~/lib/stale-snapshot-error';
import { useAuthStore } from '~/stores/auth-store';
import { AppIcon } from '~/ui/AppIcon';
import { BookCover } from '~/ui/BookCover';
import { ContextMenu, type ContextMenuItem } from '~/ui/ContextMenu';
import { CopyableCode } from '~/ui/CopyableCode';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { type FormRowProps, FormRow as UIFormRow } from '~/ui/FormRow';
import { LangChip } from '~/ui/LangChip';
import { MultiLangFormRows } from '~/ui/MultiLangFormRows';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { Section } from '~/ui/Section';
import { Select } from '~/ui/Select';
import { DetailSkeleton, Sk } from '~/ui/Skeleton';
import { toast } from '~/ui/Toast';
import './books-group-list.css';
import { Table } from '~/ui/Table';
import { useCanChangeBookStatus, useCanViewBookSource, useCanWriteBooks } from './book-rbac';
import { CategoryEditor, type CategoryEditorItem } from './dialogs/CategoryEditor';
import { ChapterEditor } from './dialogs/ChapterEditor';
import { NewBookDialog } from './dialogs/NewBookDialog';
import { NewLanguageDialog } from './dialogs/NewLanguageDialog';
import { ScheduleChapterDialog } from './dialogs/ScheduleChapterDialog';

const AGE_RATING_LABEL: Record<NovelResponse['age_rating'], string> = {
  ALL_AGES: '全年龄',
  TEEN_13: '12+',
  MATURE_16: '16+',
  ADULT_18: '18+',
};

const PUBLISH_QUEUE_POLL_INTERVAL_MS = 5000;

// 路由 bookId 只有合法 UUID 才可驱动精确列表查询；空值 / 畸形值必须回退普通搜索。
const BOOK_ROUTE_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidRouteBookId(value: string | null): value is string {
  return value !== null && BOOK_ROUTE_UUID_PATTERN.test(value);
}

export function publishQueueRefetchInterval(
  status: PublishQueueStatus | undefined,
): number | false {
  return status === 'PENDING' || status === 'CLAIMED' ? PUBLISH_QUEUE_POLL_INTERVAL_MS : false;
}

// 书籍信息编辑表单可保存字段集（仅纳入 UpdateNovelRequest 真实支持的字段）
// - per-locale 文案（写选中语言行）：title / description / authorDisplay
// - per-locale 配置（写选中语言行）：ageRating / categoryId / tags / rating / costPerKw
// - 来源维护字段：IMPORTED 书的 originalAuthor / sourceExternalId（operator+）
type BookEditForm = {
  title: string;
  description: string;
  recommendationTextI18n: Record<string, string>;
  recommendationTextEdited: boolean;
  // per-locale 封面 ref（novels.cover_url）：上传后存相对 ref；空串=未改/未设（保存时省略不下发）
  coverUrl: string;
  // 本地预览（刚上传文件的 objectURL）：admin 无 C 端 origin 解析存量相对 ref，仅对刚上传文件即时预览
  coverPreview: string | null;
  authorDisplay: string;
  ageRating: NovelResponse['age_rating'];
  originalAuthor: string;
  sourceExternalId: string;
  // per-locale：分类 / 标签 / 运营展示评分 / 千字默认单价
  categoryId: string;
  tags: string[];
  rating: number;
  // 千字默认单价（cost_default_per_kw，COIN 整数串；写既有 C 端列）
  costPerKw: string;
  lengthClass: NovelResponse['length_class'];
  priceLevel: NovelResponse['price_level'];
  /** ADM-BOK-S020 买断启用（per-locale；置 true 需该行已有价格档）*/
  isNovelPurchaseEnabled: boolean;
  limitedFreeEnabled: boolean;
  isRecommended: boolean;
  /** ADM-BOK-S005 免费章节数（0..1000 十进制串；保存时才 parse）*/
  freeChapterCount: string;
  /** ADM-BOK-S024 改写版本标签草稿（空串 = 原版；保存时 trim 后比对载入值）*/
  editionLabel: string;
};

// 契约缺端点动作的统一禁用标注（计划 §五 NOT-in-scope 裁决）
const NOT_IN_SCOPE_TITLE =
  '待契约（NOT-in-scope，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）';
// 角色不足时写动作入口的统一禁用标注（前端 RBAC 防线；后端 requireRole 才是权威）
const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';
/** 审计 diff 里属「采集来源信息」的字段：非 root 不展示这些行（同列表来源列 / 详情外部采集区块）*/
const BOOK_SOURCE_AUDIT_FIELDS = new Set(['source', 'source_provider', 'source_external_id']);

/** PG integer 上界；基数输入钳在此值，避免提交后被后端判 VALIDATION_FAILED */
const BASE_COUNT_MAX = 2_147_483_647;

/**
 * ADM-BOK-S024 改写版本 tag 文案：标签是自由短文本（如 `A` / `新B`），列表与详情页头统一显示成「B 版」；
 * 运营录入时已带「版」字的不再重复追加。
 */
function editionLabelTagText(label: string): string {
  return label.endsWith('版') ? label : `${label} 版`;
}

/** 改写版本 tag：有效标签为空（原版）不渲染。只用既有 `chip` 原子类。 */
function EditionLabelTag({
  label,
  fontSize = 10,
}: {
  label: string | null | undefined;
  fontSize?: number;
}) {
  if (!label) return null;
  return (
    <span className="chip" style={{ fontSize, flexShrink: 0 }} title={`改写版本 · ${label}`}>
      {editionLabelTagText(label)}
    </span>
  );
}

/** S005 免费章节数：合法整数 0..1000，否则 null（禁保存 / 不下发）。 */
function parseFreeChapterCount(raw: string): number | null {
  if (!/^(0|[1-9][0-9]*)$/.test(raw.trim())) return null;
  const n = Number(raw.trim());
  if (n < 0 || n > 1000) return null;
  return n;
}

/**
 * 数据基数编辑是否可用（ADM-BOK-S017）——**端到端就绪门禁**，两个条件都按「响应里有没有这个字段」
 * 判（不看 TS 类型声明），详见 `docs/plans/2026-07-23-book-display-base-catchup.md` §3.1：
 *
 * ① `read_count` / `favorite_count` 在书组详情顶层 —— admin-api 已到含 S017 的版本。部署序是
 *    Web→Server（`.claude/commands/deploy.md`），新 Web 撞旧 admin-api 时旧 schema 会静默剥离这
 *    两个键、PATCH 仍因其它 group 字段合法而返 200 → 运营看到「保存成功」但没落库。
 * ② `real_read_count` / `real_favorite_count` 也在顶层 —— **C 端 serving 合成已就绪**的可探测代理：
 *    这两列由 novel-app-server 落 C 端 migration 后 admin 镜像跟进才会出现。在此之前 C 端仍按
 *    **选中 locale 行**返回计数，而基数只写 root 行 → 改基数只有 root 语言的客户端会变，其它语言
 *    仍显示各自行的值（通常 0），跨语言不一致且运营无从察觉。
 */
/**
 * 书籍信息保存的**部分成功**：行级 PATCH 已落库、书组级 PATCH 失败（dd#970 把书组字段拆成
 * 独立端点后，有书组改动的保存必然是两次 HTTP commit，没有跨端点事务）。
 *
 * 单独立一个错误类型而不是复用原错：通用「保存失败」会让运营以为整笔都没写，于是照旧值再改一遍、
 * 把已生效的行级改动又覆盖回去。`cause` 保留原错，供下游继续识别 422 冲突等具体原因。
 */
class PartialSaveError extends Error {
  constructor(override readonly cause: unknown) {
    super('locale row saved, book group update failed');
    this.name = 'PartialSaveError';
  }
}

function baseCountsSupported(detail: NovelGroupDetail | null | undefined): boolean {
  if (!detail) return false;
  const d = detail as {
    read_count?: unknown;
    favorite_count?: unknown;
    real_read_count?: unknown;
    real_favorite_count?: unknown;
  };
  return (
    typeof d.read_count === 'number' &&
    typeof d.favorite_count === 'number' &&
    typeof d.real_read_count === 'number' &&
    typeof d.real_favorite_count === 'number'
  );
}

/**
 * 数字串钳到 int4 上界（编辑中允许空串，失焦 / 保存时按 0 归一）。
 * 超长粘贴（~308 位以上）会让 `Number()` 变成 `Infinity`——必须钳到上界，
 * 否则会落成字面串 "Infinity"，再被 normalizeBaseCount 归一成 0，把已有基数悄悄清零。
 */
function clampBaseCount(digits: string): string {
  if (digits === '') return '';
  const n = Number(digits);
  if (!Number.isFinite(n)) return String(BASE_COUNT_MAX);
  return String(Math.min(n, BASE_COUNT_MAX));
}

/** 提交口径：空串 / 非数字按 0（清空 = 把基数设为 0，而非「不改」）；非有限值钳上界，不当成 0 */
function normalizeBaseCount(digits: string): number {
  const n = Number(digits);
  if (!Number.isFinite(n)) return digits.trim() === '' ? 0 : BASE_COUNT_MAX;
  return n > 0 ? Math.min(Math.trunc(n), BASE_COUNT_MAX) : 0;
}

/**
 * ADM-BOK-S017 数据基数单行（阅读 / 收藏）——对齐高保真稿 BaseNumRow：
 * 图标 + 中英标签 + 基数输入 + 「+ 真实 N = 客户端展示 N」。
 *
 * `real` 取书组详情顶层的 `real_read_count` / `real_favorite_count`（C 端服务端写的物化列）。
 * 客户端展示值直接用后端派生的 `display_*`，**不在前端做 base + real**：合成口径单点在服务端
 * （`03-数据字典/20-内容.md` §1.1），前端自算会在口径变化时静默漂移出与 C 端不一致的数字。
 *
 * 编辑中的基数尚未落库，此时展示值按「当前输入 + 真实」即时预估（`pendingDisplay`），让运营能
 * 预判改动效果；未编辑时恒显示后端下发的 `display`。真实值缺失（后端未返该字段）→ 显示「—」不编 0。
 */
function BaseCountRow({
  icon,
  zh,
  en,
  base,
  real,
  display,
  onChange,
  disabled = false,
}: {
  icon: 'IconEye' | 'IconHeart';
  zh: string;
  en: string;
  base: string;
  /** 真实聚合值（后端 real_*）；未就绪为 null → 真实值与展示值都显示「—」 */
  real: number | null;
  /** 后端派生的客户端展示值（display_*）；未就绪为 null */
  display: number | null;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const Ic = Icons[icon];
  // 编辑中按「当前输入 + 真实」即时预估；输入与后端基数一致时回落到后端 display（口径以服务端为准）
  const pendingDisplay = real === null ? null : normalizeBaseCount(base) + real;
  return (
    <div className="hstack" style={{ gap: 8, alignItems: 'center' }}>
      <span className="hstack" style={{ gap: 6, width: 120, flexShrink: 0, fontSize: 12 }}>
        <Ic size={12} style={{ color: 'var(--fg-muted)' }} />
        {zh}
        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
          {en}
        </span>
      </span>
      <input
        className="input mono"
        value={base}
        // 只收数字并钳到 int4 上界：`novel_groups.read_count` 是 PG integer，超界会被后端 422（更早：DB 拒）
        onChange={(e) => onChange(clampBaseCount(e.target.value.replace(/[^\d]/g, '')))}
        // 失焦即把空串显式回填 0：让「清空 = 设为 0」在保存前就可见，不留歧义
        onBlur={() => {
          if (base === '') onChange('0');
        }}
        placeholder="0"
        style={{ width: 96, textAlign: 'right' }}
        aria-label={`${zh}展示基数`}
        disabled={disabled}
      />
      <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
        + 真实 {real === null ? '—' : real.toLocaleString()}
      </span>
      <span style={{ color: 'var(--fg-faint)', fontSize: 11 }}>=</span>
      <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
        客户端展示{' '}
        <span className="mono" style={{ color: 'var(--fg)', fontWeight: 600, fontSize: 12.5 }}>
          {(pendingDisplay ?? display) === null
            ? '—'
            : (pendingDisplay ?? display ?? 0).toLocaleString()}
        </span>
      </span>
    </div>
  );
}

function formatWanWords(words: number | null | undefined): string {
  if (words === null || words === undefined) return '—';
  return `${(words / 10000).toFixed(1)}万`;
}

// 本页通用二次确认弹窗的入参（替代原型 window.openDialog('confirm', …)）
interface ConfirmReq {
  title: string;
  message: string;
  tone?: 'default' | 'warn' | 'danger';
  icon?: keyof typeof Icons;
  confirmLabel?: string;
  cancelLabel?: string;
  requireTyping?: string;
  onConfirm?: () => void;
}

// =========================================================
// BooksPage — 列表 / 分类 / 标签 + 详情入口
// =========================================================
export default function BooksPage() {
  const { data: sys } = useSysConfig();
  // 分类 / 标签真实计数（sub-tab 徽标），替代原型写死的 8 / 22
  const { data: catList } = useCategoriesList();
  const { data: tagList } = useTagsList();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeBookIdParam = searchParams.get('bookId');
  const routeBookId = isValidRouteBookId(routeBookIdParam) ? routeBookIdParam : null;
  const [selected, setSelected] = useState<string | null>(() => routeBookId);
  const [openedBook, setOpenedBook] = useState<BookRow | null>(null);
  const [tab, setTab] = useState<'books' | 'cats' | 'tags'>('books');
  const [q, setQ] = useState('');
  // 筛选状态现持后端**机器码**（非中文标签），直接下推服务端（ADM-BOK-S006，P1 回归修复）：
  //   visibilityFilter：LISTED/HIDDEN/DELISTED 单值（'全部'=不传）；serials：ONGOING/COMPLETED/HIATUS 多值；
  //   sources：ORIGINAL/LICENSED/IMPORTED 多值；categories/tags：真实 UUID 多值（取自 catList/tagList）；
  //   langs：locale code 多值（原本即 code，不变）。
  // 受众档筛选（A6 §15.1 `visibility` query）：比较值 = COALESCE(层②, 层①)，**不含层③**
  //   —— 本端点是跨 App 的书组列表、无 App scope 入参，某 App 下的实际 effective 值须按 §10 逐 App 查。
  // 旧的三态 seg（LISTED/HIDDEN/DELISTED）随 dd#941 四档化退场；「草稿」在四档模型里没有对应档位，
  //   新建书的 fail-closed 由层① 缺省 `UNLOCKED_ONLY` 承担（字典 §6.0「新建路径」）。
  const [visibilityFilter, setVisibilityFilter] = useState<'全部' | NovelVisibility>('全部');
  const [categories, setCategories] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [serials, setSerials] = useState<string[]>([]);
  const [recommendationFilter, setRecommendationFilter] = useState<string[]>([]);
  const [langs, setLangs] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  // 本页弹窗 open state（替代原型 window.openDialog hub）
  const [confirm, setConfirm] = useState<ConfirmReq | null>(null);
  const [newBookOpen, setNewBookOpen] = useState(false);
  const [catEditor, setCatEditor] = useState<{
    kind: 'cats' | 'tag';
    item?: CategoryEditorItem;
  } | null>(null);
  const removeRouteBookId = useCallback(() => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        nextParams.delete('bookId');
        return nextParams;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const pageSize = useAppScope((s) => s.tablePageSize);
  // 把页面筛选状态塑成后端 BookListQuery（仅含非空维度）：服务端下推筛选，不再本页客户端过滤单页。
  const effectiveBookQuery = routeBookId ?? q.trim();
  const bookFilters: BookListQuery = {
    ...(effectiveBookQuery ? { q: effectiveBookQuery } : {}),
    ...(visibilityFilter !== '全部' ? { visibility: visibilityFilter } : {}),
    ...(sources.length ? { source: sources as BookListQuery['source'] } : {}),
    ...(categories.length ? { category: categories } : {}),
    ...(tags.length ? { tag: tags } : {}),
    ...(langs.length ? { lang: langs } : {}),
    ...(serials.length ? { serial_status: serials as BookListQuery['serial_status'] } : {}),
    ...(recommendationFilter.length ? { is_recommended: recommendationFilter[0] === 'true' } : {}),
  };
  const {
    data: BOOKS,
    isLoading: booksLoading,
    isSuccess: booksQuerySucceeded,
    total: booksTotal,
  } = useBooksPage({
    page,
    limit: pageSize,
    filters: bookFilters,
  });

  // 空值 / 畸形 bookId 不进入 selected 或服务端 q，并用 replace 清理，避免污染历史记录。
  useEffect(() => {
    if (routeBookIdParam === null || routeBookId !== null) return;
    setSelected(null);
    removeRouteBookId();
  }, [routeBookIdParam, routeBookId, removeRouteBookId]);

  // UUID 精确查询完成仍无目标时视为失效深链（如书籍已软删），清理路由后恢复普通列表搜索。
  // 其他语言书可能不在当前页：若能从本页任一行的 sibling 还原，就打开那本书，不要清路由。
  useEffect(() => {
    if (!routeBookId || !booksQuerySucceeded) return;
    if (BOOKS.some((book) => book.id === routeBookId)) return;
    if (openedBook?.id === routeBookId) return;
    const host = BOOKS.find((book) =>
      book.languageCodes.some((row) => row.novelId === routeBookId),
    );
    if (host) {
      const locale = host.languageCodes.find((row) => row.novelId === routeBookId)?.locale;
      const next = locale ? bookRowForSibling(host, locale) : null;
      if (next) {
        setOpenedBook(next);
        setSelected(next.id);
        return;
      }
    }
    setSelected(null);
    removeRouteBookId();
  }, [routeBookId, booksQuerySucceeded, BOOKS, openedBook, removeRouteBookId]);

  // 翻页偏好或**任一筛选维度**变化都重置回第一页：offset 分页下旧 page 在新 filter 集的总数下可能越界成空页，
  // 回首页避免「换筛选后停在第 N 页但结果不足 N 页」的空白。filterKey 进 queryKey 触发重取。
  // 依赖里数组维度 join 成稳定字符串，避免每渲染新数组引用触发误重置。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 依赖故意用筛选派生标量/字符串而非数组引用。
  useEffect(() => {
    setPage(1);
  }, [
    pageSize,
    q,
    routeBookId,
    visibilityFilter,
    sources.join(','),
    categories.join(','),
    tags.join(','),
    langs.join(','),
    serials.join(','),
    recommendationFilter.join(','),
  ]);

  // 越界页防空表（offset 分页）：total 收缩（并发下架/删除/筛选重取）后若停在超出真实总页数的 page，
  // 后端会返 data=[] 但 total>0，分页器页码看着正常、表格却空。加载完成后把 page clamp 回 maxPage 并重取，
  // 避免操作员卡在空表只能手动翻回。booksLoading 时 booksTotal 可能是 0/陈旧，跳过不误 clamp。
  useEffect(() => {
    if (booksLoading) return;
    const maxPage = Math.max(1, Math.ceil(booksTotal / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [booksTotal, pageSize, page, booksLoading]);

  // offset 分页：可跳任意页（与 cursor 不同，无需 cursor 缓存校验）
  const changeBookPage = (nextPage: number) => setPage(nextPage);

  // 列表行的层② 受众档切换（A6 §12.2 `POST /books/:id/visibility`）：四档中除 DELISTED 外 + 「继承」
  //   都无需原因，可在行内直接改。
  // `visibility: null` 与 `PUBLIC` **不是一回事**（§12.2 明写）：前者清除本层覆盖、跟随书组默认层变动，
  //   后者把这一本钉死为全体可见。菜单里两项并列给出，不合并。
  // DELISTED 需 status_reason_i18n.en（行内 confirm 无原因录入）→ 路由到详情「上下架」tab 填原因，
  //   不在列表行假成功（详情 BookStatus 已接真）。RBAC root/admin（前端门控，后端 requireRole 为权威）。
  const canChangeStatus = useCanChangeBookStatus();
  const canViewSource = useCanViewBookSource();
  // 「新建书籍」入口的前端 RBAC 镜像（后端 requireRole 为权威）：本页其余写入口都已按此门控，
  // 只有这个按钮漏了 —— viewer 点开弹窗、填完再被后端 403，是把失败推迟到最后一步
  const canWriteBooks = useCanWriteBooks();
  const queryClient = useQueryClient();
  // 列表导出 CSV（接真）：GET /api/v1/books/export，与列表同筛选（bookFilters）→ downloadAuthedCsv
  const exportListMutation = useMutation({
    mutationFn: () => booksApi.exportListCsv(bookFilters),
    onSuccess: ({ truncated }) =>
      toast(truncated ? '已导出（超上限仅导前 5000 本）' : '书籍列表已导出', {
        tone: truncated ? 'warn' : 'pos',
        icon: 'IconDownload',
      }),
    onError: (err) =>
      toast(err instanceof Error ? err.message : '导出失败', { tone: 'neg', icon: 'IconAlert' }),
  });
  const listStatusMutation = useMutation({
    mutationFn: ({
      novelId,
      visibility,
    }: {
      novelId: string;
      groupId: string;
      /** `null` = 清除层② 覆盖、回落继承层①（A6 §12.2）*/
      visibility: NovelVisibility | null;
      title: string;
    }) => booksApi.setVisibility(novelId, { visibility }),
    onSuccess: async (_d, vars) => {
      toast(
        vars.visibility === null
          ? `《${vars.title}》已改为${INHERIT_LABEL}`
          : `《${vars.title}》受众档已改为「${visibilityLabel(vars.visibility)}」`,
        {
          tone: vars.visibility === 'PUBLIC' ? 'pos' : 'warn',
          icon: vars.visibility === 'PUBLIC' ? 'IconCheck' : 'IconEye',
        },
      );
      // 列表投影可能来自筛选命中的 translation：状态/日志按 display novel，详情仍按 group root 失效。
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['books'] }),
        queryClient.invalidateQueries({ queryKey: ['book-detail', vars.groupId] }),
        queryClient.invalidateQueries({ queryKey: appQueryKeys.all }),
        // 审计写（列表行状态）→ 失效该书操作日志缓存（codex #171 P2）
        queryClient.invalidateQueries({ queryKey: ['book-logs', vars.novelId] }),
      ]);
    },
    onError: (err, vars) => {
      toast(`${vars.title} · ${err instanceof Error ? err.message : '操作失败'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  // 仅在合法 URL bookId 变化时同步 selected（回列表或非法 routeBookId=null 则清掉）。
  // **只依赖 routeBookId**：不依赖 BOOKS——否则 TanStack refetch / 窗口聚焦会重跑 effect 把行点击打开的
  // detail（routeBookId 仍为 null）误关，冷加载 BOOKS=[] 时也会误清有效 deep-link id（codex #14 §2）。
  // 非法 / 失效 bookId 由上方 effect 清理；详情仍只在当前查询结果命中时打开，不回退 BOOKS[0]。
  useEffect(() => {
    setSelected(routeBookId);
  }, [routeBookId]);

  // 列表 query 加载中**不再整页 early-return 骨架**：页头 / sub-tab / 搜索 / 筛选控件都用各自 state /
  // 独立 query（catList/tagList/sys），与主列表 query 无关——整页 gate 会把它们一并挡住，运营被困、
  // 分类/标签 tab 与工具也开不了。改为仅在 books tab 的**表格体**内放 TableSkeleton（见下方），
  // 数据到达原位无位移替换。detail 抽屉仍只在 BOOKS 已载入（find 命中）时打开：booksLoading 时 BOOKS=[]，
  // find 落空自然落到下方列表渲染（不误开 detail）。

  if (selected) {
    const b =
      BOOKS.find((x) => x.id === selected) ?? (openedBook?.id === selected ? openedBook : null);
    // 找到才渲染 detail；找不到（理论上 effect 已清无效路由态）不回退 BOOKS[0]，落到下方列表（codex #14）
    if (b) {
      return (
        <BookDetail
          // key=book.id：详情→详情直切（routeBookId 变）时 remount，清 BookLogs 的 page/typeFilter/expanded
          // 等内部态，避免上一本书的分页/筛选/展开残留到下一本（codex #171 W-2 P3）。
          key={b.id}
          book={b}
          onBack={() => {
            setSelected(null);
            if (routeBookId) setSearchParams({});
          }}
          onOpenBook={(next) => {
            setOpenedBook(next);
            setSelected(next.id);
            setSearchParams({ bookId: next.id });
          }}
        />
      );
    }
  }

  // 服务端下推筛选后，BOOKS 即当前页**已筛选**的服务端结果，直接渲染（不再本页客户端 filter 单页 → P1 回归修复）。
  // cursor 分页下 BOOKS.length 仅为「当前页行数」而非书库总数，故计数语义统一走 Pagination 的 cursor「已载入 N+」口径，
  // 列表行直接用 BOOKS。
  const pageRows = BOOKS;

  return (
    <div className="page page-enter">
      <div className="page-header">
        <div>
          <h1 className="page-title">书籍管理</h1>
          {/* cursor 分页 + 服务端筛选下无书库总数事实源；副标题不再呈现「N 本书」（那只是当前页行数，会误导）。*/}
          <div className="page-sub">按书组聚合 · 一本书 = 一个语言版本，点子行打开该书详情</div>
        </div>
        <div className="hstack" style={{ gap: 8 }}>
          {/* 批量导入：无契约端点（NOT-in-scope §五）→ disabled + title 标注，不留假成功 toast */}
          <button
            className="btn"
            disabled
            title={NOT_IN_SCOPE_TITLE}
            style={{ opacity: 0.55, cursor: 'not-allowed' }}
          >
            <Icons.IconUpload size={13} />
            批量导入
          </button>
          <button
            className="btn btn-accent"
            disabled={!canWriteBooks}
            title={canWriteBooks ? undefined : FORBIDDEN_ROLE_TITLE}
            style={canWriteBooks ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
            onClick={() => canWriteBooks && setNewBookOpen(true)}
          >
            <Icons.IconPlus size={13} />
            新建书籍
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ borderBottom: '1px solid var(--border)', display: 'flex', gap: 0 }}>
        {(
          [
            // 「全部书籍」是 cursor 分页 + 服务端筛选，无总数事实源 → n=null（不显误导的「当前页行数」徽标）；
            // 分类 / 标签是全量列表，n 为真实总数。
            { id: 'books', label: '全部书籍', n: booksLoading ? null : booksTotal },
            { id: 'cats', label: '分类', n: catList.length },
            { id: 'tags', label: '标签', n: tagList.length },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setTab(t.id);
              setPage(1);
            }}
            style={{
              border: 0,
              background: 'transparent',
              padding: '8px 14px',
              font: 'inherit',
              fontSize: 12.5,
              cursor: 'pointer',
              color: tab === t.id ? 'var(--fg-strong)' : 'var(--fg-muted)',
              fontWeight: tab === t.id ? 500 : 400,
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {t.label}
            {t.n !== null && (
              <>
                {' '}
                <span className="mono" style={{ color: 'var(--fg-faint)', fontSize: 11 }}>
                  · {t.n}
                </span>
              </>
            )}
          </button>
        ))}
      </div>

      {tab === 'books' && (
        <>
          <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            <SearchInput
              width={300}
              placeholder={BOOK_SEARCH_PLACEHOLDER}
              value={q}
              onChange={(e) => {
                if (searchParams.has('bookId')) {
                  setSelected(null);
                  removeRouteBookId();
                }
                setQ(e.target.value);
                setPage(1);
              }}
            />
            {/* 受众档 seg：value=四档机器码（单值，'全部'=不传），label 取展示模块唯一口径。
                档位闭集来自 shared，本处不内联字面量 —— 加档位时这里自动跟上（见上方 visibilityFilter 注释）。*/}
            <div className="seg">
              {(
                [
                  { value: '全部' as const, label: '全部' },
                  ...VISIBILITY_OPTIONS.map((v) => ({ value: v, label: visibilityLabel(v) })),
                ] as { value: '全部' | NovelVisibility; label: string }[]
              ).map((s) => (
                <button
                  key={s.value}
                  className={visibilityFilter === s.value ? 'on' : ''}
                  onClick={() => {
                    setVisibilityFilter(s.value);
                    setPage(1);
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {/* 分类 / 标签：option value=真实 UUID（取自 catList/tagList），label 中文名 → 直接下推后端 category/tag。*/}
            <FilterButton
              label="分类"
              value={categories}
              onChange={(next) => {
                setCategories(next);
                setPage(1);
              }}
              options={catList.map((c) => ({ value: c.id, label: c.name }))}
              width={160}
              searchable
            />
            <FilterButton
              label="标签"
              value={tags}
              onChange={(next) => {
                setTags(next);
                setPage(1);
              }}
              options={tagList.map((t) => ({ value: t.id, label: t.name }))}
              width={160}
              searchable
            />
            <FilterButton
              label="推荐标记"
              value={recommendationFilter}
              onChange={(next) => {
                setRecommendationFilter(next);
                setPage(1);
              }}
              options={[
                { value: 'true', label: '已推荐' },
                { value: 'false', label: '未推荐' },
              ]}
              multi={false}
              width={140}
            />
            {/* 连载：value=后端 serial_status 码（含 HIATUS 停更），label 中文。*/}
            <FilterButton
              label="连载"
              value={serials}
              onChange={(next) => {
                setSerials(next);
                setPage(1);
              }}
              options={[
                { value: 'ONGOING', label: '连载中' },
                { value: 'COMPLETED', label: '已完结' },
                { value: 'HIATUS', label: '停更' },
              ]}
              width={140}
            />
            <FilterButton
              label="语言"
              value={langs}
              onChange={(next) => {
                setLangs(next);
                setPage(1);
              }}
              options={sys.languages
                .filter((l) => l.enabled)
                .map((l) => ({
                  value: l.language_code,
                  label: `${l.language_code.toUpperCase()} ${l.display_name_i18n.zh}`,
                }))}
              width={180}
              searchable
            />
            {/* 来源：value=后端 source 码 ORIGINAL/LICENSED/IMPORTED，label 中文（与 books-adapter SOURCE_LABEL 一致）。
                来源信息仅 root 可见，故筛选入口同样只对 root 开放（否则可由筛选结果反推来源）。*/}
            {canViewSource && (
              <FilterButton
                label="来源"
                value={sources}
                onChange={(next) => {
                  setSources(next);
                  setPage(1);
                }}
                options={[
                  { value: 'ORIGINAL', label: '原创' },
                  { value: 'LICENSED', label: '授权' },
                  { value: 'IMPORTED', label: '导入' },
                ]}
                width={160}
              />
            )}
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                const allCollapsed =
                  pageRows.length > 0 && pageRows.every((g) => collapsedGroups[g.groupId]);
                setCollapsedGroups(
                  allCollapsed ? {} : Object.fromEntries(pageRows.map((g) => [g.groupId, true])),
                );
              }}
            >
              {pageRows.length > 0 && pageRows.every((g) => collapsedGroups[g.groupId])
                ? '展开全部'
                : '折叠全部'}
            </button>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
              本页 {booksLoading ? '—' : pageRows.length} 组
            </span>
            <button
              className="btn btn-ghost btn-icon"
              disabled={exportListMutation.isPending}
              title="导出当前书组清单 CSV"
              onClick={() => exportListMutation.mutate()}
            >
              <Icons.IconDownload size={13} />
            </button>
          </div>

          {booksLoading ? (
            <BookGroupListSkeleton />
          ) : (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="bgl-head">
                <div className="bgl-c-main">书组 / 语言版本</div>
                <div className="bgl-c-serial">连载</div>
                <div className="bgl-c-ch">章节</div>
                <div className="bgl-c-status">状态</div>
                <div className="bgl-c-upd">更新</div>
                <div className="bgl-c-act" />
              </div>
              {pageRows.length === 0 ? (
                <div style={{ padding: '46px 0 48px', textAlign: 'center' }}>
                  <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>没有符合条件的书组</div>
                  <div style={{ fontSize: 11.5, color: 'var(--fg-faint)', marginTop: 5 }}>
                    筛选按语言命中整组 · 也可直接用书组编码（7 位）/ 书编码（10 位）精确搜索
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{ marginTop: 12 }}
                    onClick={() => {
                      setQ('');
                      setVisibilityFilter('全部');
                      setCategories([]);
                      setTags([]);
                      setSerials([]);
                      setRecommendationFilter([]);
                      setLangs([]);
                      setSources([]);
                      setPage(1);
                    }}
                  >
                    清除筛选
                  </button>
                </div>
              ) : (
                pageRows.map((b) => (
                  <BookGroupRow
                    key={b.groupId}
                    group={b}
                    collapsed={!!collapsedGroups[b.groupId]}
                    canChangeStatus={canChangeStatus}
                    onToggle={() =>
                      setCollapsedGroups((m) => ({
                        ...m,
                        [b.groupId]: !m[b.groupId],
                      }))
                    }
                    onOpen={(next) => {
                      setOpenedBook(next);
                      setSelected(next.id);
                    }}
                    onConfirm={setConfirm}
                    onListStatus={(input) => listStatusMutation.mutate(input)}
                  />
                ))
              )}
              <Pagination
                total={booksTotal}
                page={page}
                pageSize={pageSize}
                onChange={changeBookPage}
                totalMode="exact"
              />
            </div>
          )}
        </>
      )}

      {tab === 'cats' && (
        <CategoriesTags kind="cats" onConfirm={setConfirm} onEdit={setCatEditor} />
      )}
      {tab === 'tags' && (
        <CategoriesTags kind="tags" onConfirm={setConfirm} onEdit={setCatEditor} />
      )}

      {newBookOpen && <NewBookDialog onClose={() => setNewBookOpen(false)} />}
      {catEditor && (
        <CategoryEditor
          kind={catEditor.kind === 'cats' ? 'cats' : 'tag'}
          item={catEditor.item}
          onClose={() => setCatEditor(null)}
        />
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          tone={confirm.tone}
          icon={confirm.icon}
          requireTyping={confirm.requireTyping}
          confirmLabel={confirm.confirmLabel}
          cancelLabel={confirm.cancelLabel}
          onConfirm={() => confirm.onConfirm?.()}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// =========================================================
// 分类 / 标签清单 tab
// =========================================================
function CategoriesTags({
  kind,
  onConfirm,
  onEdit,
}: {
  kind: 'cats' | 'tags';
  onConfirm: (req: ConfirmReq) => void;
  onEdit: (req: { kind: 'cats' | 'tag'; item?: CategoryEditorItem }) => void;
}) {
  // @real — 分类 / 标签真实列表（含 DTO id，编辑写动作用）：useCategoriesList / useTagsList
  const { data: cats } = useCategoriesList();
  const { data: tags } = useTagsList();
  const rows: CatTagRow[] = kind === 'cats' ? cats : tags;
  const editKind: 'cats' | 'tag' = kind === 'cats' ? 'cats' : 'tag';
  // 分类/标签创建/编辑 = server POST/PATCH /categories|/tags requireRole('root','admin','operator')；
  // 前端 RBAC 对等门控（viewer 不可开编辑器，避免发注定 403 的写请求 + 兑现「viewer 零写请求」）。后端 requireRole 为权威。
  const canWrite = useCanWriteBooks();
  const queryClient = useQueryClient();
  // 删除分类/标签（接真）：DELETE /categories|/tags/:id（被书引用后端拒删 RESOURCE_CONFLICT → 错误 toast 不假成功）
  const deleteMutation = useMutation({
    mutationFn: (id: string) => (kind === 'cats' ? categoriesApi.remove(id) : tagsApi.remove(id)),
    onSuccess: () => {
      toast(kind === 'cats' ? '分类已删除' : '标签已删除', { tone: 'pos', icon: 'IconTrash' });
      queryClient.invalidateQueries({ queryKey: [kind === 'cats' ? 'categories' : 'tags'] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '删除失败', { tone: 'neg', icon: 'IconAlert' }),
  });
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div
        className="hstack"
        style={{ padding: 12, gap: 8, borderBottom: '1px solid var(--border)' }}
      >
        <span style={{ fontWeight: 500, fontSize: 12.5 }}>
          {kind === 'cats' ? '分类' : '标签'}清单
        </span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
          · {rows.length} 项
        </span>
        <div style={{ flex: 1 }} />
        {/* 新建分类/标签：真实 categoriesApi.create / tagsApi.create（CategoryEditor 内接真）；viewer 禁用（前端 RBAC 对等）*/}
        <button
          className="btn btn-sm"
          disabled={!canWrite}
          title={canWrite ? undefined : FORBIDDEN_ROLE_TITLE}
          style={canWrite ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
          onClick={() => canWrite && onEdit({ kind: editKind })}
        >
          <Icons.IconPlus size={11} />
          新建
        </button>
      </div>
      <Table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 36 }}>#</th>
            <th>中文</th>
            <th>英文</th>
            <th>多语言版本</th>
            <th className="num">关联书籍</th>
            <th style={{ width: 100 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((it, i) => (
            <tr
              key={it.id}
              style={{ cursor: canWrite ? 'pointer' : 'default' }}
              // 编辑携带真实 id（编辑走 update 必须）+ name/nameEn 回填；viewer 不开编辑器（前端 RBAC 对等）
              onClick={() =>
                canWrite &&
                onEdit({
                  kind: editKind,
                  item: {
                    id: it.id,
                    name: it.name,
                    nameEn: it.nameEn,
                    books: it.books ?? undefined,
                    // §14.2 / §14.4 乐观并发令牌：载入时那份，编辑面原样回传
                    updatedAt: it.updatedAt,
                    // 完整 name_i18n：PATCH 整份替换，只给 zh/en 会删掉其余语言
                    nameI18n: it.nameI18n,
                  },
                })
              }
            >
              <td className="mono muted">{String(i + 1).padStart(2, '0')}</td>
              <td style={{ fontWeight: 500 }}>{it.name}</td>
              <td className="mono" style={{ fontSize: 11.5 }}>
                {it.nameEn || '—'}
              </td>
              <td>
                {/* 已录入多语言 = name_i18n 的 locale key 集合（真实，非伪造启用语言派生）*/}
                <div className="hstack" style={{ gap: 3, flexWrap: 'wrap' }}>
                  {it.locales.slice(0, 4).map((code) => (
                    <span key={code} className="chip" style={{ fontSize: 9.5, padding: '0 5px' }}>
                      <span className="mono">{code.toUpperCase()}</span>
                      <span style={{ color: 'var(--fg-faint)', marginLeft: 3 }}>
                        {langName(code)}
                      </span>
                    </span>
                  ))}
                  {it.locales.length > 4 && (
                    <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                      +{it.locales.length - 4}
                    </span>
                  )}
                </div>
              </td>
              {/* 关联书籍数（接真）：DTO.book_count（server LEFT JOIN novels 书组去重计数）；null 仅理论兜底 → 「—」 */}
              <td className="num">{it.books ?? '—'}</td>
              <td>
                <div className="hstack" style={{ gap: 4 }} onClick={(e) => e.stopPropagation()}>
                  {/* 行编辑：root/admin/operator 可写（与 server PATCH requireRole 对等）；viewer 禁用 */}
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={!canWrite}
                    title={canWrite ? undefined : FORBIDDEN_ROLE_TITLE}
                    style={canWrite ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                    onClick={() =>
                      canWrite &&
                      onEdit({
                        kind: editKind,
                        item: {
                          id: it.id,
                          name: it.name,
                          nameEn: it.nameEn,
                          books: it.books ?? undefined,
                          // §14.2 / §14.4 乐观并发令牌：载入时那份，编辑面原样回传
                          updatedAt: it.updatedAt,
                          // 完整 name_i18n：PATCH 整份替换，只给 zh/en 会删掉其余语言
                          nameI18n: it.nameI18n,
                        },
                      })
                    }
                  >
                    <Icons.IconEdit size={11} />
                  </button>
                  {/* 删除分类/标签（接真）：DELETE /categories|/tags/:id（被书引用后端拒删）→ 二次确认 */}
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={!canWrite || deleteMutation.isPending}
                    title={canWrite ? '删除' : FORBIDDEN_ROLE_TITLE}
                    style={canWrite ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                    onClick={() =>
                      canWrite &&
                      onConfirm({
                        title: `删除${kind === 'cats' ? '分类' : '标签'}「${it.name}」？`,
                        message:
                          (it.books ?? 0) > 0
                            ? `该${kind === 'cats' ? '分类' : '标签'}已被 ${it.books} 本书引用，删除将被拒绝（请先改这些书的归属）。`
                            : '物理删除，不可恢复。',
                        tone: 'danger',
                        confirmLabel: '删除',
                        onConfirm: () => deleteMutation.mutate(it.id),
                      })
                    }
                  >
                    <Icons.IconTrash size={11} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// =========================================================
// Book detail
// =========================================================
function bookRowForSibling(from: BookRow, locale: string): BookRow | null {
  const sib = from.languageCodes.find((row) => row.locale === locale);
  if (!sib) return null;
  return {
    ...from,
    id: sib.novelId,
    displayNovelId: sib.novelId,
    displayLocale: locale,
    displayTitle: sib.title || from.displayTitle,
    visibility: sib.visibility,
    visibilityOverridden: sib.visibilityOverridden,
    serial: sib.serial || from.serial,
    chapters: sib.chapters ?? from.chapters,
    publishedChapters: sib.publishedChapters ?? sib.chapters ?? from.publishedChapters,
    coverUrl: sib.coverUrl || from.coverUrl,
  };
}

const AGE_RATING_CHIP: Record<string, string> = {
  ALL_AGES: '',
  TEEN_13: 'chip-info',
  MATURE_16: 'chip-warn',
  ADULT_18: 'chip-neg',
};

function fmtListWords(words: number | null): string | null {
  if (words == null) return null;
  return `${(words / 10000).toFixed(1)} 万字`;
}

function fmtListPaywall(row: { freeChapterCount: number | null; costPerKw: string | null }):
  | string
  | null {
  if (row.freeChapterCount == null && row.costPerKw == null) return null;
  const free =
    row.freeChapterCount == null
      ? '免费章 —'
      : row.freeChapterCount === 0
        ? '无免费章'
        : `前 ${row.freeChapterCount} 章免费`;
  const price = row.costPerKw == null ? '—' : `${row.costPerKw} / 千字`;
  return `${free} · ${price}`;
}

function fmtListDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function ListField({
  k,
  v,
  mono,
  cls = '',
}: {
  k: string;
  v: ReactNode;
  mono?: boolean;
  cls?: string;
}) {
  return (
    <span className={`bgl-f ${cls}`}>
      <span className="bgl-fk">{k}</span>
      <span className={mono ? 'bgl-fv mono' : 'bgl-fv'}>
        {v == null || v === '' ? <span style={{ color: 'var(--fg-faint)' }}>—</span> : v}
      </span>
    </span>
  );
}

function BookTaxonomyMarks({
  category,
  tags,
}: {
  category: string;
  tags: string[];
}) {
  return (
    <>
      {category ? (
        <span className="bgl-cat" title="分类">
          {category}
        </span>
      ) : null}
      {tags.map((t) => (
        <span key={t} className="bgl-tag" title="标签">
          {t}
        </span>
      ))}
    </>
  );
}

function fmtGroupUpdated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function languageBookMenuItems(opts: {
  book: BookRow;
  canChangeStatus: boolean;
  onOpen: (book: BookRow) => void;
  onConfirm: (req: ConfirmReq) => void;
  onListStatus: (input: {
    novelId: string;
    groupId: string;
    visibility: NovelVisibility | null;
    title: string;
  }) => void;
}): ContextMenuItem[] {
  const { book: b, canChangeStatus, onOpen, onConfirm, onListStatus } = opts;
  const code = b.languageCodes.find((row) => row.locale === b.displayLocale)?.bookCode ?? null;
  return [
    { label: '查看详情', icon: 'IconEye', onClick: () => onOpen(b) },
    { label: '编辑信息', icon: 'IconEdit', onClick: () => onOpen(b) },
    { label: '章节管理', icon: 'IconList', onClick: () => onOpen(b) },
    '---',
    { section: `受众档 · 仅 ${fmtLang(b.displayLocale)} 这一本（层②）` } satisfies ContextMenuItem,
    // 「继承书组默认」与「普通用户可见」并列、不合并：A6 §12.2 明写 `null` 与 `PUBLIC` 语义不同 ——
    // 前者跟随层① 变动，后者把这一本钉死为全体可见。合并成一个「上架」会让运营失去表达前者的能力。
    {
      label: b.visibilityOverridden ? INHERIT_LABEL : `● 当前 · ${INHERIT_LABEL}`,
      icon: 'IconCircle',
      disabled: !b.visibilityOverridden || !canChangeStatus || !b.displayNovelId,
      title: !canChangeStatus
        ? FORBIDDEN_ROLE_TITLE
        : !b.displayNovelId
          ? '滚动发布期间无法确定筛选命中的语言版本，请刷新后重试'
          : INHERIT_DESC,
      onClick: () =>
        onConfirm({
          title: `《${b.displayTitle}》改为${INHERIT_LABEL}？`,
          message: `清除这一本语言版自己的受众档设置，跟随书组默认层变动。当前书组默认为「${visibilityLabel(b.groupDefaultVisibility)}」。`,
          tone: 'default',
          icon: 'IconCircle',
          confirmLabel: '确认改为继承',
          onConfirm: () =>
            onListStatus({
              novelId: b.displayNovelId,
              groupId: b.groupId,
              visibility: null,
              title: b.displayTitle,
            }),
        }),
    },
    // 四档中 DELISTED 单独走详情（§12.2 要求 status_reason_i18n.en，行内 confirm 无原因录入）
    ...VISIBILITY_OPTIONS.filter((v) => v !== 'DELISTED').map((v) => ({
      label:
        b.visibilityOverridden && b.visibility === v
          ? `● 当前 · ${visibilityLabel(v)}`
          : visibilityLabel(v),
      icon: v === 'PUBLIC' ? ('IconCheckCircle' as const) : ('IconEye' as const),
      disabled:
        (b.visibilityOverridden && b.visibility === v) || !canChangeStatus || !b.displayNovelId,
      title: !canChangeStatus
        ? FORBIDDEN_ROLE_TITLE
        : !b.displayNovelId
          ? '滚动发布期间无法确定筛选命中的语言版本，请刷新后重试'
          : undefined,
      onClick: () =>
        onConfirm({
          title: `《${b.displayTitle}》受众档改为「${visibilityLabel(v)}」？`,
          message: VISIBILITY_CONFIRM_MESSAGE[v],
          tone: v === 'PUBLIC' ? ('default' as const) : ('warn' as const),
          icon: v === 'PUBLIC' ? ('IconCheckCircle' as const) : ('IconEye' as const),
          confirmLabel: `确认改为${visibilityLabel(v)}`,
          onConfirm: () =>
            onListStatus({
              novelId: b.displayNovelId,
              groupId: b.groupId,
              visibility: v,
              title: b.displayTitle,
            }),
        }),
    })),
    {
      label: b.visibility === 'DELISTED' ? '● 当前 · 全局下架' : '全局下架…',
      icon: 'IconBan',
      disabled: !canChangeStatus || !b.displayNovelId,
      title: !canChangeStatus
        ? FORBIDDEN_ROLE_TITLE
        : !b.displayNovelId
          ? '滚动发布期间无法确定筛选命中的语言版本，请刷新后重试'
          : '下架需填写下架原因 · 在详情「上下架」tab 操作',
      onClick: () => onOpen(b),
    },
    '---',
    {
      label: '复制书组编码',
      icon: 'IconCopy',
      disabled: !b.bookGroupCode,
      title: b.bookGroupCode ? undefined : '书组编码未回传',
      onClick: () => {
        if (!b.bookGroupCode) return;
        void navigator.clipboard.writeText(b.bookGroupCode);
        toast(`已复制书组编码 ${b.bookGroupCode}`, { tone: 'pos', icon: 'IconCopy' });
      },
    },
    {
      label: '复制书编码',
      icon: 'IconCopy',
      disabled: !code,
      title: code ? undefined : '书编码未回传',
      onClick: () => {
        if (!code) return;
        void navigator.clipboard.writeText(code);
        toast(`已复制书编码 ${code}`, { tone: 'pos', icon: 'IconCopy' });
      },
    },
    {
      label: '导出章节',
      icon: 'IconDownload',
      disabled: true,
      title: NOT_IN_SCOPE_TITLE,
    },
    '---',
    {
      label: '删除该语言书',
      icon: 'IconTrash',
      tone: 'danger',
      disabled: true,
      title: NOT_IN_SCOPE_TITLE,
    },
  ];
}

function LanguageBookRow({
  group,
  row,
  child,
  canChangeStatus,
  onOpen,
  onConfirm,
  onListStatus,
}: {
  group: BookRow;
  row: BookRow['languageCodes'][number];
  child: BookRow;
  canChangeStatus: boolean;
  onOpen: (book: BookRow) => void;
  onConfirm: (req: ConfirmReq) => void;
  onListStatus: (input: {
    novelId: string;
    groupId: string;
    visibility: NovelVisibility | null;
    title: string;
  }) => void;
}) {
  const [briefOpen, setBriefOpen] = useState(false);
  const canViewSource = useCanViewBookSource();
  const paywall = fmtListPaywall(row);
  const sourceLabel = row.source;
  return (
    <div
      data-book-row={row.novelId}
      className="bgl-row"
      onClick={() => onOpen(child)}
      title={`打开 ${fmtLang(row.locale)}《${row.title}》详情`}
    >
      <div className="bgl-c-main">
        <div className="bgl-vline">
          <BookCover
            src={row.coverUrl}
            alt={`${row.title} 封面`}
            hue={group.coverHue}
            width={22}
            height={30}
            radius={3}
          >
            <span
              style={{
                position: 'absolute',
                inset: 0,
                padding: 2,
                fontSize: 5.5,
                color: 'white',
                lineHeight: 1.1,
                fontWeight: 600,
                opacity: 0.9,
              }}
            >
              {row.title.slice(0, 12)}
            </span>
          </BookCover>
          <span className="chip" style={{ fontSize: 10, flexShrink: 0 }}>
            {fmtLang(row.locale)}
          </span>
          <span className="bgl-bookcode" onClick={(e) => e.stopPropagation()}>
            <CopyableCode value={row.bookCode} label="书编码" />
          </span>
          <span className="bgl-title" title={row.title}>
            {row.title}
          </span>
          <EditionLabelTag label={row.editionLabel} />
          <span className="bgl-meta">
            <BookTaxonomyMarks category={group.category} tags={group.tags} />
          </span>
        </div>
        <div className="bgl-sub">
          {row.ageRating ? (
            <span
              className={`chip ${AGE_RATING_CHIP[row.ageRating] ?? ''} mono`}
              style={{ fontSize: 9.5 }}
              title="内容分级 · 该语言书单独设定"
            >
              {row.ageRating}
            </span>
          ) : (
            <ListField k="分级" v={null} />
          )}
          <ListField k="作者" v={row.authorDisplay} />
          <ListField k="推荐" v={row.isRecommended ? '已推荐' : '未推荐'} />
          <ListField k="付费墙" v={paywall} />
          <ListField k="字数" v={fmtListWords(row.totalWords)} mono cls="bgl-f-sec" />
          <ListField k="篇幅" v={row.lengthClass} cls="bgl-f-sec" />
          <ListField k="首次上架" v={fmtListDate(row.publishedAt)} mono cls="bgl-f-sec" />
          {canViewSource && (
            <ListField
              cls="bgl-f-sec"
              k="来源"
              v={
                sourceLabel ? (
                  <span className="hstack" style={{ gap: 4 }}>
                    {sourceLabel}
                    {row.sourceProvider ? (
                      <span className="bgl-chan mono">{row.sourceProvider}</span>
                    ) : null}
                  </span>
                ) : null
              }
            />
          )}
        </div>
        <div className="bgl-briefline">
          <span className="bgl-fk">简介</span>
          {row.description ? (
            <>
              <span className={briefOpen ? 'bgl-brieftext open' : 'bgl-brieftext'}>
                {row.description}
              </span>
              <button
                type="button"
                className="bgl-more focusable"
                onClick={(e) => {
                  e.stopPropagation();
                  setBriefOpen((o) => !o);
                }}
              >
                {briefOpen ? '收起' : '展开'}
              </button>
            </>
          ) : (
            <span className="bgl-brieftext" style={{ color: 'var(--fg-faint)' }}>
              —
            </span>
          )}
        </div>
      </div>
      <div className="bgl-c-serial">
        <span
          className={`chip ${row.serial === '已完结' ? 'chip-pos' : 'chip-info'}`}
          style={{ fontSize: 10 }}
        >
          {row.serial}
        </span>
      </div>
      <div className="bgl-c-ch mono">
        {row.publishedChapters}
        <span style={{ color: 'var(--fg-faint)' }}> / {row.chapters}</span>
      </div>
      <div className="bgl-c-status">
        {/* chip 色与标签都取展示模块（唯一口径）；继承层① 的行额外标一个「继承」角标，
            否则运营看不出这一档是本行自己配的还是跟着书组走的 —— 那决定了他该改哪一层。 */}
        <span className={`chip ${visibilityChipClass(row.visibility)}`} style={{ fontSize: 10 }}>
          ● {visibilityLabel(row.visibility)}
        </span>
        {!row.visibilityOverridden && (
          <span
            className="chip"
            style={{ fontSize: 9, marginLeft: 4, opacity: 0.7 }}
            title={INHERIT_DESC}
          >
            继承
          </span>
        )}
      </div>
      <div className="bgl-c-upd" />
      <div className="bgl-c-act" onClick={(e) => e.stopPropagation()}>
        <ContextMenu
          width={180}
          items={languageBookMenuItems({
            book: child,
            canChangeStatus,
            onOpen,
            onConfirm,
            onListStatus,
          })}
        >
          <button className="btn btn-ghost btn-icon" style={{ width: 22, height: 22 }}>
            <Icons.IconMoreV size={12} />
          </button>
        </ContextMenu>
      </div>
    </div>
  );
}

function BookGroupRow({
  group,
  collapsed,
  canChangeStatus,
  onToggle,
  onOpen,
  onConfirm,
  onListStatus,
}: {
  group: BookRow;
  collapsed: boolean;
  canChangeStatus: boolean;
  onToggle: () => void;
  onOpen: (book: BookRow) => void;
  onConfirm: (req: ConfirmReq) => void;
  onListStatus: (input: {
    novelId: string;
    groupId: string;
    visibility: NovelVisibility | null;
    title: string;
  }) => void;
}) {
  const shelf = bookGroupStatusSummary(group.languageCodes);
  const children = group.languageCodes;
  // 组头主标题 = 书组中文运营识别名（dd#970 `cn_title`）：书组身份与「有没有中文语言版」脱钩后，
  // 运营在建书时填的就是它，列表上必须能看到、且能与自己搜的那个词对上（搜索谓词已含 cn_title）。
  // 回退链只为旧 Worker 的无 cn_title DTO 兜底（原作书名 → 代表行书名）。
  const groupTitle = group.groupTitleZh || group.originalTitle || group.displayTitle;
  // 副行按「原作书名 · 英文版书名 · 作者」排：主标题换成 cn_title 后，原作书名不再出现在主位，
  // 不补到副行就等于从列表上消失了。与主标题相同时省略，避免同一串字重复两遍。
  const originalTitle = group.originalTitle;
  const latinTitle = children.find((row) => row.locale === 'en')?.title;
  const subtitleParts = [
    originalTitle && originalTitle !== groupTitle ? originalTitle : null,
    latinTitle && latinTitle !== originalTitle ? latinTitle : null,
    group.author || null,
  ].filter((part): part is string => !!part);
  return (
    <div className="bgl-group">
      <div
        className="bgl-grouphead"
        onClick={onToggle}
        title={collapsed ? '展开语言版本' : '折叠语言版本'}
      >
        <button
          type="button"
          className="bgl-chev focusable"
          aria-expanded={!collapsed}
          aria-label={collapsed ? '展开书组' : '折叠书组'}
          title={collapsed ? '展开' : '折叠'}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <Icons.IconChevRight
            size={14}
            style={{
              transform: collapsed ? 'none' : 'rotate(90deg)',
              transition: 'transform 0.15s',
              color: 'var(--fg-muted)',
            }}
          />
        </button>
        <BookCover
          src={group.coverUrl}
          alt={`${groupTitle} 封面`}
          hue={group.coverHue}
          width={30}
          height={40}
          radius={3}
        >
          <span
            style={{
              position: 'absolute',
              inset: 0,
              padding: 3,
              fontSize: 6.5,
              color: 'white',
              lineHeight: 1.1,
              fontWeight: 600,
              opacity: 0.9,
            }}
          >
            {groupTitle.slice(0, 14)}
          </span>
        </BookCover>
        <div className="bgl-c-main">
          <div className="hstack" style={{ gap: 8, flexWrap: 'wrap', rowGap: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--fg-strong)' }}>
              {groupTitle}
            </span>
            <span className="bgl-code" title="书组编码 · 7 位 · 同一原作的各语言书共用">
              <CopyableCode value={group.bookGroupCode} label="书组编码" prefix="书组" />
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
              {subtitleParts.join(' · ')}
            </span>
            <span className="chip" style={{ fontSize: 9.5 }}>
              <Icons.IconLanguages size={9} />
              {children.length} 本语言书
            </span>
            <BookTaxonomyMarks category={group.category} tags={group.tags} />
          </div>
          <div className="hstack" style={{ gap: 6, marginTop: 5, flexWrap: 'wrap', rowGap: 4 }}>
            {children.map((row) => (
              <span
                key={row.locale}
                className="bgl-brief"
                title={
                  isAdminInternalLocale(row.locale)
                    ? 'admin 内部预览 / 管理语言 · 不投放 C 端 · 不计入整组状态摘要'
                    : undefined
                }
              >
                <span className="chip" style={{ fontSize: 9.5, padding: '0 5px' }}>
                  {fmtLang(row.locale)}
                </span>
                <CopyableCode value={row.bookCode} label="书编码" />
                <EditionLabelTag label={row.editionLabel} fontSize={9.5} />
                <span style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                  <span style={{ color: visibilityDot(row.visibility) }}>●</span>{' '}
                  {visibilityLabel(row.visibility)}
                </span>
              </span>
            ))}
          </div>
        </div>
        <div className="bgl-c-serial" />
        <div className="bgl-c-ch" />
        <div className="bgl-c-status">
          <span className={`chip ${shelf.cls}`} style={{ fontSize: 9.5 }} title={shelf.title}>
            <span style={{ color: shelf.dot }}>●</span>
            {shelf.label}
          </span>
        </div>
        <div className="bgl-c-upd mono">{fmtGroupUpdated(group.updatedAt)}</div>
        <div className="bgl-c-act" />
      </div>
      {!collapsed && (
        <div className="bgl-rows">
          {children.map((row) => {
            const child = bookRowForSibling(group, row.locale);
            if (!child) return null;
            return (
              <LanguageBookRow
                key={row.novelId}
                group={group}
                row={row}
                child={child}
                canChangeStatus={canChangeStatus}
                onOpen={onOpen}
                onConfirm={onConfirm}
                onListStatus={onListStatus}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function BookGroupListSkeleton({ groups = 5 }: { groups?: number }) {
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div className="bgl-head">
        <div className="bgl-c-main">书组 / 语言版本</div>
        <div className="bgl-c-serial">连载</div>
        <div className="bgl-c-ch">章节</div>
        <div className="bgl-c-status">状态</div>
        <div className="bgl-c-upd">更新</div>
        <div className="bgl-c-act" />
      </div>
      {Array.from({ length: groups }).map((_, i) => (
        <div key={i} className="bgl-group">
          <div className="bgl-grouphead" style={{ cursor: 'default' }}>
            <span style={{ width: 26, flexShrink: 0 }} />
            <Sk w={30} h={40} r={3} />
            <div className="bgl-c-main">
              <div className="vstack" style={{ gap: 8 }}>
                <Sk w={i % 2 ? 260 : 210} h={13} r={4} />
                <Sk w={i % 3 ? 300 : 190} h={10} />
              </div>
            </div>
            <div className="bgl-c-serial" />
            <div className="bgl-c-ch" />
            <div className="bgl-c-status">
              <Sk w={66} h={16} r={999} />
            </div>
            <div className="bgl-c-upd">
              <Sk w={62} h={10} />
            </div>
            <div className="bgl-c-act" />
          </div>
          <div className="bgl-rows">
            {Array.from({ length: 2 + (i % 2) }).map((_, j) => (
              <div key={j} className="bgl-row" style={{ cursor: 'default' }}>
                <div className="bgl-c-main">
                  <div className="bgl-vline">
                    <Sk w={22} h={30} r={3} />
                    <Sk w={54} h={16} r={999} />
                    <Sk w={92} h={10} />
                    <Sk w={j % 2 ? 150 : 196} h={11} />
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                      <Sk w={34} h={16} r={4} />
                      <Sk w={52} h={16} r={4} />
                    </span>
                  </div>
                  <div className="bgl-sub">
                    <Sk w={62} h={14} r={999} />
                    <Sk w={78} h={10} />
                    <Sk w={124} h={10} />
                    <Sk w={54} h={10} />
                    <Sk w={66} h={10} />
                    <Sk w={48} h={10} />
                  </div>
                  <div className="bgl-briefline">
                    <Sk w={j % 2 ? 240 : 300} h={10} />
                  </div>
                </div>
                <div className="bgl-c-serial" style={{ alignSelf: 'center' }}>
                  <Sk w={48} h={16} r={999} />
                </div>
                <div className="bgl-c-ch" style={{ alignSelf: 'center' }}>
                  <Sk w={42} h={10} />
                </div>
                <div className="bgl-c-status" style={{ alignSelf: 'center' }}>
                  <Sk w={52} h={16} r={999} />
                </div>
                <div className="bgl-c-upd" />
                <div className="bgl-c-act" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function BookDetail({
  book,
  onBack,
  onOpenBook,
}: {
  book: BookRow;
  onBack: () => void;
  onOpenBook: (next: BookRow) => void;
}) {
  const [tab, setTab] = useState<'info' | 'chapters' | 'stats' | 'status' | 'logs'>('info');
  // 日志 tab 含跨 admin 审计明细 → 后端 /:id/logs requireRole operator+；前端 RBAC 镜像（viewer 不显该 tab）。
  const role = useAuthStore((s) => s.user?.role);
  const canViewLogs = role === 'root' || role === 'admin' || role === 'operator';
  const bookLangs = book.langs;
  const lang = book.displayLocale || book.langs[0] || 'en';
  const [previewing, setPreviewing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [recommendationTranslationPending, setRecommendationTranslationPending] = useState(false);
  // 书籍信息表单的本地校验结果（免费章节数 / 改写版本）由 BookInfo 上报。页头「保存」的禁用态必须读 state：
  // editFormRef 只在 BookInfo 渲染时写入，改对输入后父组件不重渲染，按 ref 求值会让按钮停在禁用态
  const [bookInfoFormValid, setBookInfoFormValid] = useState(true);
  // 书籍信息编辑表单快照：BookInfo（受控、key 含 lang 会 remount）每次渲染把最新值写进本 ref，
  // 供页头「保存」按钮读取（按钮与表单分处头/正文，用 ref 桥接，避免逐字段 onChange prop 透传）。
  const editFormRef = useRef<BookEditForm | null>(null);
  // 「放弃更改」用：bump 后 BookInfo key 变化 → remount → 受控字段从 props 原值重挂。
  const [formVersion, setFormVersion] = useState(0);
  // ADM-BOK-S017 数据基数编辑态**提到本层**：基数是书组（group）级字段，而 BookInfo 的 key 含 lang、
  // 切语言 / 离开信息 tab 都会 remount。放在那里会让未保存的基数编辑被静默清空，`dirty` 却仍为 true
  // → 运营点保存时基数字段因「与持久值相等」被省略，等于改了个寂寞。null = 本轮未编辑（用持久值）。
  // **逐字段**记录：只把本轮真正编辑过的那个字段放进来。整体拷贝两个值会让未编辑的那个也成为
  // 候选更新——详情在表单 dirty 期间被 invalidate（如连载状态变更）后，若他人改了未编辑的那个，
  // 保存会拿陈旧副本把它 PATCH 回去，静默回退对方的改动。
  const [baseCountEdits, setBaseCountEdits] = useState<{ read?: string; favorite?: string }>({});
  // 新增语言版本对话框（POST /books/:id/languages）
  const [newLangOpen, setNewLangOpen] = useState(false);
  // 本组件本地通用确认弹窗
  const [confirm, setConfirm] = useState<ConfirmReq | null>(null);
  const canWrite = useCanWriteBooks();

  // 分类 / 标签真实列表（含 DTO UUID）：信息 tab 分类下拉 + 标签多选用真实 id（非 mock 名）
  const { data: catList } = useCategoriesList();
  const { data: tagList } = useTagsList();
  // 详情页消费真实统计（ADM-BOK-S014，§19）+ 书组详情（§15.2）——**入口 id = 书组 id**
  // （`novel_groups.id`，dd#970）。此前传 `book.id`（= 书组根 novel id），书组升格后那个 id
  // 在 `/book-groups/:id` 上查不到任何行 → 404。
  const { data: stats } = useBookStats(book.groupId);
  const { data: detail, isLoading: detailLoading } = useBookDetail(book.groupId);
  // **书组代表行**（A6 §15.1，dd#970）：`root_novel_id` 所指行，可空或不在成员集内时回退
  // `locale` 升序第一行。它只承载**行级**回显默认值（简介 / 千字单价 / `source_external_id`）——
  // 书组级字段（`cn_title` / 原作信息 / `source` / 展示基数）已迁 `novel_groups`，从 `detail`
  // 顶层直接读，不再有「root 行才有值」这回事。
  const representativeRow =
    (detail?.root_novel_id
      ? detail.languages.find((l) => l.id === detail.root_novel_id)
      : undefined) ??
    detail?.languages[0] ??
    null;
  // 详情主体切换（换书）或 formVersion bump（放弃更改 / 保存成功后 refetch 完成）→ 丢弃本地基数编辑，
  // 回落到刷新后的持久值。**不含 lang**：切语言不该清掉书组级字段的未保存编辑。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 依赖即「何时该重置」，不是读取值
  useEffect(() => {
    setBaseCountEdits({});
  }, [book.id, formVersion]);
  /**
   * 书组编辑基线 —— 与行级 `formSnapshotRef` 同一条纪律，只是作用域是书组。
   *
   * 行级那份的注释已写明：令牌取「提交那一刻的最新值」会把乐观并发前置条件**架空**（中途任一次
   * refetch 都把它刷成新值，而表单字段还停在旧快照）。书组这半此前正是那种取法 —— dirty 基线与
   * `expected_updated_at` 都直接读 `detail`。而 `baseCountEdits` 的重置键是 `[book.id, formVersion]`，
   * 纯 detail refetch（如新增语言后 invalidate `book-detail`）三者都不变、BookInfo 不重挂，
   * 于是**旧表单值配新令牌**提交，CAS 通过、静默覆盖他人刚写的基数 / 原作者。
   *
   * 故按与 `baseCountEdits` **完全相同的键**捕获一份基线：键一致就沿用当初那份值与令牌，
   * 键变了（换书 / 放弃更改 / 保存成功后 refetch 完成）才重新捕获。用 render 期派生而非 effect：
   * effect 在渲染之后才跑，中间那次渲染发出的保存会拿到错值。
   *
   * `detail` 首次到达前无从捕获，故记 `captured` 而不是靠「值非空」判 —— 原作者本就可能是 null。
   */
  const groupBaselineKey = `${book.id}-${formVersion}`;
  const groupBaselineRef = useRef<{
    key: string;
    captured: boolean;
    updatedAt: string | null;
    readCount: number;
    favoriteCount: number;
    originalAuthor: string | null;
  }>({
    key: '',
    captured: false,
    updatedAt: null,
    readCount: 0,
    favoriteCount: 0,
    originalAuthor: null,
  });
  if (groupBaselineRef.current.key !== groupBaselineKey) {
    groupBaselineRef.current = {
      key: groupBaselineKey,
      captured: false,
      updatedAt: null,
      readCount: 0,
      favoriteCount: 0,
      originalAuthor: null,
    };
  }
  if (!groupBaselineRef.current.captured && detail) {
    groupBaselineRef.current = {
      key: groupBaselineKey,
      captured: true,
      updatedAt: detail.updated_at ?? null,
      readCount: detail.read_count ?? 0,
      favoriteCount: detail.favorite_count ?? 0,
      originalAuthor: detail.original_author ?? null,
    };
  }
  const selectedLanguageRow = detail?.languages.find((l) => l.locale === lang) ?? null;
  const currentNovelRow = selectedLanguageRow;
  // 评分展示值（S016）属于当前 per-locale 行；详情未到达落 null。
  const rating = selectedLanguageRow?.rating ?? null;
  // 当前选中语言的 per-locale novel_id（章节挂在精确 novel_id 下，非 group root）：从书组 languages 按 locale 解析。
  // detail 未到达 / 该 locale 无版本时回退 null（章节 hook enabled=false 不发请求，不误显 root 章节）。
  const selectedNovelId = selectedLanguageRow?.id ?? null;
  /**
   * 乐观并发 token（#355）——**必须与表单快照同生命周期捕获，不能在提交时现取**。
   *
   * `BookInfo` 的受控字段只在 mount 时从 props 初始化一次，而它的 key 是
   * `${lang}-${rowId}-${formVersion}` —— **不含 `updated_at`**。于是 `book-detail` 被任一并发
   * mutation（如仍可点的连载状态切换，其 onSuccess 只 invalidate、不 bump formVersion）失效并
   * 重取后，表单里还是旧快照的值，而 `currentNovelRow.updated_at` 已经是新值。此时提交
   * 「旧字段 + 新 token」，服务端校验通过、照单全收 —— 期间别人改的东西被静默覆盖，
   * 正是本功能要防的那件事。
   *
   * 故按与 BookInfo 完全相同的 key 捕获：key 一致就沿用当初那份 token，key 变了（切语言 /
   * 换行 / 放弃更改 / 保存成功后 refetch 完成）才重新捕获 —— 那几个时刻表单本来也会重挂。
   * 用 render 期派生而非 effect：effect 在渲染之后才跑，中间那次渲染发出的保存会拿到错值。
   */
  const formSnapshotKey = `${lang}-${selectedLanguageRow?.id ?? 'none'}-${formVersion}`;
  const formSnapshotRef = useRef<{
    key: string;
    updatedAt: string | null;
    tags: string[];
    editionLabel: string | null;
    isRecommended: boolean;
  }>({
    key: '',
    updatedAt: null,
    tags: [],
    editionLabel: null,
    isRecommended: false,
  });
  if (formSnapshotRef.current.key !== formSnapshotKey) {
    formSnapshotRef.current = {
      key: formSnapshotKey,
      updatedAt: selectedLanguageRow?.updated_at ?? null,
      // 一并记下载入时的 tags，供「未改动就不下发」判定（见 localeBody 处说明）
      tags: [...(selectedLanguageRow?.tags ?? [])],
      // S024 改写版本同理：「变了才下发」的基线是载入值（见 localeBody 处说明）
      editionLabel: selectedLanguageRow?.edition_label ?? null,
      isRecommended: selectedLanguageRow?.is_recommended ?? false,
    };
  }
  // ADM-BOK-S010 连载状态按 per-locale novel 行独立维护；切语言立即读取当前行持久化值。
  const serial = selectedLanguageRow
    ? selectedLanguageRow.serial_status === 'COMPLETED'
      ? '已完结'
      : selectedLanguageRow.serial_status === 'HIATUS'
        ? '停更'
        : '连载中'
    : book.serial;
  const isFinished = serial === '已完结';
  /**
   * 详情头部 chip 的受众档：这里是**展示位**、不是层② 写入口，故按 A6 §15.1 的合成口径
   * （层② 覆盖层①）取值 —— 层② 为 `null` 是常态，直接渲染原值会让头部 chip 空白。
   * 层② 原值回显（含 `null`）在「上下架」tab 的层② 写卡片里（§15.3 要的是那一处）。
   */
  const selectedVisibility = selectedLanguageRow
    ? visibilityDisplayOf(
        selectedLanguageRow.visibility,
        selectedLanguageRow.group_default_visibility,
      )
    : { value: book.visibility, overridden: book.visibilityOverridden };

  const queryClient = useQueryClient();
  // 连载状态切换（ADM-BOK-S010）：写当前语言行 selectedNovelId；失败不改本地态。
  const serialMutation = useMutation({
    mutationFn: (next: '已完结' | '连载中') => {
      if (!selectedNovelId) throw new Error('当前语言版本尚未就绪');
      return booksApi.update(selectedNovelId, {
        serial_status: next === '已完结' ? 'COMPLETED' : 'ONGOING',
      });
    },
    onSuccess: async (dto) => {
      const label = dto.serial_status === 'COMPLETED' ? '已完结' : '连载中';
      toast(label === '已完结' ? '已标记为完结' : '已恢复为连载中', {
        tone: 'pos',
        icon: 'IconCheck',
      });
      // await 全部 invalidate：refetch 完成前 isPending 保持真、按钮不提前复活（adversarial F4）
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['books'] }),
        queryClient.invalidateQueries({ queryKey: ['book-detail', book.groupId] }),
        // 审计写 → 失效书籍操作日志缓存，回日志 tab 立即可见刚做的操作（codex #171 P2，不留 30s staleTime 陈旧）
        queryClient.invalidateQueries({ queryKey: ['book-logs', selectedNovelId] }),
      ]);
    },
    onError: (err) => {
      toast(`${book.displayTitle} · ${err instanceof Error ? err.message : '操作失败'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });
  const toggleSerial = () => {
    if (!selectedNovelId) return;
    if (isFinished) {
      setConfirm({
        title: `将《${book.displayTitle}》恢复为连载中？`,
        message: '当前语言版本的连载状态将恢复为连载中。',
        tone: 'default',
        icon: 'IconRefresh',
        confirmLabel: '恢复连载',
        // 真实接线：POST /books/:id PATCH serial_status=ONGOING
        onConfirm: () => serialMutation.mutate('连载中'),
      });
    } else {
      setConfirm({
        title: `将《${book.displayTitle}》标记为已完结？`,
        message: `当前语言版本共 ${selectedLanguageRow?.total_chapters ?? 0} 章。标记前必须先发布该语言版本的全部章节；已发布章节保持不变。`,
        tone: 'warn',
        icon: 'IconCheckCircle',
        confirmLabel: '标记完结',
        // 真实接线：POST /books/:id PATCH serial_status=COMPLETED
        onConfirm: () => serialMutation.mutate('已完结'),
      });
    }
  };
  const onFormChange = () => {
    if (!dirty) setDirty(true);
  };
  // 书籍信息保存（接真 · ADM-BOK 编辑）：
  //  - per-locale 文案（title/description/author_display）写**选中语言行** selectedNovelId；
  //  - per-locale 分类 / 标签 / 分级 / 评分与文案一起写选中语言行；
  //  - **书组级字段（原作者 / 展示基数）走 `PATCH /book-groups/:id`（A6 §16.4，dd#970）**：
  //    这批列已迁 `novel_groups`，`PATCH /books/:id` 提交它们一律 VALIDATION_FAILED。
  //    因此书组字段有改动时必然是两次 HTTP commit —— 这是契约把书组字段拆出独立端点的直接结果，
  //    不是本页选择；只在**书组字段真被改动**时才发第二个请求，普通改书名仍是单次 PATCH。
  //  - `source_external_id` 仍是 per-locale 行级列（dd#923 决议二），随 localeBody 写选中行。
  // 服务端按单行 / 单书组 id 写且自动落 admin_audit_logs（update_novel / update_book_group），无需前端补审计。
  // 千字单价与封面均已接真：前者写 per-locale cost_default_per_kw，后者先上传共享桶再写 cover_url。
  const saveMutation = useMutation({
    mutationFn: async (form: BookEditForm) => {
      if (!selectedNovelId) throw new Error('当前语言版本尚未就绪');
      const localeId = selectedNovelId;
      const groupId = detail?.group_id ?? book.groupId;
      // 权属已是书组级（dd#970）：从书组详情顶层读，不再从某一行读
      const isImported = detail?.source === 'IMPORTED';
      // 采集来源 identity 的「空串按 null」归一：下方 groupBody 拿它与持久值比 dirty，两侧必须同口径
      const nextOriginalAuthor =
        form.originalAuthor.trim() === '' ? null : form.originalAuthor.trim();
      const nextSourceExternalId =
        form.sourceExternalId.trim() === '' ? null : form.sourceExternalId.trim();
      const normalizeRecommendationText = (value: Record<string, unknown> | null | undefined) =>
        Object.fromEntries(
          Object.entries(value ?? {})
            .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
            .map(([locale, text]) => [locale, text.trim()] as const)
            .filter(([, text]) => text.length > 0)
            .sort(([left], [right]) => left.localeCompare(right)),
        );
      const recommendationTextI18n = normalizeRecommendationText(form.recommendationTextI18n);
      const persistedRecommendationTextForComparison = Object.fromEntries(
        Object.entries(currentNovelRow?.recommendation_text_i18n ?? {}).sort(([left], [right]) =>
          left.localeCompare(right),
        ),
      );
      const recommendationTextChanged =
        form.recommendationTextEdited &&
        JSON.stringify(recommendationTextI18n) !==
          JSON.stringify(persistedRecommendationTextForComparison);
      const nextFree = parseFreeChapterCount(form.freeChapterCount);
      // S024 改写版本：工厂译制集合成员不表达自己的标签（表单里是译制基准行的值），回传会让 §16.2 整次
      // 保存 VALIDATION_FAILED —— 成员行一律不下发。非成员也**仅在 trim 后值与载入值不同**时下发：
      // 无条件回发会把载入时的旧标签写回去，覆盖期间他人改过的值。本页不改 source_provider，
      // 故合并后的提供方就是当前行的值。
      const editionLabelDraft = parseEditionLabelDraft(form.editionLabel);
      const editionLabelFollowsBase = isTranslationFactoryProviderCode(
        currentNovelRow?.source_provider,
      );
      const localeBody: UpdateNovelRequest = {
        // 乐观并发前置条件（#355）：本表单把 title / tags / 推荐语等**全量快照**回传，两个运营先后
        // 保存时后者会用自己载入时的旧值覆盖前者刚写的新值。带上载入时的 updated_at，服务端在行锁内
        // 比对，不等则整笔拒（422 RESOURCE_CONFLICT）而不是静默覆盖。`currentNovelRow` 就是 localeId
        // 指向的那一行（selectedNovelId = selectedLanguageRow.id），故此处的时间戳与写目标同源。
        // 取 ref 而非 `currentNovelRow.updated_at`：后者是**提交那一刻**的最新值，中途任一次
        // refetch 都会把它刷成新值、而表单字段还停在旧快照 —— 那正好把本前置条件架空
        // （捕获时机的完整说明见 expectedUpdatedAtRef 定义处）。
        ...(formSnapshotRef.current.updatedAt
          ? { expected_updated_at: formSnapshotRef.current.updatedAt }
          : {}),
        title: form.title,
        description: form.description,
        author_display: form.authorDisplay.trim() === '' ? null : form.authorDisplay,
        ...(recommendationTextChanged ? { recommendation_text_i18n: recommendationTextI18n } : {}),
        // 封面 per-locale：仅本次上传了新封面（form.coverUrl 非空）才下发相对 ref；未上传则省略不动既有 cover_url
        ...(form.coverUrl ? { cover_url: form.coverUrl } : {}),
        age_rating: form.ageRating,
        rating: form.rating,
        // tags **仅在本轮真被改动时下发**（同基数 / 推荐语的既有做法）。无条件回发会让「只改书名」
        // 这类无关保存把载入时的旧标签数组写回去 —— 而标签归并 / 清除（`tags-cleanup-admin-service`）
        // 刻意不动 `updatedAt`（不让书在 C 端「最近更新」冒泡），故 `expected_updated_at` 检查不出它，
        // 写回等于撤销那次清理、还会把已物理删除的标签 id 重新种回 `novels.tags`。
        // 基线取**表单初始化快照**而非 `currentNovelRow.tags`：后者是提交那一刻的最新值，清理发生在
        // 载入之后时它已是清理后的新数组，与旧表单值一比就判成「改动过」→ 照样下发，白做。
        ...(JSON.stringify(form.tags) !== JSON.stringify(formSnapshotRef.current.tags)
          ? { tags: form.tags }
          : {}),
        ...(form.categoryId ? { category_id: form.categoryId } : {}),
        // 千字单价 per-locale（cost_default_per_kw 在每个语言行）：合法非负整数串才下发，避免空串/非法 422
        ...(/^(0|[1-9][0-9]*)$/.test(form.costPerKw)
          ? { cost_default_per_kw: form.costPerKw }
          : {}),
        ...(form.lengthClass !== (currentNovelRow?.length_class ?? null)
          ? { length_class: form.lengthClass }
          : {}),
        ...(form.lengthClass &&
        form.priceLevel &&
        form.priceLevel !== (currentNovelRow?.price_level ?? null)
          ? { price_level: form.priceLevel }
          : {}),
        ...(form.isRecommended !== formSnapshotRef.current.isRecommended
          ? { is_recommended: form.isRecommended }
          : {}),
        ...(form.limitedFreeEnabled !== (currentNovelRow?.limited_free_enabled ?? false)
          ? { limited_free_enabled: form.limitedFreeEnabled }
          : {}),
        // S020：篇幅置未标注时不下发本字段 —— 后端级联已覆盖为 false，重复提交 true 会被级联
        // 静默覆盖、提交 false 则是冗余字段（A6 §16.2「级联优先于本次提交」）
        ...(form.lengthClass !== null &&
        form.isNovelPurchaseEnabled !== (currentNovelRow?.is_novel_purchase_enabled ?? false)
          ? { is_novel_purchase_enabled: form.isNovelPurchaseEnabled }
          : {}),
        ...(nextFree !== null && nextFree !== (currentNovelRow?.free_chapter_count ?? 5)
          ? { free_chapter_count: nextFree }
          : {}),
        ...(!editionLabelFollowsBase &&
        editionLabelDraft.ok &&
        editionLabelDraft.value !== formSnapshotRef.current.editionLabel
          ? { edition_label: editionLabelDraft.value }
          : {}),
        // 采集来源外部条目号是 per-locale 行级列（dd#923 决议二），与其它行级字段同一次 PATCH。
        // **仅在与持久值不同时下发**：未进入来源维护面的运营改书名时，不能把页面加载时的
        // 来源表单值静默写回 DB 并落 audit（issue #594 第 3 项）。
        // 空串按 null 归一后再比，两侧同口径；未填仍是未填，不会被「空串 ≠ null」判成一次改动。
        // 外部ID必须同请求携带本语言行provider，避免按另一来源解释；陈旧行仍由updated_at拒绝。
        ...(isImported && nextSourceExternalId !== (currentNovelRow?.source_external_id ?? null)
          ? {
              source_external_id: nextSourceExternalId,
              source_provider: currentNovelRow?.source_provider ?? null,
            }
          : {}),
      };
      // 书组级字段（A6 §16.4）：**仅在本次真被改动时下发**，一个都没改就不发第二个请求。
      // 无条件回发会让「改书名」这类无关保存把页面加载时的旧值写回去 —— 若期间另一个运营改过
      // 基数 / 原作者，那次改动被静默覆盖（lost update）。
      // **展示基数清空输入按 0 处理**（normalizeBaseCount）——静默省略会让 UI 报「已保存」并清
      // dirty，刷新后旧值又回来，等于骗了运营一次。
      // dirty 基线取**表单初始化时那份书组快照**而非 `detail` 的最新值（同 tags 的既有做法）：
      // 后者是提交那一刻的值，他人中途改过原作者 / 基数时，我这边没动的表单值反而被判成「主动修改」
      // 而下发 —— 那正是要防的静默覆盖。
      const groupBaseline = groupBaselineRef.current;
      const groupBody: UpdateNovelGroupRequest = {
        ...(baseCountsSupported(detail) &&
        baseCountEdits.read !== undefined &&
        normalizeBaseCount(baseCountEdits.read) !== groupBaseline.readCount
          ? { read_count: normalizeBaseCount(baseCountEdits.read) }
          : {}),
        ...(baseCountsSupported(detail) &&
        baseCountEdits.favorite !== undefined &&
        normalizeBaseCount(baseCountEdits.favorite) !== groupBaseline.favoriteCount
          ? { favorite_count: normalizeBaseCount(baseCountEdits.favorite) }
          : {}),
        ...(isImported && nextOriginalAuthor !== groupBaseline.originalAuthor
          ? { original_author: nextOriginalAuthor }
          : {}),
      };
      const localeSaved = await booksApi.update(localeId, localeBody);
      // **第一段成功即刷新行级令牌**（部分成功恢复）：书组那段失败时 `onSuccess` 不跑、
      // `formVersion` 不 bump、也不 invalidate，行级令牌会停在保存前的旧值 —— 而行级改动**已经落库**
      // 且推进了 `updated_at`。运营再点一次保存，第一段必然 422 RESOURCE_CONFLICT，书组那半从此
      // 提交不上去，而 toast 只说「保存失败」。刷新令牌后重试第一段是幂等重写、第二段照常带书组
      // 基线令牌（真冲突仍会正确拒），两段都能走完。
      formSnapshotRef.current = {
        ...formSnapshotRef.current,
        updatedAt: localeSaved?.updated_at ?? formSnapshotRef.current.updatedAt,
        tags: [...(localeSaved?.tags ?? formSnapshotRef.current.tags)],
        // 两段提交部分成功时详情缓存仍旧；推荐 delta 须与已持久化值比较，允许反向修改后重试。
        isRecommended: localeSaved?.is_recommended ?? formSnapshotRef.current.isRecommended,
        editionLabel:
          localeSaved?.edition_label !== undefined
            ? localeSaved.edition_label
            : formSnapshotRef.current.editionLabel,
      };
      if (Object.keys(groupBody).length > 0) {
        // 乐观并发令牌取**书组基线**的 `updated_at`（§16.4）；基线未捕获（详情未到达）时省略 = 不做该检查
        try {
          await booksApi.updateGroup(groupId, {
            ...groupBody,
            ...(groupBaseline.updatedAt ? { expected_updated_at: groupBaseline.updatedAt } : {}),
          });
        } catch (error) {
          // 显式标注「行级已生效」：否则运营看到通用「保存失败」会以为整笔都没写，
          // 而书名 / 价格等行级改动其实已经在线上了。
          throw new PartialSaveError(error);
        }
      }
    },
    onSuccess: async () => {
      toast('书籍信息已保存', { tone: 'pos', icon: 'IconCheck' });
      // await 全部 invalidate：refetch 完成前 isPending 保持真、按钮不提前复活（对齐 serialMutation）
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['books'] }),
        queryClient.invalidateQueries({ queryKey: ['categories'] }),
        queryClient.invalidateQueries({ queryKey: ['tags'] }),
        queryClient.invalidateQueries({ queryKey: ['book-detail', book.groupId] }),
        // 推荐语和封面都会被书架推荐列表缓存；书籍信息保存后让已访问的运营页立即重取。
        queryClient.invalidateQueries({ queryKey: ['shelf-recommendations'] }),
        // 审计写 → 失效操作日志缓存，回日志 tab 立即可见
        queryClient.invalidateQueries({ queryKey: ['book-logs', selectedNovelId] }),
      ]);
      setDirty(false);
      // **remount 必须在 refetch 完成之后**：BookInfo 的受控字段（含数据基数 baseline 快照）只在
      // mount 时跑一次 useState 初始化。若先 bump，remount 读到的是保存前的 stale 书组代表行，而随后
      // 的 refetch 因 key 未变不会再初始化 → 表单显示旧值且 dirty=false，下次无关保存会把这批旧值
      // 重新提交、回退刚保存的改动。
      setFormVersion((v) => v + 1);
    },
    onError: (err) => {
      // 部分成功先解包：具体原因（陈旧快照）仍要从原错识别，但结论要额外说清
      // 「行级已生效、只有书组字段没写成」—— 否则运营按「整笔失败」重来会覆盖已生效的行级改动。
      const partial = err instanceof PartialSaveError;
      const reason = partial ? (err as PartialSaveError).cause : err;
      const staleError = staleSnapshotErrorMessage(reason, partial ? '书组信息' : '书籍信息');
      const detailMessage = staleError ?? (reason instanceof Error ? reason.message : '保存失败');
      toast(
        partial
          ? `${book.displayTitle} · 语言版本已保存，书组信息未保存：${detailMessage}`
          : `${book.displayTitle} · ${detailMessage}`,
        {
          tone: 'neg',
          icon: 'IconAlert',
        },
      );
      // 行级已落库 → 让列表 / 详情读到新值；**不 bump `formVersion`**：书组那半的未保存编辑
      // （基数 / 原作者）必须留在表单里供重试，bump 会连同基线一起重置、把它们丢掉。
      if (partial) {
        void queryClient.invalidateQueries({ queryKey: ['books'] });
      }
    },
  });
  const handleSave = () => {
    if (
      !selectedNovelId ||
      !editFormRef.current ||
      saveMutation.isPending ||
      recommendationTranslationPending
    )
      return;
    const form = editFormRef.current;
    const nextFree = parseFreeChapterCount(form.freeChapterCount);
    if (nextFree === null) return;
    if (!parseEditionLabelDraft(form.editionLabel).ok) return;
    const persistedFree = currentNovelRow?.free_chapter_count ?? 5;
    if (nextFree !== persistedFree) {
      setConfirm({
        title: '调整免费章节数？',
        message:
          '将按新的免费章节数重算该语言版本全部章节的免费标记，并覆盖单章手工设置的营销免费值。章节价格不受影响。',
        tone: 'warn',
        icon: 'IconAlert',
        confirmLabel: '确认并保存',
        cancelLabel: '返回修改',
        onConfirm: () => saveMutation.mutate(form),
      });
      return;
    }
    saveMutation.mutate(form);
  };
  // 放弃更改：清 dirty / 书组基数草稿 + bump formVersion → BookInfo 从 props 原值重挂。
  const discardCurrentEdits = () => {
    setDirty(false);
    setBaseCountEdits({});
    setFormVersion((version) => version + 1);
  };
  const handleDiscard = () => discardCurrentEdits();
  const handleSelectLanguage = (nextLanguage: string) => {
    if (nextLanguage === lang) return;
    if (saveMutation.isPending || recommendationTranslationPending) return;
    const next = bookRowForSibling(book, nextLanguage);
    if (!next) return;
    const jump = () => onOpenBook(next);
    if (!dirty) {
      jump();
      return;
    }
    setConfirm({
      title: '打开其他语言的书？',
      message: '当前这本书有未保存的更改。跳转后这些更改将被丢弃。',
      tone: 'warn',
      icon: 'IconAlert',
      confirmLabel: '丢弃更改并跳转',
      cancelLabel: '继续编辑',
      onConfirm: () => {
        discardCurrentEdits();
        jump();
      },
    });
  };
  const handleSelectTab = (nextTab: typeof tab) => {
    if (nextTab === tab) return;
    if (saveMutation.isPending || recommendationTranslationPending) return;
    if (tab !== 'info' || !dirty) {
      setTab(nextTab);
      return;
    }
    setConfirm({
      title: '离开书籍信息？',
      message: '当前书籍信息有未保存的更改。切换功能页后，这些更改将被丢弃。',
      tone: 'warn',
      icon: 'IconAlert',
      confirmLabel: '丢弃更改并离开',
      cancelLabel: '继续编辑',
      onConfirm: () => {
        discardCurrentEdits();
        setTab(nextTab);
      },
    });
  };
  const handleBack = () => {
    if (saveMutation.isPending || recommendationTranslationPending) return;
    if (!dirty) {
      onBack();
      return;
    }
    setConfirm({
      title: '有未保存的更改',
      message: `《${book.displayTitle}》的信息已有改动但尚未保存。离开后未保存的更改将被丢弃。`,
      tone: 'warn',
      icon: 'IconAlert',
      confirmLabel: '丢弃更改并离开',
      cancelLabel: '继续编辑',
      onConfirm: () => onBack(),
    });
  };

  // 2026-05-28 PR#63 派生：可新增的语言版本 = system_languages WHERE enabled
  const { data: sys } = useSysConfig();
  const ALL_LANGS = sys.languages
    .filter((l) => l.enabled)
    .map((l) => ({
      code: l.language_code,
      zh: l.display_name_i18n.zh,
    }));
  // 推荐语语言候选覆盖历史语言：系统语言即使已停用也允许维护；
  // 同时保留已有 Map 中不再出现在系统配置里的语言，避免运营编辑时误删。
  const recommendationLanguageCodes = [
    ...new Set([
      ...sys.languages.map((language) => language.language_code),
      ...Object.keys(currentNovelRow?.recommendation_text_i18n ?? {}),
    ]),
  ];
  const availableToAdd = ALL_LANGS.filter((l) => !bookLangs.includes(l.code));
  const publishTimeZone =
    sys.languages.find(
      (language) => language.language_code === (lang.split('-')[0] ?? lang).toLocaleLowerCase(),
    )?.default_tz ?? null;

  const tabs = [
    { id: 'info', label: '书籍信息', icon: 'IconBook' },
    { id: 'chapters', label: '章节', icon: 'IconList' },
    { id: 'stats', label: '数据', icon: 'IconChart' },
    { id: 'status', label: '上下架', icon: 'IconLayers' },
    { id: 'logs', label: '日志', icon: 'IconHistory' },
  ] as const;

  // 详情 query 加载中 → 详情骨架（返回 + 大页头 + framed KPI + tabs + section 同形）。
  // 列表已带来 book 基础字段，但评分 / 各语言版本 / per-locale 章节挂载等真实详情来自 useBookDetail，
  // 未到达前用骨架占位，数据到达无位移替换。放在所有 hooks 之后以守 Rules of Hooks。
  // 传 onBack：useBookDetail 慢/挂起时返回控件保持可用，运营不被困在骨架里（此时表单未挂载、必非 dirty，直接回列表）。
  if (detailLoading) return <DetailSkeleton onBack={onBack} />;

  return (
    <div className="page page-enter">
      <div className="hstack" style={{ gap: 8, fontSize: 12, color: 'var(--fg-muted)' }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={handleBack}
          disabled={saveMutation.isPending || recommendationTranslationPending}
          title={
            saveMutation.isPending
              ? '书籍信息保存中，请稍候'
              : recommendationTranslationPending
                ? '推荐语翻译中，请稍候'
                : undefined
          }
        >
          <Icons.IconChevLeft size={12} />
          返回书籍列表
        </button>
        {dirty && (
          <span className="chip chip-warn" style={{ fontSize: 10 }}>
            ● 有未保存的更改
          </span>
        )}
      </div>

      <div className="card" style={{ padding: 16, display: 'flex', gap: 16 }}>
        <BookCover
          src={currentNovelRow?.cover_url ?? book.coverUrl}
          alt={`${currentNovelRow?.title ?? book.displayTitle} 封面`}
          hue={book.coverHue}
          width={96}
          height={130}
          style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              padding: 10,
              color: 'white',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.1, marginBottom: 6 }}>
              {book.originalTitle}
            </div>
            <div style={{ fontSize: 8, opacity: 0.8 }}>{book.author}</div>
          </div>
        </BookCover>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div className="hstack" style={{ gap: 8 }}>
                <h1 className="page-title" style={{ fontSize: 22 }}>
                  {currentNovelRow?.title ?? book.displayTitle}
                </h1>
                <span
                  className={`chip ${visibilityChipClass(selectedVisibility.value)}`}
                  title={VISIBILITY_META[selectedVisibility.value].desc}
                >
                  ● {visibilityLabel(selectedVisibility.value)}
                </span>
                {!selectedVisibility.overridden && (
                  <span className="chip" style={{ fontSize: 9, opacity: 0.7 }} title={INHERIT_DESC}>
                    继承
                  </span>
                )}
                <span className={`chip ${serial === '已完结' ? 'chip-pos' : 'chip-info'}`}>
                  {serial}
                </span>
              </div>
              <div
                className="hstack"
                style={{
                  fontSize: 11,
                  color: 'var(--fg-muted)',
                  marginTop: 4,
                  flexWrap: 'wrap',
                  gap: 4,
                }}
              >
                <CopyableCode
                  value={book.bookGroupCode ?? currentNovelRow?.book_group_code}
                  label="书组编码"
                  prefix="书组"
                />
                <span>·</span>
                <CopyableCode
                  value={
                    currentNovelRow?.book_code ??
                    book.languageCodes.find((row) => row.locale === lang)?.bookCode
                  }
                  label="书编码"
                  prefix="书编码"
                />
                <span>·</span>
                {currentNovelRow?.locale ? (
                  <LangChip code={currentNovelRow.locale} style={{ fontSize: 10 }} />
                ) : (
                  fmtLang(lang)
                )}
                <EditionLabelTag label={currentNovelRow?.edition_label} />
                {' · '}
                {currentNovelRow?.title ?? book.originalTitle} ·{' '}
                {currentNovelRow?.author_display ?? book.author} · 创建于 {fmtDate(book.createdAt)}
              </div>
            </div>
            <div className="hstack" style={{ gap: 6 }}>
              <button
                className="btn btn-sm"
                onClick={() => handleSelectTab('chapters')}
                title="前往章节页配置书籍级发布计划与查看队列"
                disabled={saveMutation.isPending || recommendationTranslationPending}
              >
                <Icons.IconClock size={11} />
                管理发布计划
              </button>
              <button className="btn btn-sm" onClick={() => setPreviewing(true)}>
                <Icons.IconEye size={11} />
                预览
              </button>
              {/* 连载状态：root/admin/operator 可写当前语言 novel 行 → viewer/语言行未就绪时禁用 */}
              {isFinished ? (
                <button
                  className="btn btn-sm"
                  onClick={toggleSerial}
                  disabled={!canWrite || !selectedNovelId || serialMutation.isPending}
                  title={
                    !canWrite
                      ? FORBIDDEN_ROLE_TITLE
                      : selectedNovelId
                        ? '恢复为连载中'
                        : '当前语言版本尚未就绪'
                  }
                  style={canWrite ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                >
                  <Icons.IconRefresh size={11} />
                  恢复连载
                </button>
              ) : (
                <button
                  className="btn btn-sm"
                  onClick={toggleSerial}
                  disabled={!canWrite || !selectedNovelId || serialMutation.isPending}
                  title={
                    !canWrite
                      ? FORBIDDEN_ROLE_TITLE
                      : selectedNovelId
                        ? '标记为已完结 · 当前语言版本须先发布全部章节'
                        : '当前语言版本尚未就绪'
                  }
                  style={canWrite ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                >
                  <Icons.IconCheckCircle size={11} />
                  标记完结
                </button>
              )}
              {dirty && (
                <button
                  className="btn btn-sm"
                  onClick={handleDiscard}
                  disabled={saveMutation.isPending || recommendationTranslationPending}
                >
                  放弃更改
                </button>
              )}
              {/* 书籍信息保存（接真）：per-locale 字段 PATCH /books/:id，书组级字段 PATCH /book-groups/:id（§16.4）；viewer 无写权禁用 */}
              {dirty && (
                <button
                  className="btn btn-sm btn-primary"
                  disabled={
                    !canWrite ||
                    !selectedNovelId ||
                    saveMutation.isPending ||
                    recommendationTranslationPending ||
                    !bookInfoFormValid
                  }
                  title={
                    canWrite
                      ? recommendationTranslationPending
                        ? '推荐语翻译中，请稍候'
                        : '保存书籍信息'
                      : FORBIDDEN_ROLE_TITLE
                  }
                  style={canWrite ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                  onClick={handleSave}
                >
                  <Icons.IconCheck size={11} />
                  {saveMutation.isPending
                    ? '保存中…'
                    : recommendationTranslationPending
                      ? '翻译中…'
                      : '保存'}
                </button>
              )}
            </div>
          </div>

          {/* Lang switcher */}
          <div className="hstack" style={{ gap: 6, paddingTop: 4 }}>
            <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>当前书</span>
            {bookLangs.map((l) => {
              const langCode =
                detail?.languages.find((row) => row.locale === l)?.book_code ??
                book.languageCodes.find((row) => row.locale === l)?.bookCode ??
                null;
              return (
                <button
                  key={l}
                  onClick={() => handleSelectLanguage(l)}
                  disabled={saveMutation.isPending || recommendationTranslationPending}
                  className="focusable"
                  style={{
                    padding: '3px 8px',
                    borderRadius: 4,
                    border: lang === l ? '1px solid var(--accent)' : '1px solid var(--border)',
                    background: lang === l ? 'var(--accent-soft)' : 'var(--bg-elev)',
                    color: lang === l ? 'var(--accent-fg)' : 'var(--fg-muted)',
                    font: 'inherit',
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                  title={
                    l === lang
                      ? langCode
                        ? `当前书 ${langCode}`
                        : '当前这本书'
                      : langCode
                        ? `打开该书 ${langCode}`
                        : '打开该语言的独立书籍'
                  }
                >
                  <span className="mono">{l.toUpperCase()}</span>
                  <span
                    style={{
                      marginLeft: 4,
                      color: lang === l ? 'inherit' : 'var(--fg-faint)',
                      fontWeight: 400,
                    }}
                  >
                    {langName(l) || l.toUpperCase()}
                  </span>
                  {langCode ? (
                    <span className="mono" style={{ marginLeft: 4, fontSize: 10, opacity: 0.8 }}>
                      {langCode}
                    </span>
                  ) : null}
                </button>
              );
            })}
            {availableToAdd.length > 0 && (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                {/* 新增语言版本（接真）：打开对话框收集 title/description/cover_url（封面复用 root）
                    → POST /books/:id/languages（createNovelLanguage 继承书组属性）*/}
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={!canWrite}
                  title={canWrite ? '新增语言版本' : FORBIDDEN_ROLE_TITLE}
                  style={canWrite ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                  onClick={() => canWrite && setNewLangOpen(true)}
                >
                  <Icons.IconPlus size={11} />
                </button>
              </div>
            )}
          </div>

          {/* Quick stats — 阅读/付费/付费章节走真实 ADM-BOK-S014 stats（stats 缺字段保持 0 默认）；
              收藏取书组真实收藏聚合 real_favorite_count（**运营统计口径不含运营基数**，ADM-BOK-S017：
              基数只影响客户端展示、不计入统计报表）；评分（S016）取当前 per-locale 行 rating；
              累计收入无聚合端点 → 占位「—」（不编造法币派生） */}
          <div
            className="hstack"
            style={{ gap: 16, paddingTop: 8, borderTop: '1px dashed var(--border)' }}
          >
            <QS label="章节" v={currentNovelRow?.total_chapters ?? 0} />
            <QS label="字数" v={formatWanWords(currentNovelRow?.total_words)} />
            <QS label="阅读用户" v={stats.readers.toLocaleString()} />
            <QS
              label="收藏"
              v={
                typeof detail?.real_favorite_count === 'number'
                  ? detail.real_favorite_count.toLocaleString()
                  : '—'
              }
            />
            <QS
              label="付费用户"
              v={stats.payers.toLocaleString()}
              title="已注销用户经匿名化折叠为 1，观测近似，非结算"
            />
            <QS label="付费章节" v={stats.paidChapters.toLocaleString()} />
            <QS
              label="评分"
              v={
                rating === null ? (
                  '—'
                ) : (
                  <span>
                    <span style={{ color: 'var(--warn)' }}>★</span> {rating.toFixed(1)}
                  </span>
                )
              }
            />
          </div>
        </div>
      </div>

      <div
        style={{
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          gap: 0,
          marginTop: 6,
        }}
      >
        {tabs
          .filter((t) => t.id !== 'logs' || canViewLogs)
          .map((t) => (
            <button
              key={t.id}
              onClick={() => handleSelectTab(t.id)}
              disabled={saveMutation.isPending || recommendationTranslationPending}
              style={{
                border: 0,
                background: 'transparent',
                padding: '9px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                font: 'inherit',
                fontSize: 12.5,
                color: tab === t.id ? 'var(--fg-strong)' : 'var(--fg-muted)',
                fontWeight: tab === t.id ? 500 : 400,
                borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              {createElement(Icons[t.icon], { size: 13 })} {t.label}
            </button>
          ))}
      </div>

      <div>
        {tab === 'info' && (
          <fieldset
            disabled={saveMutation.isPending || !selectedNovelId}
            inert={saveMutation.isPending || !selectedNovelId}
            aria-busy={saveMutation.isPending}
            style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
          >
            {/* key 含 lang + 选中行 id + formVersion：切语言/详情到达/放弃更改时 remount → 受控字段从 props 原值重挂。
                dirty 由 BookInfo 受控字段编辑经 onDirty 上报（不再用 wrapper onInput/onChange，避免其它 tab 误触 dirty）。 */}
            <BookInfo
              key={`${lang}-${currentNovelRow?.id ?? 'none'}-${formVersion}`}
              book={book}
              lang={lang}
              currentNovelRow={currentNovelRow}
              representativeRow={representativeRow}
              editFormRef={editFormRef}
              onDirty={onFormChange}
              catList={catList}
              tagList={tagList}
              onRequestConfirm={setConfirm}
              baseCounts={{
                // 契约 A6 §15.2：基数在**书组详情顶层**，语言条目不含独立基数 → 一律读 detail 顶层，
                // 不读 languages[] 里的同名字段（合规响应里那些字段可能根本不存在）
                read: baseCountEdits.read ?? String(detail?.read_count ?? ''),
                favorite: baseCountEdits.favorite ?? String(detail?.favorite_count ?? ''),
                // 真实值 / 客户端展示值同样只读顶层；后端未返（旧 admin-api）→ null，UI 显示「—」不编 0
                realRead:
                  typeof detail?.real_read_count === 'number' ? detail.real_read_count : null,
                realFavorite:
                  typeof detail?.real_favorite_count === 'number'
                    ? detail.real_favorite_count
                    : null,
                displayRead:
                  typeof detail?.display_read_count === 'number' ? detail.display_read_count : null,
                displayFavorite:
                  typeof detail?.display_favorite_count === 'number'
                    ? detail.display_favorite_count
                    : null,
                // dd#970：展示基数是书组唯一一份（`novel_groups`），任一语言版本都能改（写 §16.4），
                // 不再有「只有书组根语言可编辑」这道闸
                // 保存进行中禁用：saveMutation 成功后会 bump formVersion 清空本地编辑，
                // 此间新输入的值既不在已发出的请求里、也会被清掉（改了个寂寞）
                supported: baseCountsSupported(detail) && !saveMutation.isPending,
              }}
              // 只落被编辑的那个字段：未触碰的字段永远不进 payload（见上方 state 注释）
              onBaseCountChange={(field, v) =>
                setBaseCountEdits((prev) => ({ ...prev, [field]: v }))
              }
              recommendationLanguageCodes={recommendationLanguageCodes}
              onRecommendationTranslationPendingChange={setRecommendationTranslationPending}
              onFormValidityChange={setBookInfoFormValid}
            />
          </fieldset>
        )}
        {tab === 'chapters' && (
          <BookChapters
            book={book}
            lang={lang}
            novelId={selectedNovelId}
            currentNovelRow={currentNovelRow}
            publishTimeZone={publishTimeZone}
          />
        )}
        {tab === 'stats' && <BookStats book={book} stats={stats} />}
        {tab === 'status' && (
          <BookStatus
            book={book}
            locale={lang}
            novelId={selectedNovelId}
            novelRow={selectedLanguageRow}
          />
        )}
        {tab === 'logs' && canViewLogs && selectedNovelId && <BookLogs novelId={selectedNovelId} />}
      </div>

      {previewing && (
        <BookPreviewModal
          book={book}
          lang={lang}
          bookLangs={bookLangs}
          currentNovelRow={currentNovelRow}
          categoryName={
            catList.find((category) => category.id === currentNovelRow?.category_id)?.name ?? '—'
          }
          novelId={selectedNovelId}
          // 预览是「客户端所见」的模拟 → 用**展示口径**合成值（基数 + 真实），与 C 端书卡 / 详情
          // 下发的 read_count / favorite_count 同源（A6 §15.2 display_*）。未编辑时直接用服务端
          // display_*；有未保存草稿时用「当前输入 + real_*」即时预估，与 BaseCountRow 保持同一交互。
          displayReadCount={
            baseCountEdits.read !== undefined
              ? typeof detail?.real_read_count === 'number'
                ? normalizeBaseCount(baseCountEdits.read) + detail.real_read_count
                : null
              : typeof detail?.display_read_count === 'number'
                ? detail.display_read_count
                : null
          }
          displayFavoriteCount={
            baseCountEdits.favorite !== undefined
              ? typeof detail?.real_favorite_count === 'number'
                ? normalizeBaseCount(baseCountEdits.favorite) + detail.real_favorite_count
                : null
              : typeof detail?.display_favorite_count === 'number'
                ? detail.display_favorite_count
                : null
          }
          onSelectLang={handleSelectLanguage}
          onClose={() => setPreviewing(false)}
        />
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          tone={confirm.tone}
          icon={confirm.icon}
          requireTyping={confirm.requireTyping}
          confirmLabel={confirm.confirmLabel}
          cancelLabel={confirm.cancelLabel}
          onConfirm={() => confirm.onConfirm?.()}
          onClose={() => setConfirm(null)}
        />
      )}
      {newLangOpen && (
        <NewLanguageDialog
          book={book}
          representativeRow={representativeRow}
          availableLocales={availableToAdd}
          onClose={() => setNewLangOpen(false)}
        />
      )}
    </div>
  );
}

// =========================================================
// 客户端预览弹窗
// =========================================================
function BookPreviewModal({
  book,
  lang,
  bookLangs,
  currentNovelRow,
  categoryName,
  novelId,
  displayReadCount,
  displayFavoriteCount,
  onSelectLang,
  onClose,
}: {
  book: BookRow;
  lang: string;
  bookLangs: string[];
  currentNovelRow: NovelResponse | null;
  categoryName: string;
  novelId: string | null;
  /** 客户端展示口径（基数 + 真实），后端派生 display_*；未就绪为 null → 显示「—」不编 0 */
  displayReadCount: number | null;
  displayFavoriteCount: number | null;
  onSelectLang: (l: string) => void;
  onClose: () => void;
}) {
  // 预览与章节 tab 同源：按当前语言版本 novel_id 取章节，不误用书组 root 行。
  const { data: CHAPTERS } = useChapters(novelId);
  const intros: Record<string, string> = {
    zh: '年轻誊写员的一封信，点燃了沿海小镇的秘密。一个严酷的冬天，三座隐秘的港口，潮汐与抉择决定了天明时还能留下什么。',
    en: "A young scrivener's letter sets a coastal town's secret ablaze. Across one harsh winter and three hidden ports, choices and tides decide what survives the dawn.",
    de: 'Der Brief eines jungen Schreibers setzt das Geheimnis einer Küstenstadt in Brand. Drei verborgene Häfen, ein harter Winter — Gezeiten und Entscheidungen bestimmen, was den Morgen überlebt.',
    fr: "La lettre d'un jeune copiste enflamme le secret d'une ville côtière. Trois ports cachés, un hiver rude — marées et choix décident de ce qui survivra à l'aube.",
    it: "La lettera di un giovane scrivano accende il segreto di una città costiera. Tre porti nascosti, un inverno duro — maree e scelte decidono cosa sopravvive all'alba.",
    es: 'La carta de un joven escribiente enciende el secreto de un pueblo costero. Tres puertos ocultos, un invierno duro — mareas y decisiones determinan qué sobrevive al amanecer.',
    nl: 'De brief van een jonge schrijver ontketent het geheim van een kuststad.',
    pl: 'List młodego skryby rozpala sekret nadmorskiego miasta.',
    pt: 'A carta de um jovem escrivão acende o segredo de uma cidade costeira.',
  };
  const maintained = bookLangs.includes(lang);
  // 标题必须来自当前明确选中的 per-locale member；不再把 EN 当作无标注代表行。
  const t = currentNovelRow?.title ?? book.displayTitle;
  const intro = maintained ? intros[lang] || intros.en : intros.en;
  const freeChapterCount = currentNovelRow?.free_chapter_count ?? 0;
  const serial = currentNovelRow
    ? currentNovelRow.serial_status === 'COMPLETED'
      ? '已完结'
      : currentNovelRow.serial_status === 'HIATUS'
        ? '停更'
        : '连载中'
    : '—';
  const publishedChapters = currentNovelRow?.total_chapters ?? 0;
  const ch = CHAPTERS.slice(0, 6);
  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div
        className="dialog"
        style={{ width: 720, display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 7,
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.IconEye size={16} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500, fontSize: 14 }}>客户端预览 · {t}</div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>
              {book.bookGroupCode ? `书组 ${book.bookGroupCode} · ` : ''}
              按当前选定语言渲染 · 未维护语言显示英文兜底
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <Icons.IconX size={14} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 18, background: 'var(--bg-subtle)' }}>
          <div className="hstack" style={{ gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>预览语言：</span>
            {bookLangs.map((l) => (
              <button
                key={l}
                onClick={() => onSelectLang(l)}
                className="focusable"
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  border: lang === l ? '1px solid var(--accent)' : '1px solid var(--border)',
                  background: lang === l ? 'var(--accent-soft)' : 'var(--bg-elev)',
                  color: lang === l ? 'var(--accent-fg)' : 'var(--fg-muted)',
                  font: 'inherit',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                <span className="mono">{l.toUpperCase()}</span>
                <span style={{ marginLeft: 4, color: lang === l ? 'inherit' : 'var(--fg-faint)' }}>
                  {langName(l) || l.toUpperCase()}
                </span>
              </button>
            ))}
            {!maintained && (
              <span className="chip chip-warn" style={{ fontSize: 10, marginLeft: 4 }}>
                <Icons.IconAlert size={9} />
                当前语言未维护 · fallback EN
              </span>
            )}
          </div>

          <div
            style={{
              margin: '0 auto',
              width: 360,
              padding: 14,
              borderRadius: 28,
              background: 'var(--bg-inset)',
              border: '1px solid var(--border)',
              boxShadow: '0 10px 40px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.03)',
            }}
          >
            <div style={{ background: 'var(--bg-elev)', borderRadius: 18, overflow: 'hidden' }}>
              <div
                className="hstack"
                style={{ padding: '8px 14px', fontSize: 10.5, color: 'var(--fg-muted)' }}
              >
                <span className="mono" style={{ fontWeight: 600 }}>
                  9:41
                </span>
                <div style={{ flex: 1 }} />
                <span className="mono">•••• 4G</span>
              </div>
              <div className="hstack" style={{ padding: '0 12px 8px', gap: 8 }}>
                <Icons.IconChevLeft size={16} style={{ color: 'var(--fg-muted)' }} />
                <div style={{ flex: 1 }} />
                <Icons.IconHeart size={15} style={{ color: 'var(--fg-muted)' }} />
                <Icons.IconMoreV size={15} style={{ color: 'var(--fg-muted)' }} />
              </div>
              <div style={{ padding: '0 14px 12px', display: 'flex', gap: 12 }}>
                <BookCover
                  src={currentNovelRow?.cover_url ?? book.coverUrl}
                  alt={`${t} 封面`}
                  hue={book.coverHue}
                  width={90}
                  height={124}
                  style={{ boxShadow: '0 6px 14px rgba(0,0,0,0.18)' }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      padding: 8,
                      color: 'white',
                      fontSize: 9.5,
                      lineHeight: 1.15,
                      fontWeight: 700,
                      opacity: 0.95,
                    }}
                  >
                    {book.originalTitle}
                  </div>
                </BookCover>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      marginBottom: 4,
                      color: 'var(--fg-strong)',
                    }}
                  >
                    {t}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{book.author}</div>
                  <div className="hstack" style={{ gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
                    <span className="chip" style={{ fontSize: 9, padding: '0 5px' }}>
                      {categoryName}
                    </span>
                    <span className="chip" style={{ fontSize: 9, padding: '0 5px' }}>
                      {serial}
                    </span>
                  </div>
                  <div
                    className="hstack"
                    style={{ gap: 12, marginTop: 10, fontSize: 10, color: 'var(--fg-muted)' }}
                  >
                    <span>
                      {/* 客户端展示口径（基数 + 真实），与 C 端书卡同源；后端未返 display_* → 「—」不编 0 */}
                      <span className="mono strong" style={{ color: 'var(--fg)', fontSize: 12 }}>
                        {displayReadCount === null ? '—' : displayReadCount.toLocaleString()}
                      </span>{' '}
                      读
                    </span>
                    <span>
                      <span className="mono strong" style={{ color: 'var(--fg)', fontSize: 12 }}>
                        {displayFavoriteCount === null
                          ? '—'
                          : displayFavoriteCount.toLocaleString()}
                      </span>{' '}
                      藏
                    </span>
                    <span>
                      <span className="mono strong" style={{ color: 'var(--fg)', fontSize: 12 }}>
                        {publishedChapters}
                      </span>{' '}
                      章
                    </span>
                    {/* 去假（对抗审查 P2-2）：原硬编码 ★ 4.7 → 取真实 currentNovelRow.rating（S016 运营展示评分），无则「—」 */}
                    <span className="mono">
                      <span style={{ color: 'var(--warn)' }}>★</span>{' '}
                      {currentNovelRow?.rating != null ? currentNovelRow.rating.toFixed(1) : '—'}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ padding: '0 14px 12px' }}>
                <div style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginBottom: 4 }}>
                  简介
                </div>
                <div
                  style={{
                    fontSize: 11.5,
                    lineHeight: 1.55,
                    color: 'var(--fg)',
                    maxHeight: 70,
                    overflow: 'hidden',
                  }}
                >
                  {intro}
                </div>
              </div>
              <div
                style={{
                  padding: '10px 14px 8px',
                  borderTop: '1px solid var(--border)',
                  background: 'var(--bg-subtle)',
                }}
              >
                <div className="hstack" style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 500 }}>章节</span>
                  <span className="mono muted" style={{ fontSize: 10, marginLeft: 4 }}>
                    {publishedChapters}
                  </span>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 10, color: 'var(--fg-muted)' }}>正序</span>
                </div>
                {ch.map((c, i) => {
                  const free = c.isFree || i < freeChapterCount;
                  return (
                    <div
                      key={c.id}
                      className="hstack"
                      style={{
                        padding: '5px 0',
                        borderBottom: i < ch.length - 1 ? '1px solid var(--border)' : 0,
                        gap: 8,
                      }}
                    >
                      <span className="mono muted" style={{ fontSize: 10, width: 22 }}>
                        {String(c.no).padStart(2, '0')}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 11,
                          color: 'var(--fg)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {c.title}
                      </span>
                      {free ? (
                        <span className="chip chip-pos" style={{ fontSize: 9, padding: '0 5px' }}>
                          免费
                        </span>
                      ) : (
                        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-muted)' }}>
                          {c.price} COIN
                        </span>
                      )}
                    </div>
                  );
                })}
                <div style={{ paddingTop: 8, textAlign: 'center' }}>
                  <span className="mono" style={{ fontSize: 10.5, color: 'var(--accent)' }}>
                    查看全部 {publishedChapters} 章 ›
                  </span>
                </div>
              </div>
              <div style={{ padding: 12, display: 'flex', gap: 8 }}>
                <button className="btn" style={{ flex: 1 }}>
                  加书架
                </button>
                <button className="btn btn-accent" style={{ flex: 2 }}>
                  开始阅读
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
            <Icons.IconInfo
              size={11}
              style={{ verticalAlign: 'middle', marginRight: 4, color: 'var(--info)' }}
            />
            预览仅展示移动端样式 · 实际呈现以客户端版本为准
          </span>
          <button className="btn" onClick={onClose}>
            关闭预览
          </button>
        </div>
      </div>
    </div>
  );
}

function QS({ label, v, title }: { label: string; v: ReactNode; title?: string }) {
  return (
    <div title={title}>
      <div className="mono" style={{ fontSize: 15, fontWeight: 600 }}>
        {v}
      </div>
      <div style={{ fontSize: 10, color: 'var(--fg-faint)' }}>{label}</div>
    </div>
  );
}

/**
 * 详情页 FormRow（180px 标签 + 字段）— 复用共享 ~/ui/FormRow，
 * 逐属性对齐原型 books.jsx：labelWidth=180 / pad="12px 0"（gap 走默认 16）。
 */
const FormRow = (p: FormRowProps) => <UIFormRow labelWidth={180} pad="12px 0" {...p} />;

// =========================================================
// 书籍信息 tab
// =========================================================
function BookInfo({
  book,
  lang,
  currentNovelRow,
  representativeRow,
  editFormRef,
  onDirty,
  catList,
  tagList,
  baseCounts,
  onBaseCountChange,
  recommendationLanguageCodes,
  onRecommendationTranslationPendingChange,
  onFormValidityChange,
  onRequestConfirm,
}: {
  book: BookRow;
  lang: string;
  currentNovelRow: NovelResponse | null;
  representativeRow: NovelResponse | null;
  editFormRef: { current: BookEditForm | null };
  onDirty: () => void;
  /** ADM-BOK-S017 数据基数（书组级，state 在 BookDetail 层，切语言 / 切 tab 不丢）*/
  baseCounts: {
    read: string;
    favorite: string;
    realRead: number | null;
    realFavorite: number | null;
    displayRead: number | null;
    displayFavorite: number | null;
    supported: boolean;
  };
  onBaseCountChange: (field: 'read' | 'favorite', value: string) => void;
  recommendationLanguageCodes: string[];
  onRecommendationTranslationPendingChange: (pending: boolean) => void;
  /** 表单本地校验结果变化时上报（页头保存按钮据此禁用）*/
  onFormValidityChange: (valid: boolean) => void;
  catList: CatTagRow[];
  tagList: CatTagRow[];
  /** 启用买断是价格决策，落表单前先走页面级二次确认（复用本页单例 ConfirmDialog）*/
  onRequestConfirm: (req: ConfirmReq) => void;
}) {
  const canWriteRecommendation = useCanWriteBooks();
  const fromEn = lang !== 'en';
  // dd#970：原作信息 / 权属 / 展示基数是**书组级**，任一语言版本都能改（写 §16.4），
  // 不再有「必须切回书组根语言」这道闸。行级的 `source_external_id` 仍写当前行。
  const canViewSource = useCanViewBookSource();
  const canMaintainSource = useCanWriteBooks();
  const [sourceMaintenanceOpen, setSourceMaintenanceOpen] = useState(false);
  const sourceProvider = currentNovelRow?.source_provider ?? currentNovelRow?.source ?? '';
  const originalLanguage = currentNovelRow?.original_locale.toUpperCase() ?? '—';
  // 受控编辑 state：从 props 原值初始化（本组件 key 含 lang/选中行 id/formVersion，切换/到达/放弃时 remount 重挂）。
  // per-locale 文案 / 分级 / 分类 / 标签 / 评分取当前语言行；原作 / 来源 identity 与展示基数取 root。
  const [form, setForm] = useState<BookEditForm>(() => ({
    title: currentNovelRow?.title ?? (lang === 'zh' ? book.displayTitle : book.originalTitle),
    description: currentNovelRow?.description ?? representativeRow?.description ?? '',
    recommendationTextI18n: currentNovelRow?.recommendation_text_i18n ?? {},
    recommendationTextEdited: false,
    // 空 = 本次未上传新封面（保存时省略，不动既有 cover_url）；上传后存新相对 ref → 保存随选中语言行下发
    coverUrl: '',
    coverPreview: null,
    authorDisplay:
      currentNovelRow?.author_display ?? representativeRow?.original_author ?? book.author,
    ageRating: currentNovelRow?.age_rating ?? 'ALL_AGES',
    // **不拿展示作者兜底**：`original_author` 为 null 时预填 `book.author`，会让「未填」看起来像「已填成
    // 展示作者」，保存时再把这个从没人输入过的值写进 DB（下方 groupBody 的 dirty 守卫也就判不出未改动）。
    // 空串 = 未填，是诚实的初值；来源维护表单不能拿展示作者替代缺失原作者。
    // 书组级：服务端已按 group_id join 回填到每一行（dd#970），取当前行即取书组值。
    originalAuthor: currentNovelRow?.original_author ?? '',
    // 行级（dd#923 决议二）：取当前语言行自己的值
    sourceExternalId: currentNovelRow?.source_external_id ?? '',
    categoryId: currentNovelRow?.category_id ?? '',
    tags: currentNovelRow?.tags ?? [],
    rating: currentNovelRow?.rating ?? 0,
    // ADM-BOK-S017：基数恒取书组 root（后端 NovelResponse.read_count 已按 root 归属回显，译版行同值）
    // 千字单价取当前语言行（per-locale 章节默认千字单价权威，§59）；缺则 root；再缺 '30'（schema 默认）
    costPerKw:
      currentNovelRow?.cost_default_per_kw ?? representativeRow?.cost_default_per_kw ?? '30',
    lengthClass: currentNovelRow?.length_class ?? null,
    priceLevel: currentNovelRow?.price_level ?? null,
    isNovelPurchaseEnabled: currentNovelRow?.is_novel_purchase_enabled ?? false,
    limitedFreeEnabled: currentNovelRow?.limited_free_enabled ?? false,
    isRecommended: currentNovelRow?.is_recommended ?? false,
    freeChapterCount: String(currentNovelRow?.free_chapter_count ?? 5),
    // S024：有效标签（成员行是译制基准行的值，只读展示、保存时不下发）
    editionLabel: currentNovelRow?.edition_label ?? '',
  }));
  useEffect(() => {
    setForm((current) =>
      current.recommendationTextEdited
        ? current
        : {
            ...current,
            recommendationTextI18n: currentNovelRow?.recommendation_text_i18n ?? {},
          },
    );
  }, [currentNovelRow?.recommendation_text_i18n]);
  // S024：工厂译制集合成员的标签跟随译制基准语言版本，输入框只读（判定与服务端写入闸同源）
  const editionLabelFollowsBase = isTranslationFactoryProviderCode(
    currentNovelRow?.source_provider,
  );
  const editionLabelDraft = parseEditionLabelDraft(form.editionLabel);
  const formValid = parseFreeChapterCount(form.freeChapterCount) !== null && editionLabelDraft.ok;
  useEffect(() => {
    onFormValidityChange(formValid);
  }, [formValid, onFormValidityChange]);
  // 评分展示值编辑弹窗（ADM-BOK-S016）：本地 open state；弹窗内暂存值确认后写回 form.rating（随页头保存落库）
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  // 每次渲染把最新快照写进父 ref，供页头「保存」按钮读取（无需逐字段透传 onChange）。
  editFormRef.current = form;
  const setField = <K extends keyof BookEditForm>(k: K, v: BookEditForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    onDirty();
  };
  // 千字单价 / 章节价格只读展示（dev-docs 03-数据字典/20-内容 §59）：cost_default_per_kw = 该 locale 章节默认千字单价
  // **权威**（coin/千字）。无 admin 写端点 → 输入框只读（接真排除,见保存 mutation 注释）。
  // 平均章节字数 / 单章约由 total_words / total_chapters + 千字单价**真算**（非编造）。
  const perKwCoin =
    currentNovelRow?.cost_default_per_kw != null
      ? Number(currentNovelRow.cost_default_per_kw)
      : null;
  const totalWords = currentNovelRow?.total_words ?? 0;
  const totalChapters = currentNovelRow?.total_chapters ?? 0;
  const avgChapterWords = totalChapters > 0 ? Math.round(totalWords / totalChapters) : null;
  const perChapterCoin =
    avgChapterWords != null && perKwCoin != null
      ? Math.round((avgChapterWords / 1000) * perKwCoin)
      : null;
  // 从英文翻译：契约翻译能力仅覆盖 feedback 回复（DeepL），books 无翻译端点（NOT-in-scope §五）
  // → 按钮 disabled + title 标注（保留视觉但不留假成功 toast）
  // 从英文翻译（接真）：POST /books/translate 复用 DeepL（源语言自动检测），把原版文案翻成当前 locale → 填表（不直接落库，随保存写）
  const translateMut = useMutation({
    mutationFn: (text: string) => booksApi.translate({ texts: [text], target_locale: lang }),
    onError: (err) =>
      toast(err instanceof Error ? err.message : '翻译失败', { tone: 'neg', icon: 'IconAlert' }),
  });
  const doTranslate = async (source: string, onResult: (t: string) => void) => {
    if (!source.trim()) {
      toast('原版文案为空，无法翻译', { tone: 'warn' });
      return;
    }
    const r = await translateMut.mutateAsync(source).catch(() => null);
    const t = r?.translations[0];
    if (t) {
      onResult(t);
      toast('已翻译（请核对后保存）', { tone: 'pos', icon: 'IconCheck' });
    }
  };
  const TranslateBtn = ({
    source,
    onResult,
    title = '从英文翻译',
  }: {
    source: string;
    onResult: (t: string) => void;
    title?: string;
  }) =>
    fromEn ? (
      <button
        className="btn btn-sm btn-ghost"
        disabled={translateMut.isPending}
        title={`${title}（DeepL 自动检测源语言）`}
        onClick={() => doTranslate(source, onResult)}
        style={{ height: 28, padding: '0 8px', flexShrink: 0 }}
      >
        <Icons.IconSparkles size={11} style={{ color: 'var(--accent)' }} />
        <span style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
          {translateMut.isPending ? '翻译中…' : '从英文翻译'}
        </span>
      </button>
    ) : null;
  // 封面上传（ADM-BOK-S003）：选文件即传到共享内容桶拿相对 ref，存进 form.coverUrl（随保存写 per-locale cover_url）。
  // 即时本地预览（objectURL）——admin 无 C 端 origin 解析存量相对 ref，故只对刚上传文件预览；存量封面 C 端按 per-app serve。
  const coverMut = useMutation({
    mutationFn: (file: File) => booksApi.uploadCover(file),
    onSuccess: (res, file) => {
      // 重选时先 revoke 旧 objectURL，避免 blob URL 泄漏（adversarial）
      setForm((f) => {
        if (f.coverPreview) URL.revokeObjectURL(f.coverPreview);
        return { ...f, coverUrl: res.cover_url, coverPreview: URL.createObjectURL(file) };
      });
      onDirty();
      toast('封面已上传 · 保存后生效', { tone: 'pos', icon: 'IconCheck' });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '封面上传失败', {
        tone: 'neg',
        icon: 'IconAlert',
      }),
  });
  const uploadCoverFile = (file?: File | null) => {
    if (file && !coverMut.isPending) coverMut.mutate(file);
  };
  return (
    <div className="vstack" style={{ gap: 12 }}>
      <Section
        title="基础信息"
        sub={`只编辑这本书（${fmtLang(lang)}）。其他语言是独立的书，用页头跳转。${fromEn ? ' · 未维护 fallback 英文' : ''}`}
      >
        <FormRow label="书组编码" hint="系统分配 book_group_code · 7 位 · 创建后不可改">
          <div className="hstack" style={{ gap: 6, alignItems: 'center' }}>
            <CopyableCode
              value={book.bookGroupCode || currentNovelRow?.book_group_code}
              label="书组编码"
            />
            <span className="chip" style={{ fontSize: 9.5 }}>
              <Icons.IconLock size={8} />
              只读
            </span>
          </div>
        </FormRow>
        <FormRow label="书编码" hint="当前语言行 book_code · 10 位 · 必须同列展示语言 · 不可改">
          <div className="hstack" style={{ gap: 6, alignItems: 'center' }}>
            <CopyableCode
              value={
                currentNovelRow?.book_code ||
                book.languageCodes.find((row) => row.locale === lang)?.bookCode
              }
              label="书编码"
            />
            <LangChip code={lang} style={{ fontSize: 10 }} />
            <span className="chip" style={{ fontSize: 9.5 }}>
              <Icons.IconLock size={8} />
              只读
            </span>
          </div>
        </FormRow>
        <FormRow label="书名">
          <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
            <input
              className="input"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              style={{ flex: 1, maxWidth: 400 }}
            />
            <TranslateBtn
              title="基于原版书名翻译"
              source={representativeRow?.title ?? book.originalTitle}
              onResult={(t) => setField('title', t)}
            />
          </div>
        </FormRow>
        <FormRow label="作者">
          <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
            <input
              className="input"
              value={form.authorDisplay}
              onChange={(e) => setField('authorDisplay', e.target.value)}
              style={{ flex: 1, maxWidth: 280 }}
            />
            <TranslateBtn
              title="基于原版作者名翻译"
              source={
                representativeRow?.original_author ??
                representativeRow?.author_display ??
                book.author
              }
              onResult={(t) => setField('authorDisplay', t)}
            />
          </div>
        </FormRow>
        <FormRow
          label="改写版本"
          hint={
            editionLabelFollowsBase
              ? '交工厂译制的语言版本不单独标注'
              : '区分同一作品的不同改写本（如 A / B）· 仅运营后台可见'
          }
        >
          <div className="hstack" style={{ gap: 8, alignItems: 'center' }}>
            <input
              aria-label="改写版本"
              className="input"
              value={form.editionLabel}
              onChange={(e) => setField('editionLabel', e.target.value)}
              placeholder="留空 = 原版"
              readOnly={editionLabelFollowsBase}
              title={editionLabelFollowsBase ? '跟随译制基准语言版本' : undefined}
              style={{ flex: 1, maxWidth: 200 }}
            />
            {editionLabelFollowsBase && (
              <span className="chip" style={{ fontSize: 9.5 }}>
                <Icons.IconLock size={8} />
                跟随译制基准语言版本
              </span>
            )}
            {!editionLabelDraft.ok && (
              <span style={{ fontSize: 11, color: 'var(--neg)' }}>
                最多 {EDITION_LABEL_MAX_LENGTH} 个字符
              </span>
            )}
          </div>
        </FormRow>
        <FormRow label="简介">
          <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
            <textarea
              className="input"
              rows={3}
              style={{ flex: 1, height: 'auto' }}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
            />
            <TranslateBtn
              title="基于原版简介翻译"
              source={representativeRow?.description ?? ''}
              onResult={(t) => setField('description', t)}
            />
          </div>
        </FormRow>
        <MultiLangFormRows
          label="推荐语"
          value={form.recommendationTextI18n}
          onChange={(value) => {
            setForm((current) => ({
              ...current,
              recommendationTextI18n: value,
              recommendationTextEdited: true,
            }));
            onDirty();
          }}
          multiline
          requiredLangs={[]}
          pinnedLangs={['zh', 'en']}
          availableLangs={recommendationLanguageCodes}
          onTranslatingChange={onRecommendationTranslationPendingChange}
          otherLangsHint={`当前仅保存到${langName(lang) || lang.toUpperCase()}（${lang.toUpperCase()}）书籍版本 · 其他版本请先在上方切换后维护`}
          placeholderMap={{
            zh: '如：编辑精选 · 高口碑佳作',
            en: "e.g. Editor's pick · Highly rated",
          }}
        />
        <FormRow label="封面">
          <div
            className="hstack"
            style={{ gap: 12, alignItems: 'flex-start' }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              uploadCoverFile(e.dataTransfer.files?.[0]);
            }}
            onPaste={(e) => {
              const image = [...e.clipboardData.items].find((item) =>
                item.type.startsWith('image/'),
              );
              uploadCoverFile(image?.getAsFile());
            }}
          >
            <BookCover
              src={form.coverPreview ?? currentNovelRow?.cover_url ?? book.coverUrl}
              alt={`${form.title || book.displayTitle} 封面`}
              hue={book.coverHue}
              width={96}
              height={132}
              radius={6}
              style={{ border: '1px solid var(--border)' }}
            />
            <div className="vstack" style={{ gap: 6, alignItems: 'flex-start' }}>
              <label
                className="btn btn-sm"
                style={{ cursor: coverMut.isPending ? 'wait' : 'pointer' }}
                title="上传封面图片（运营 root/admin/operator）"
              >
                <Icons.IconUpload size={12} />
                <span style={{ fontSize: 11 }}>
                  {coverMut.isPending ? '上传中…' : form.coverUrl ? '重新选择' : '上传新封面'}
                </span>
                <input
                  type="file"
                  accept="image/webp,image/png,image/jpeg"
                  hidden
                  disabled={coverMut.isPending}
                  onChange={(e) => {
                    uploadCoverFile(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
              <span style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                支持拖拽 / 粘贴 · JPG · PNG · WebP · 640×880 推荐 · ≤2MB
              </span>
              {form.coverUrl && (
                <span className="chip chip-pos" style={{ fontSize: 10 }}>
                  已上传 · 保存后生效
                </span>
              )}
            </div>
          </div>
        </FormRow>
        <FormRow label="推荐标记" hint="仅当前语言版本 · 优先参与算法选书，不影响曝光资格">
          <label className="hstack" style={{ gap: 8, fontSize: 12 }}>
            <input
              type="checkbox"
              aria-label="推荐标记"
              disabled={!canWriteRecommendation}
              checked={form.isRecommended}
              onChange={(event) => setField('isRecommended', event.target.checked)}
            />
            {form.isRecommended ? '已推荐' : '未推荐'}
          </label>
        </FormRow>
        <FormRow label="本书免费" hint="ADM-BOK-S018 · 开启后该书全本免费可读，不改章节免费态">
          <label className="hstack" style={{ gap: 8, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={form.limitedFreeEnabled}
              onChange={(e) => setField('limitedFreeEnabled', e.target.checked)}
            />
            {form.limitedFreeEnabled ? '已开启' : '关闭'}
          </label>
        </FormRow>
        <FormRow label="篇幅" hint="ADM-BOK-S019 · 定性分类，与章节数/字数无关。未标注不可买断">
          <Select
            className="input"
            style={{ maxWidth: 180 }}
            value={form.lengthClass ?? ''}
            onChange={(e) => {
              const next = e.target.value;
              if (next === '') {
                // 与后端级联一致（A6 §16.2）：取消标注 → 同时清价格档、关买断。
                // UI 不同步的话，保存后界面仍显示「已启用」而库里已是 false
                setField('lengthClass', null);
                setField('priceLevel', null);
                setField('isNovelPurchaseEnabled', false);
                return;
              }
              const length = next as NonNullable<NovelResponse['length_class']>;
              setField('lengthClass', length);
              if (!form.priceLevel) setField('priceLevel', initialPriceLevelForLengthClass(length));
            }}
          >
            <option value="">未标注</option>
            <option value="SHORT">短篇 SHORT</option>
            <option value="MEDIUM">中篇 MEDIUM</option>
            <option value="MEDIUM_LONG">中长篇 MEDIUM_LONG</option>
            <option value="LONG">长篇 LONG</option>
            <option value="EPIC">超长篇 EPIC</option>
          </Select>
        </FormRow>
        <FormRow label="价格档" hint="买断定价维度。改篇幅不回写本档">
          <Select
            className="input"
            style={{ maxWidth: 180 }}
            value={form.priceLevel ?? ''}
            disabled={!form.lengthClass}
            onChange={(e) => {
              if (e.target.value === '') {
                setField('priceLevel', null);
                // 同步关买断：否则勾选框会停在「已禁用但仍勾着」，保存时 diff 仍下发
                // is_novel_purchase_enabled=true 而不带 price_level，后端按**库里残留的**旧档位
                // 启用 —— 运营看着「未标注」点了保存，书却按旧价开卖了
                setField('isNovelPurchaseEnabled', false);
                return;
              }
              setField('priceLevel', e.target.value as NonNullable<NovelResponse['price_level']>);
            }}
          >
            <option value="">未标注</option>
            <option value="L1">L1</option>
            <option value="L2">L2</option>
            <option value="L3">L3</option>
            <option value="L4">L4</option>
            <option value="L5">L5</option>
          </Select>
        </FormRow>
        <FormRow
          label="书籍买断"
          hint="ADM-BOK-S020 · 每书显式启用；需先标价格档。与 App 级总开关正交，标注篇幅/调档位都不自动开。审核放行成立的 C 端请求方不受本开关限制（SRV-READ-028）"
        >
          <label className="hstack" style={{ gap: 8, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={form.isNovelPurchaseEnabled}
              // 前端只做 UX 前置：无价格档时开不了（后端以合并本次提交后的行为准，是权威）
              disabled={!form.priceLevel}
              onChange={(e) => {
                if (!e.target.checked) {
                  setField('isNovelPurchaseEnabled', false);
                  return;
                }
                // 启用即对外可售，价格档要当面确认一次再落表单
                onRequestConfirm({
                  title: `启用《${book.displayTitle}》当前语言版本的买断？`,
                  message: `按价格档 ${form.priceLevel} 定价。启用后 C 端详情下发该档位、可创建买断意图；此后调档只影响新买断，已成交的不受影响。关闭也不是全量收口——审核放行成立的 C 端请求方不看本开关（SRV-READ-028）。`,
                  tone: 'default',
                  icon: 'IconCoin',
                  confirmLabel: '启用买断',
                  onConfirm: () => setField('isNovelPurchaseEnabled', true),
                });
              }}
            />
            {form.isNovelPurchaseEnabled ? '已启用' : '未启用'}
            {!form.priceLevel && (
              <span style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>未标价格档，无法启用</span>
            )}
          </label>
        </FormRow>
        <FormRow label="年龄限制">
          <Select
            className="input"
            style={{ maxWidth: 180 }}
            value={form.ageRating}
            onChange={(e) => setField('ageRating', e.target.value as NovelResponse['age_rating'])}
          >
            {Object.entries(AGE_RATING_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormRow>
        {/* ADM-BOK-S016 评分展示值（接真）：运营展示评分（非真实用户评分），0–5 一位小数 → 写当前 per-locale 行 rating。
            展示态对齐高保真稿（mono 数字 + 5★ + 编辑按钮 + chip）；「编辑」开本地弹窗暂存值，确认写回 form.rating。 */}
        <FormRow label="评分展示值" hint="ADM-BOK-S016 · 创建时默认随机 4.6–4.9 · 运营可手动修改">
          <div className="hstack" style={{ gap: 8, alignItems: 'center' }}>
            <span className="mono" style={{ fontSize: 16, fontWeight: 600 }}>
              {form.rating}
            </span>
            <span className="hstack" style={{ gap: 1 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Icons.IconStar
                  key={i}
                  size={11}
                  style={{ color: i < Math.round(form.rating) ? 'var(--warn)' : 'var(--fg-faint)' }}
                />
              ))}
            </span>
            <button className="btn btn-sm" onClick={() => setRatingDialogOpen(true)}>
              <Icons.IconEdit size={10} />
              编辑
            </button>
            <span className="chip" style={{ fontSize: 9.5, color: 'var(--fg-faint)' }}>
              运营展示值 · 非真实用户评分
            </span>
          </div>
        </FormRow>
        {/* ADM-BOK-S017 数据基数：阅读 / 收藏展示基数 → 写书组 root 行 read_count / favorite_count；
            真实值读 real_read_count / real_favorite_count，客户端展示值取后端派生的 display_*
            （三元组契约 A6 §15.2）。旧 admin-api 未返这些字段时显示「—」而非编 0。 */}
        <FormRow
          label="数据基数"
          hint="阅读 / 收藏展示基数 · 客户端展示值 = 基数 + 真实值 · 仅影响客户端展示，不计入运营统计与报表"
        >
          <div className="vstack" style={{ gap: 8 }}>
            <BaseCountRow
              icon="IconEye"
              zh="阅读"
              en="Readers"
              base={baseCounts.read}
              real={baseCounts.realRead}
              display={baseCounts.displayRead}
              onChange={(v) => {
                onBaseCountChange('read', v);
                onDirty();
              }}
              disabled={!baseCounts.supported}
            />
            <BaseCountRow
              icon="IconHeart"
              zh="收藏"
              en="Saves"
              base={baseCounts.favorite}
              real={baseCounts.realFavorite}
              display={baseCounts.displayFavorite}
              onChange={(v) => {
                onBaseCountChange('favorite', v);
                onDirty();
              }}
              disabled={!baseCounts.supported}
            />
            {!baseCounts.supported && (
              <span style={{ fontSize: 11, color: 'var(--warn)' }}>
                基数编辑暂不可用：待 C 端展示合成与 real_* / display_* 详情契约上线后自动恢复。
              </span>
            )}
          </div>
        </FormRow>
        {/* 分类（接真）：真实分类 UUID 下拉（catList）→ 写当前 per-locale 行 category_id */}
        <FormRow label="分类">
          <Select
            className="input"
            style={{ maxWidth: 220 }}
            value={form.categoryId}
            onChange={(e) => setField('categoryId', e.target.value)}
          >
            <option value="">（未分类）</option>
            {catList.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </FormRow>
        {/* 标签（接真）：真实标签 UUID 多选（tagList）→ 写当前 per-locale 行 tags[]；chip 删 + 下拉加 */}
        <FormRow label="标签">
          <div className="hstack" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {form.tags.map((tid) => {
              const t = tagList.find((x) => x.id === tid);
              return (
                <span key={tid} className="chip" style={{ paddingRight: 4 }}>
                  {t?.name ?? tid}
                  <button
                    onClick={() =>
                      setField(
                        'tags',
                        form.tags.filter((x) => x !== tid),
                      )
                    }
                    title="移除"
                    style={{
                      border: 0,
                      background: 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                      padding: 2,
                      marginLeft: 2,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <Icons.IconX size={10} />
                  </button>
                </span>
              );
            })}
            {form.tags.length === 0 && (
              <span style={{ fontSize: 12, color: 'var(--fg-faint)' }}>—</span>
            )}
            <Select
              className="input"
              style={{ width: 150, height: 28 }}
              value=""
              onChange={(e) => {
                if (e.target.value) setField('tags', [...form.tags, e.target.value]);
              }}
            >
              <option value="">+ 添加标签…</option>
              {tagList
                .filter((t) => !form.tags.includes(t.id))
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </Select>
          </div>
        </FormRow>
      </Section>

      {/* 非 root 的浏览面不展示来源；operator+ 显式进入维护面后才能查看并编辑当前行。 */}
      {!canViewSource &&
        canMaintainSource &&
        !sourceMaintenanceOpen &&
        currentNovelRow?.source === 'IMPORTED' && (
          <div>
            <button className="btn btn-ghost btn-sm" onClick={() => setSourceMaintenanceOpen(true)}>
              维护来源信息
            </button>
          </div>
        )}
      {(canViewSource || (canMaintainSource && sourceMaintenanceOpen)) &&
        currentNovelRow?.source === 'IMPORTED' && (
          <Section
            title={canViewSource ? '外部采集信息' : '来源信息维护'}
            sub="走外部采集的书籍需维护源信息以支持自动化更新"
          >
            <FormRow label="内容来源">
              <span className="chip mono">
                <Icons.IconBranch size={10} />
                {sourceProvider}
              </span>
            </FormRow>
            <FormRow label="原作者">
              <input
                className="input"
                value={form.originalAuthor}
                onChange={(e) => setField('originalAuthor', e.target.value)}
                style={{ maxWidth: 280 }}
              />
            </FormRow>
            <FormRow label="原语言">
              <span className="chip mono">{originalLanguage}</span>
            </FormRow>
            <FormRow
              label="来源外部书籍 ID"
              hint={
                <span className="hstack" style={{ gap: 6, color: 'var(--fg-muted)' }}>
                  <span className="mono">source_external_id</span>
                  <span>· 与内部翻译指针 source_novel_id 区分</span>
                </span>
              }
            >
              <input
                className="input mono"
                value={form.sourceExternalId}
                onChange={(e) => setField('sourceExternalId', e.target.value)}
                style={{ maxWidth: 280 }}
              />
            </FormRow>
          </Section>
        )}

      <Section
        title="价格 & 免费章节"
        sub="章节默认千字单价（per-locale 权威）· 章节价格按字数 × 千字单价自动计算"
      >
        <FormRow
          label="千字单价 (本书)"
          hint={
            <span>
              该 locale 章节默认千字单价权威 ·{' '}
              <span className="mono">novels.cost_default_per_kw</span>
              （coin/千字，章节价格按此计算；无单独 per-novel 覆盖列）
            </span>
          }
        >
          <div className="hstack" style={{ gap: 8 }}>
            {/* 千字单价接真：UpdateNovelRequest.cost_default_per_kw（写既有 C 端列 novels.cost_default_per_kw，
                carve-out 准先实现）→ 编辑后随「保存」入 saveMutation。非负整数 COIN 串。 */}
            <input
              className="input mono"
              value={form.costPerKw}
              onChange={(e) => setField('costPerKw', e.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
              style={{ width: 90, textAlign: 'right' }}
            />
            <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>COIN / 千字</span>
            <span className="chip chip-accent" style={{ marginLeft: 4, fontSize: 10 }}>
              per-locale
            </span>
            {!/^(0|[1-9][0-9]*)$/.test(form.costPerKw) && (
              <span style={{ fontSize: 11, color: 'var(--neg)' }}>须为非负整数</span>
            )}
          </div>
        </FormRow>
        <FormRow
          label="免费章节数"
          hint="前 N 章免费 · 0–1000 · 调整将重算该语言版本全部章节的免费标记，并覆盖单章手工设置"
        >
          <div className="hstack" style={{ gap: 8 }}>
            <input
              className="input mono"
              aria-label="书籍免费章节数"
              value={form.freeChapterCount}
              onChange={(e) => setField('freeChapterCount', e.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
              style={{ maxWidth: 100, textAlign: 'right' }}
            />
            {parseFreeChapterCount(form.freeChapterCount) === null && (
              <span style={{ fontSize: 11, color: 'var(--neg)' }}>须为 0–1000 整数</span>
            )}
          </div>
        </FormRow>
        <FormRow label="章节价格" hint="按字数与本书千字单价自动计算，无需手动维护">
          <div className="hstack" style={{ gap: 6, fontSize: 11.5, color: 'var(--fg-muted)' }}>
            <Icons.IconInfo size={12} style={{ color: 'var(--info)' }} />
            {/* 接真：千字单价=cost_default_per_kw；平均章节=total_words/total_chapters；单章约=均字数/1000×千字单价 */}
            本书千字单价{' '}
            <span className="mono strong">
              {perKwCoin != null ? `${fmtNum(perKwCoin)} COIN` : '—'}
            </span>{' '}
            · 平均章节{' '}
            <span className="mono strong">
              {avgChapterWords != null ? `${fmtNum(avgChapterWords)} 字` : '—'}
            </span>{' '}
            · 单章约{' '}
            <span className="mono strong">
              {perChapterCoin != null ? `${fmtNum(perChapterCoin)} COIN` : '—'}
            </span>
          </div>
        </FormRow>
      </Section>

      {ratingDialogOpen && (
        <RatingEditDialog
          book={book}
          value={form.rating}
          onConfirm={(next) => {
            setField('rating', next);
            setRatingDialogOpen(false);
          }}
          onClose={() => setRatingDialogOpen(false)}
        />
      )}
    </div>
  );
}

// =========================================================
// ADM-BOK-S016 评分展示值编辑弹窗
// 本地 useState 暂存值（确认→ onConfirm 写回 form.rating，随页头「保存」走 UpdateNovelRequest 落库，不新增端点）；
// 取值域 0–5 一位小数（复用 clamp Math.min(5,Math.max(0,n))；不用设计稿断言的 4.6–4.9，已记 dev-docs 待办）。
// =========================================================
function RatingEditDialog({
  book,
  value,
  onConfirm,
  onClose,
}: {
  book: BookRow;
  value: number;
  onConfirm: (next: number) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(value);
  const rounded = Math.round(draft);
  return (
    <Dialog
      width={500}
      title={`评分展示值 · ${book.displayTitle}`}
      subtitle="运营展示值，非真实用户评分"
      icon="IconStar"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button className="btn btn-accent" onClick={() => onConfirm(draft)}>
            <Icons.IconCheck size={11} />
            确认
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <div className="hstack" style={{ gap: 14, alignItems: 'center' }}>
          <span className="mono" style={{ fontSize: 28, fontWeight: 600 }}>
            {draft}
          </span>
          <span className="hstack" style={{ gap: 2 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Icons.IconStar
                key={i}
                size={16}
                style={{ color: i < rounded ? 'var(--warn)' : 'var(--fg-faint)' }}
              />
            ))}
          </span>
          <div style={{ flex: 1 }} />
          <input
            className="input mono"
            type="number"
            min={0}
            max={5}
            step={0.1}
            value={draft}
            onChange={(e) => {
              const n = Number(e.target.value);
              setDraft(Number.isFinite(n) ? Math.min(5, Math.max(0, n)) : 0);
            }}
            style={{ width: 100, textAlign: 'right' }}
          />
        </div>
        <div
          className="card-flat"
          style={{
            marginTop: 14,
            padding: '8px 12px',
            background: 'var(--info-soft)',
            borderColor: 'transparent',
          }}
        >
          <span style={{ fontSize: 11.5, color: 'var(--fg)' }}>
            <Icons.IconInfo
              size={11}
              style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--info)' }}
            />
            运营展示值，非真实用户评分，仅用于书城卡片/详情展示。
          </span>
        </div>
      </div>
    </Dialog>
  );
}

// =========================================================
// 章节 tab
// =========================================================
function BookChapters({
  book,
  lang,
  novelId,
  currentNovelRow,
  publishTimeZone,
}: {
  book: BookRow;
  lang: string;
  novelId: string | null;
  // 当前编辑 lang 的 per-locale 行：供章节编辑器取免费章节数（价格由服务端按 §3.0 派生，前端不算）
  currentNovelRow: NovelResponse | null;
  publishTimeZone: string | null;
}) {
  // 免费章节数（前 N 章免费，per-locale free_chapter_count）：行未就绪时 null → 编辑器显「—」，
  // 不兜底 0（会误示「无免费章节」，且与 schema 默认 5 不符；对抗审查 F5）
  const freeChapters = currentNovelRow?.free_chapter_count ?? null;
  // 按当前选中语言的 per-locale novel_id 取真实章节（章节按精确 chapters.novel_id 过滤，非 group root）：
  // 切到翻译语言时拿该语言版本自己的章节，不再误显/误发 root novel 的章节（adversarial F1）。
  // novelId 未就绪（详情冷加载 / 该 locale 无版本）时 hook enabled=false 不发请求、返稳定 fallback。
  const { data: chapters } = useChapters(novelId);
  // 通用确认弹窗本地 open state
  const [confirm, setConfirm] = useState<ConfirmReq | null>(null);
  // 全屏章节编辑器（标题/价格/免费 + 正文 均接真）：点章节行/编辑按钮/菜单「编辑章节」打开
  const [editChapter, setEditChapter] = useState<ChapterRow | null>(null);
  // 新建章节态（chapter=null 走代理创建端点）：与 editChapter 分开（null 已表「关闭」）
  const [newChapterOpen, setNewChapterOpen] = useState(false);
  // 发布计划是书籍语言版本级配置，不再挂在单章上。
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const canWrite = useCanWriteBooks();
  const queryClient = useQueryClient();
  const scheduleQuery = useQuery({
    queryKey: ['publish-schedule', novelId],
    queryFn: () => chaptersApi.getPublishSchedule(novelId ?? ''),
    enabled: novelId != null,
  });
  const queueQuery = useQuery({
    queryKey: ['publish-queue', novelId],
    queryFn: () => chaptersApi.getPublishQueue(novelId ?? ''),
    enabled: novelId != null,
    // queue 会由 cron worker 跨进程推进；PENDING / CLAIMED 时轮询，进入终态后自动停止。
    refetchInterval: (query) => publishQueueRefetchInterval(query.state.data?.status),
  });
  const previousQueueStatus = useRef<{
    novelId: string | null;
    status: PublishQueueStatus | null;
  }>({ novelId: null, status: null });
  const queueStatus = queueQuery.data?.status ?? null;
  useEffect(() => {
    const previous = previousQueueStatus.current;
    previousQueueStatus.current = { novelId, status: queueStatus };
    const isTerminal =
      queueStatus === 'PUBLISHED' || queueStatus === 'FAILED' || queueStatus === 'CANCELLED';
    const enteredTerminal = isTerminal && previous.status !== queueStatus;
    if (!novelId || previous.novelId !== novelId || !enteredTerminal) return;

    // worker 在另一个进程完成发布后，队列自身虽已刷新，章节/计划/书籍聚合仍可能停在旧缓存。
    // 同一语言版本首次观测到终态就刷新一次：既覆盖处理中→终态，也覆盖极快任务 null→终态。
    // 组件初挂时 previous.novelId 不同，仍不会为缓存里的历史终态触发无意义请求。
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: ['chapters', novelId] }),
      queryClient.invalidateQueries({ queryKey: ['publish-schedule', novelId] }),
      queryClient.invalidateQueries({ queryKey: ['books'] }),
      queryClient.invalidateQueries({ queryKey: ['book-detail', book.groupId] }),
    ]);
  }, [book.groupId, novelId, queryClient, queueStatus]);
  const initialPublishMode: 'immediate' | 'scheduled' | 'blocked' = !scheduleQuery.isSuccess
    ? 'blocked'
    : scheduleQuery.data.enabled
      ? scheduleQuery.data.scheduled_publish_at
        ? 'scheduled'
        : 'blocked'
      : 'immediate';
  const initialPublishBlockedReason = scheduleQuery.isError
    ? '发布计划加载失败，请重试后再发布'
    : !scheduleQuery.isSuccess
      ? '发布计划加载中，请稍候'
      : '定时发布已开启，请先配置下次计划时刻';

  // 首发状态机：计划 OFF 时同步发布；计划 ON 时写书级队列。响应是可辨识联合类型。
  const publishMutation = useMutation({
    mutationFn: (chapterId: string) => chaptersApi.publish(chapterId),
    onSuccess: async (dto) => {
      const message =
        'published_chapter_ids' in dto
          ? `${dto.published_chapter_ids.length} 章已同步发布`
          : '已加入发布队列';
      toast(message, { tone: 'pos', icon: 'IconCheck' });
      // await 全部 invalidate：刷新完成前 isPending 保持真，按钮不提前复活（adversarial F4）
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['chapters', novelId ?? null] }),
        queryClient.invalidateQueries({ queryKey: ['publish-schedule', novelId] }),
        queryClient.invalidateQueries({ queryKey: ['publish-queue', novelId] }),
        queryClient.invalidateQueries({ queryKey: ['books'] }),
        queryClient.invalidateQueries({ queryKey: ['book-logs', novelId] }),
      ]);
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '发布失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });
  const cancelQueueMutation = useMutation({
    mutationFn: () => chaptersApi.cancelPublishQueue(novelId ?? ''),
    onSuccess: async () => {
      toast('发布队列已取消', { tone: 'pos', icon: 'IconCheck' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['publish-queue', novelId] }),
        queryClient.invalidateQueries({ queryKey: ['book-logs', novelId] }),
      ]);
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '取消发布队列失败', {
        tone: 'neg',
        icon: 'IconAlert',
      }),
  });
  // 该语言版本不存在（detail 该 locale 无行）→ 无 novel_id 可挂章节：诚实空态，不误显 root 章节。
  if (!novelId) {
    return (
      <div className="card" style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
          当前语言（{langName(lang) || lang.toUpperCase()}）暂无该书的语言版本 ·
          章节按语言版本独立维护
        </div>
      </div>
    );
  }

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <Section
        title="章节清单"
        sub={`已发布 ${chapters.filter((c) => c.status === '已发布').length} · 草稿 ${chapters.filter((c) => c.status === '草稿').length} · 待重发布 ${chapters.filter((c) => c.hasPendingDraft).length}`}
        action={
          <div className="hstack" style={{ gap: 6 }}>
            <button
              className="btn btn-sm"
              disabled={!canWrite}
              title={canWrite ? '配置书籍级首次发布计划' : FORBIDDEN_ROLE_TITLE}
              onClick={() => setScheduleOpen(true)}
            >
              <Icons.IconClock size={11} />
              发布计划
            </button>
            {/* 章节批量导入：无契约端点（NOT-in-scope §五）→ disabled + title 标注 */}
            <button
              className="btn btn-sm"
              disabled
              title={NOT_IN_SCOPE_TITLE}
              style={{ opacity: 0.55, cursor: 'not-allowed' }}
            >
              <Icons.IconUpload size={11} />
              批量导入
            </button>
            {/* 新建章节：打开 ChapterEditor 新建态 → 代理创建端点（server 生成 id/算 sha256/word_count + PUT R2）*/}
            <button
              className="btn btn-sm btn-accent"
              disabled={!canWrite}
              title={
                canWrite ? undefined : '权限不足 · 该操作受角色限制（后端 requireRole 为权威）'
              }
              style={canWrite ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
              onClick={() => setNewChapterOpen(true)}
            >
              <Icons.IconPlus size={11} />
              新建章节
            </button>
          </div>
        }
      >
        <div
          className="card-flat"
          style={{
            margin: '6px 0 10px',
            padding: '9px 11px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'var(--bg-subtle)',
          }}
        >
          <span
            className={`chip ${
              scheduleQuery.isError
                ? 'chip-neg'
                : initialPublishMode === 'scheduled'
                  ? 'chip-warn'
                  : 'chip-info'
            }`}
            style={{ fontSize: 10 }}
          >
            <Icons.IconClock size={9} />
            {scheduleQuery.isError
              ? '计划不可用'
              : !scheduleQuery.isSuccess
                ? '计划读取中'
                : scheduleQuery.data.enabled
                  ? '定时发布'
                  : '立即发布'}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
            {scheduleQuery.isError
              ? '发布计划加载失败 · 请重试'
              : !scheduleQuery.isSuccess
                ? '发布计划加载中'
                : scheduleQuery.data.enabled && scheduleQuery.data.scheduled_publish_at
                  ? `计划时刻 ${fmtDateTimeInTimeZone(
                      scheduleQuery.data.scheduled_publish_at,
                      publishTimeZone,
                    )}`
                  : scheduleQuery.data.enabled
                    ? '定时已开启 · 请先配置下次计划时刻'
                    : '首次发布请求同步落地'}
          </span>
          {queueQuery.data && (
            <>
              <div className="vdivider" style={{ height: 16 }} />
              <span className="chip chip-warn" style={{ fontSize: 10 }}>
                发布队列 · {queueQuery.data.status}
              </span>
              <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                目标章 {queueQuery.data.target_chapter_id}
              </span>
              {queueQuery.data.status === 'PENDING' && (
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={!canWrite || cancelQueueMutation.isPending}
                  onClick={() => cancelQueueMutation.mutate()}
                >
                  取消队列
                </button>
              )}
            </>
          )}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
            待重发布草稿不进入定时队列
          </span>
        </div>
        <Table className="tbl" style={{ marginTop: 6 }}>
          <thead>
            <tr>
              <th style={{ width: 60 }}>#</th>
              <th>标题</th>
              <th className="num">字数</th>
              <th className="num">价格 (COIN)</th>
              <th>状态</th>
              <th>发布时间</th>
              <th style={{ width: 140 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {chapters.map((c) => {
              // ADM-BOK-S008 · 计算前置未发布章节
              const priorUnpub = chapters.filter((x) => x.no < c.no && x.status !== '已发布');
              const publishChapter = () => {
                if (initialPublishMode === 'blocked') return;
                const total = priorUnpub.length + 1;
                const firstUnpub = priorUnpub[0];
                const scheduled = initialPublishMode === 'scheduled';
                setConfirm({
                  title:
                    scheduled && priorUnpub.length > 0 && firstUnpub
                      ? `将第 ${firstUnpub.no} ~ ${c.no} 章加入发布队列？`
                      : scheduled
                        ? `将第 ${c.no} 章加入发布队列？`
                        : priorUnpub.length > 0 && firstUnpub
                          ? `一并发布第 ${firstUnpub.no} ~ ${c.no} 章？`
                          : `立即发布第 ${c.no} 章「${c.title}」？`,
                  message:
                    scheduled && priorUnpub.length > 0
                      ? `《${book.displayTitle}》第 ${c.no} 章之前还有 ${priorUnpub.length} 个未发布章节（${priorUnpub.map((x) => `第 ${x.no} 章`).join(' · ')}）。\n\n提交后会把目标推进至第 ${c.no} 章，共 ${total} 章进入书籍级发布队列，由调度在计划时刻首发；不会立即对客户端可见。`
                      : scheduled
                        ? '提交后将进入书籍级发布队列，由调度在计划时刻首发；不会立即对客户端可见。'
                        : priorUnpub.length > 0
                          ? `《${book.displayTitle}》第 ${c.no} 章之前还有 ${priorUnpub.length} 个未发布章节（${priorUnpub.map((x) => `第 ${x.no} 章`).join(' · ')}）。\n\n根据 ADM-BOK-S008，发布时会一并发布全部前置未发布章节，共 ${total} 章同步上线。`
                          : '发布后客户端立即可见 · 已订阅自动扣款的用户会自动解锁。',
                  tone: priorUnpub.length > 0 ? 'warn' : 'default',
                  icon: 'IconPaperPlane',
                  confirmLabel: scheduled
                    ? priorUnpub.length > 0
                      ? `推进队列至第 ${c.no} 章`
                      : '加入队列'
                    : priorUnpub.length > 0
                      ? `一并发布 ${total} 章`
                      : '立即发布',
                  // 真实接线：POST /books/chapters/:id/publish（一并发布前置由服务端处理）
                  onConfirm: () => publishMutation.mutate(c.id),
                });
              };
              // 点击章节行 → 打开章节编辑弹窗（标题/价格/免费 + 正文，均接真）。
              return (
                <tr
                  key={c.id}
                  onClick={() => setEditChapter(c)}
                  style={{ cursor: 'pointer' }}
                  title="编辑章节（标题/价格/免费/正文）"
                >
                  <td className="mono muted">{String(c.no).padStart(3, '0')}</td>
                  <td>
                    {c.title}
                    {c.hasPendingDraft && (
                      <span
                        className="chip chip-warn"
                        style={{ fontSize: 9.5, marginLeft: 6, padding: '0 5px' }}
                      >
                        待重发布
                      </span>
                    )}
                    {priorUnpub.length > 0 && c.status !== '已发布' && (
                      <span
                        className="chip chip-warn"
                        style={{ fontSize: 9.5, marginLeft: 6, padding: '0 5px' }}
                        title="发布时将一并发布前置未发布章节"
                      >
                        <Icons.IconAlert size={9} />
                        前置 {priorUnpub.length} 章未发
                      </span>
                    )}
                  </td>
                  <td className="num">{c.words.toLocaleString()}</td>
                  <td className="num">
                    {c.isFree ? <span style={{ color: 'var(--fg-faint)' }}>免费</span> : c.price}
                  </td>
                  <td>
                    <span
                      className={`chip ${c.status === '已发布' ? 'chip-pos' : 'chip-info'}`}
                      style={{ fontSize: 10 }}
                    >
                      {c.status === '已发布' && <Icons.IconCheck size={9} />}
                      {c.status === '草稿' && <Icons.IconEdit size={9} />}
                      {c.status}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    {fmtDateTime(c.publishedAt) || '—'}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="hstack" style={{ gap: 4 }}>
                      {/* 编辑章节（标题/价格/免费 + 正文）：PATCH /books/chapters/:id + PUT .../content 接真
                          → root/admin/operator 可写，viewer 禁用。 */}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setEditChapter(c)}
                        disabled={!canWrite}
                        title={canWrite ? '编辑章节（标题/价格/免费/正文）' : FORBIDDEN_ROLE_TITLE}
                        style={canWrite ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                      >
                        <Icons.IconEdit size={11} />
                      </button>
                      {/* 未发布章节首发：计划 OFF 同步发布，计划 ON 进入书级队列。 */}
                      {c.status === '草稿' && (
                        <button
                          className="btn btn-sm"
                          style={{
                            fontSize: 10,
                            height: 22,
                            padding: '0 8px',
                            ...(canWrite && initialPublishMode !== 'blocked'
                              ? {}
                              : { opacity: 0.55, cursor: 'not-allowed' }),
                          }}
                          disabled={
                            !canWrite ||
                            publishMutation.isPending ||
                            initialPublishMode === 'blocked'
                          }
                          title={
                            !canWrite
                              ? FORBIDDEN_ROLE_TITLE
                              : initialPublishMode === 'blocked'
                                ? initialPublishBlockedReason
                                : undefined
                          }
                          onClick={publishChapter}
                        >
                          发布
                        </button>
                      )}
                      <ContextMenu
                        width={180}
                        items={[
                          {
                            // 编辑章节（标题/价格/免费 + 正文）接真
                            label: '编辑章节',
                            icon: 'IconEdit',
                            disabled: !canWrite,
                            title: canWrite ? undefined : FORBIDDEN_ROLE_TITLE,
                            onClick: canWrite ? () => setEditChapter(c) : undefined,
                          },
                          ...(c.status === '已发布'
                            ? [
                                {
                                  // 下架本章：server 无章节级下架/删除端点（章节状态机无独立下架，NOT-in-scope §五）→ 禁用
                                  label: '下架本章',
                                  icon: 'IconBan' as const,
                                  disabled: true,
                                  title: NOT_IN_SCOPE_TITLE,
                                },
                              ]
                            : [
                                {
                                  label:
                                    initialPublishMode === 'scheduled'
                                      ? '加入发布队列'
                                      : '立即发布',
                                  icon: 'IconPaperPlane' as const,
                                  disabled: !canWrite || initialPublishMode === 'blocked',
                                  title: !canWrite
                                    ? FORBIDDEN_ROLE_TITLE
                                    : initialPublishMode === 'blocked'
                                      ? initialPublishBlockedReason
                                      : undefined,
                                  onClick:
                                    canWrite && initialPublishMode !== 'blocked'
                                      ? publishChapter
                                      : undefined,
                                },
                              ]),
                          '---',
                          {
                            // 删除章节：server 无 DELETE /chapters（NOT-in-scope §五）→ 禁用
                            label: '删除章节',
                            icon: 'IconTrash',
                            tone: 'danger',
                            disabled: true,
                            title: NOT_IN_SCOPE_TITLE,
                          },
                        ]}
                      >
                        <button className="btn btn-ghost btn-sm">
                          <Icons.IconMoreV size={11} />
                        </button>
                      </ContextMenu>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Section>

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          tone={confirm.tone}
          icon={confirm.icon}
          requireTyping={confirm.requireTyping}
          confirmLabel={confirm.confirmLabel}
          cancelLabel={confirm.cancelLabel}
          onConfirm={() => confirm.onConfirm?.()}
          onClose={() => setConfirm(null)}
        />
      )}
      {(editChapter || newChapterOpen) && novelId && (
        <ChapterEditor
          chapter={editChapter}
          book={book}
          novelId={novelId}
          lang={lang}
          canWrite={canWrite}
          siblings={chapters}
          freeChapters={freeChapters}
          nextIndex={chapters.reduce((m, c) => Math.max(m, c.no), 0) + 1}
          initialPublishMode={initialPublishMode}
          initialPublishBlockedReason={initialPublishBlockedReason}
          onClose={() => {
            setEditChapter(null);
            setNewChapterOpen(false);
          }}
        />
      )}
      {scheduleOpen && novelId && (
        <ScheduleChapterDialog
          novelId={novelId}
          schedule={scheduleQuery.data}
          timeZone={publishTimeZone}
          onClose={() => setScheduleOpen(false)}
        />
      )}
    </div>
  );
}

// =========================================================
// 数据 tab
// =========================================================
function BookStats({ book, stats }: { book: BookRow; stats: BookStatsData }) {
  void book; // 数据 tab 一律消费真实 /stats 聚合（reading_users/paying_users/paid_chapters），不再读列表 adapter 零值
  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {/* 真实聚合（ADM-BOK-S014，GET /books/:id/stats）：reading_progress / unlock_records 聚合。
            环比 delta / 30 天 trend / 完读率无契约端点 → 移除（不编造，见 docs/plans §五）。 */}
        <div className="card" style={{ padding: 14 }}>
          <div className="stat-label">阅读用户</div>
          <div className="stat-value" style={{ fontSize: 22 }}>
            {stats.readers.toLocaleString()}
          </div>
        </div>
        <div className="card" style={{ padding: 14 }}>
          <div className="stat-label">付费用户</div>
          <div className="stat-value" style={{ fontSize: 22 }}>
            {stats.payers.toLocaleString()}
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 4, lineHeight: 1.45 }}>
            已注销用户经匿名化折叠为 1，观测近似，非结算
          </div>
        </div>
        <div className="card" style={{ padding: 14 }}>
          <div className="stat-label">付费章节</div>
          <div className="stat-value" style={{ fontSize: 22 }}>
            {stats.paidChapters.toLocaleString()}
          </div>
        </div>
      </div>
      <div
        className="card-flat"
        style={{ padding: '10px 12px', background: 'var(--info-soft)', borderColor: 'transparent' }}
      >
        <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
          <Icons.IconInfo size={12} style={{ color: 'var(--info)', marginTop: 2 }} />
          <span style={{ fontSize: 11.5, color: 'var(--fg-muted)', lineHeight: 1.55 }}>
            阅读用户 / 付费用户 / 付费章节为实时聚合 ·
            环比趋势与完读率暂无数据端点（未接入），待补埋点 / 统计端点后展示（见 docs/plans §五）
          </span>
        </div>
      </div>
    </div>
  );
}

// =========================================================
// 上下架 tab —— 四档可见受众的三层写界面（A6 §12 三层写 / §13 App 级覆盖 / §10 · §11 读模型）
// =========================================================

/**
 * 三层的界面语（层号 → 标题 / 生效范围）。层序即解析次序：层③ 覆盖层②，层② 覆盖层①（字典 §1.2）。
 * 与 `SOURCE_LAYER_LABEL` 分工：那份回答「当前值来自哪一层」（读侧 `source_layer`），
 * 这份是三个**写入口**自己的抬头，两者措辞相同但绑定的是不同字段，不合并成一份。
 */
const LAYER_SCOPE = {
  GROUP_DEFAULT: '生效范围：全部 App × 该书组全部语言版本',
  NOVEL: '生效范围：本语言版 × 该书全部投放 App',
  APP: '生效范围：本语言版 × 这一个 App',
} as const;

function BookStatus({
  book,
  locale,
  novelId,
  novelRow,
}: {
  book: BookRow;
  locale: string;
  novelId: string | null;
  novelRow: NovelResponse | null;
}) {
  const { data: apps } = useApps();
  const canChangeStatus = useCanChangeBookStatus();
  const queryClient = useQueryClient();
  /**
   * 本 tab 的三层真值统一取 `GET /books/:id/app-status`（A6 §10 / §11）—— 层①
   * `group_default_visibility` + 层② `novel_visibility` + 逐 App 的层③ 解析结果都在这一个读模型里。
   * 不再从 `novelRow` 另取一份层①/层②：同一事实两个来源必漂移（刷新时序不同就会出现两处显示不一致）。
   */
  const { data: appStatus } = useBookAppStatus(novelId);
  const statusWriteDisabled = !canChangeStatus || !novelId;
  const statusWriteDisabledTitle = !canChangeStatus
    ? FORBIDDEN_ROLE_TITLE
    : !novelId
      ? '当前语言版本尚未就绪'
      : undefined;

  const groupDefault = appStatus.group_default_visibility;
  const novelOverride = appStatus.novel_visibility;
  const globalReason: Record<string, string> = appStatus.global_reason_i18n ?? {};
  /**
   * 本行「不看 App 层」的解析值：层② 覆盖层①。判据取 shared 具名函数
   * （层③ 传 `null` = 本视角不考虑 App 覆盖），不在此内联 `??` 串（字典 §6.1）。
   */
  const rowVisibility = resolveEffectiveVisibility(null, novelOverride, groupDefault);

  // 待处理的单 App（override 写/删期间禁用该 App 行按钮，关掉「响应回但列表未刷、按钮复活」二次点击窗口）
  const [pendingApp, setPendingApp] = useState<string | null>(null);

  /** 缺原因的 novel_id → 运营能认的行标识（语言 + 书编码）；界面不展示裸 novel_id（§15.1 界面标识规则）*/
  const resolveRowLabel = (id: string): string | null => {
    const row = book.languageCodes.find((l) => l.novelId === id);
    if (!row) return null;
    return row.bookCode
      ? `${row.locale.toUpperCase()} · ${row.bookCode}`
      : row.locale.toUpperCase();
  };
  /** 三层写共用的失败 toast：`BOOK_DELIST_REASON_REQUIRED` 展开成「哪几行缺原因」，其余回退通用文案 */
  const writeErrorToast = (err: unknown, prefix: string) => {
    const detail = delistReasonRequiredErrorMessage(err, { resolveRow: resolveRowLabel });
    toast(`${prefix} · ${detail ?? (err instanceof Error ? err.message : '操作失败')}`, {
      tone: 'neg',
      icon: 'IconAlert',
    });
  };
  /**
   * 三层写成功后统一失效：列表 / 详情 / 本 tab 读模型 / Apps 书籍计数 / 该书操作日志。
   *
   * `scope: 'group'`（层① 写）额外把 App-status 读模型按**前缀**整体失效 —— 层① 改的是书组默认层，
   * 该书组每个语言版本的 effective 值都随之变化；只失效当前 novelId 的话，切到另一个最近看过的
   * 语言版本会在 staleTime 窗口内继续显示旧的书组默认档与旧 effective 值。
   */
  const invalidateAfterWrite = (scope: 'row' | 'group' = 'row') =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['books'] }),
      queryClient.invalidateQueries({ queryKey: ['book-detail', book.groupId] }),
      queryClient.invalidateQueries({ queryKey: appQueryKeys.all }),
      queryClient.invalidateQueries({
        queryKey: scope === 'group' ? ['book-app-status'] : ['book-app-status', novelId],
      }),
      queryClient.invalidateQueries({ queryKey: ['book-logs', novelId] }),
    ]);

  /**
   * 层① 书组默认层写（A6 §12.1 `POST /book-groups/:id/group-visibility`）。
   *
   * `:id` 是**书组 id**、不是 novel id（§12「书组资源路径」）。落 `DELISTED` 时入参原因会 merge 进
   * 该书组**每个** active 语言版本，任一行 merge 后仍缺 `en` → 整笔被拒（不是部分成功），
   * 故失败提示要点名是哪几行。
   */
  const groupVisibilityMutation = useMutation({
    mutationFn: ({
      visibility,
      reason,
    }: { visibility: NovelVisibility; reason: Record<string, string> }) => {
      const reasonEntries = Object.entries(reason).filter(([, v]) => v.trim().length > 0);
      return booksApi.setGroupDefaultVisibility(book.groupId, {
        visibility,
        ...(reasonEntries.length ? { status_reason_i18n: Object.fromEntries(reasonEntries) } : {}),
      });
    },
    onSuccess: async (_d, vars) => {
      toast(`书组默认受众档已改为「${visibilityLabel(vars.visibility)}」`, {
        tone: vars.visibility === 'PUBLIC' ? 'pos' : 'warn',
        icon: vars.visibility === 'DELISTED' ? 'IconBan' : 'IconEye',
      });
      await invalidateAfterWrite('group');
    },
    onError: (err) => writeErrorToast(err, '书组默认层未改动'),
  });

  /**
   * 层② 单书覆盖层写（A6 §12.2 `POST /books/:id/visibility`）。
   *
   * `visibility: null` 是**有意义的入参**（清除本层覆盖、回落继承层①），与配 `PUBLIC` 语义不同，
   * 故请求体恒带该键。回落后若层① 是 `DELISTED`，该行 effective 仍是 `DELISTED` → 同样要 `en` 原因。
   */
  const novelVisibilityMutation = useMutation({
    mutationFn: ({
      visibility,
      reason,
    }: { visibility: NovelVisibility | null; reason: Record<string, string> }) => {
      if (!novelId) throw new Error('当前语言版本尚未就绪');
      const reasonEntries = Object.entries(reason).filter(([, v]) => v.trim().length > 0);
      return booksApi.setVisibility(novelId, {
        visibility,
        ...(reasonEntries.length ? { status_reason_i18n: Object.fromEntries(reasonEntries) } : {}),
      });
    },
    onSuccess: async (_d, vars) => {
      toast(
        vars.visibility === null
          ? `《${book.displayTitle}》${locale.toUpperCase()} 版已改为${INHERIT_LABEL}`
          : `《${book.displayTitle}》${locale.toUpperCase()} 版受众档已改为「${visibilityLabel(vars.visibility)}」`,
        {
          tone: vars.visibility === 'PUBLIC' ? 'pos' : 'warn',
          icon: vars.visibility === 'DELISTED' ? 'IconBan' : 'IconEye',
        },
      );
      await invalidateAfterWrite();
    },
    onError: (err) => writeErrorToast(err, '本语言版未改动'),
  });

  /**
   * 层③ App 级覆盖写（A6 §13.1）。**四档全开**：上层为非 `PUBLIC` 时本层配 `PUBLIC` 是有业务意义的
   * 放开，与「无覆盖行」（继承上层）语义不同，不可用删行代替 —— 故这里给的是四档选择器，
   * 「恢复继承」是另一个动作（§13.2 DELETE）。
   */
  const appOverrideMutation = useMutation({
    mutationFn: ({
      appId,
      visibility,
      reason,
    }: {
      appId: string;
      visibility: NovelVisibility;
      appName: string;
      reason?: Record<string, string>;
    }) => {
      if (!novelId) return Promise.reject(new Error('当前语言版本尚未就绪'));
      const reasonEntries = Object.entries(reason ?? {}).filter(([, v]) => v.trim().length > 0);
      return booksApi.setOverride(novelId, {
        app_id: appId,
        visibility,
        ...(reasonEntries.length ? { status_reason_i18n: Object.fromEntries(reasonEntries) } : {}),
      });
    },
    onSuccess: async (_d, vars) => {
      toast(`${vars.appName} · 已改为「${visibilityLabel(vars.visibility)}」`, {
        tone: vars.visibility === 'PUBLIC' ? 'pos' : 'warn',
        icon: vars.visibility === 'DELISTED' ? 'IconBan' : 'IconEye',
      });
      await invalidateAfterWrite();
    },
    onError: (err, vars) => writeErrorToast(err, `${vars.appName} 未改动`),
    onSettled: () => setPendingApp(null),
  });

  /** 移除层③ 覆盖（A6 §13.2）→ 该 App 回落继承层②（层② 为空再落层①）。 */
  const appRemoveOverrideMutation = useMutation({
    mutationFn: ({ appId }: { appId: string; appName: string }) =>
      novelId
        ? booksApi.removeOverride(novelId, appId)
        : Promise.reject(new Error('当前语言版本尚未就绪')),
    onSuccess: async (_d, vars) => {
      toast(`${vars.appName} · 已移除 App 覆盖，回落继承上层`, { tone: 'pos', icon: 'IconCheck' });
      await invalidateAfterWrite();
    },
    onError: (err, vars) => writeErrorToast(err, `${vars.appName} 未改动`),
    onSettled: () => setPendingApp(null),
  });

  // per-App 状态行（端点真值 apps[]）→ 关联 app 元信息展示；app 已删时回退 app_id 兜底。
  const statusApps = appStatus.apps.map((row) => ({
    ...row,
    app: apps.find((a) => a.id === row.app_id) ?? null,
  }));
  const totalApps = statusApps.length;
  // header sub 用「进得了发现面的 App 数」而不是「上架数」：四档下 PAYING_ONLY 也进发现面（仅已付费可见），
  // 把它算进「未上架」会让运营以为该 App 没投放。判据取 display 模块的 discoveryReach（字典 §6.1 投影）。
  const reachableCount = statusApps.filter(
    (r) => discoveryReach(r.effective_visibility) !== 'NONE',
  ).length;
  const overriddenApps = statusApps.filter((r) => r.has_override);

  /** 三层写弹窗：`layer` 决定写哪一层；`appId` 仅层③ 用。 */
  const [visibilityDialog, setVisibilityDialog] = useState<{
    layer: 'GROUP_DEFAULT' | 'NOVEL' | 'APP';
    appId?: string;
    appName?: string;
  } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmReq | null>(null);

  const dialogCurrent: NovelVisibility | null =
    visibilityDialog?.layer === 'GROUP_DEFAULT'
      ? groupDefault
      : visibilityDialog?.layer === 'NOVEL'
        ? novelOverride
        : (statusApps.find((r) => r.app_id === visibilityDialog?.appId && r.has_override)
            ?.effective_visibility ?? null);
  const dialogPending =
    visibilityDialog?.layer === 'GROUP_DEFAULT'
      ? groupVisibilityMutation.isPending
      : visibilityDialog?.layer === 'NOVEL'
        ? novelVisibilityMutation.isPending
        : appOverrideMutation.isPending;

  const submitVisibilityDialog = (next: NovelVisibility | null, reason: Record<string, string>) => {
    if (!visibilityDialog) return;
    const close = () => setVisibilityDialog(null);
    const fail = () => {
      /* 失败：保留弹窗 + 表单内容不丢；错误 toast 已在 onError；不显成功 */
    };
    if (visibilityDialog.layer === 'GROUP_DEFAULT') {
      // 层① 不接受 null（三层里唯一 NOT NULL 的一层，§12.1）——选择器也不给「继承」项
      if (next === null) return;
      groupVisibilityMutation.mutateAsync({ visibility: next, reason }).then(close).catch(fail);
      return;
    }
    if (visibilityDialog.layer === 'NOVEL') {
      novelVisibilityMutation.mutateAsync({ visibility: next, reason }).then(close).catch(fail);
      return;
    }
    if (next === null || !visibilityDialog.appId) return;
    setPendingApp(visibilityDialog.appId);
    appOverrideMutation
      .mutateAsync({
        appId: visibilityDialog.appId,
        visibility: next,
        appName: visibilityDialog.appName ?? visibilityDialog.appId,
        reason,
      })
      .then(close)
      .catch(fail);
  };

  /** 清空全部层③ 覆盖：层③ 会遮住刚改的层②，运营改完层② 常需要一次性把例外收回来。 */
  const clearAllOverrides = () => {
    setConfirm({
      title: `清除《${book.displayTitle}》全部 App 覆盖？`,
      message: `将删除 ${overriddenApps.length} 条 App 级覆盖行，这些 App 回落继承本语言版（层②，当前「${novelOverride ? visibilityLabel(novelOverride) : `${INHERIT_LABEL} → ${visibilityLabel(groupDefault)}`}」）。不改动层① / 层② 本身。`,
      tone: 'default',
      icon: 'IconCheckCircle',
      confirmLabel: '清除全部覆盖',
      onConfirm: async () => {
        const results = await Promise.allSettled(
          overriddenApps.map((r) =>
            appRemoveOverrideMutation.mutateAsync({
              appId: r.app_id,
              appName: r.app?.name ?? r.app_id,
            }),
          ),
        );
        const failed = results.filter((x) => x.status === 'rejected').length;
        if (failed > 0) {
          toast(`${failed} 个 App 覆盖清除失败 · 其余已清除，请重试`, {
            tone: 'neg',
            icon: 'IconAlert',
          });
          return;
        }
        setConfirm(null);
      },
    });
  };

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
        当前语言：{locale.toUpperCase()} ·
        <CopyableCode
          value={
            novelRow?.book_code ?? book.languageCodes.find((row) => row.locale === locale)?.bookCode
          }
          label="书编码"
          prefix="书编码"
        />
      </div>

      {/* 三层配置（A6 §12）：层① 书组默认 + 层② 本语言版覆盖。层③ 在下面一个 Section 逐 App 给。 */}
      <Section
        title="受众档 · 三层配置"
        sub="下层覆盖上层：App 覆盖（层③）→ 本语言版覆盖（层②）→ 书组默认（层①）。层① 恒有值，层②/③ 可不配置=继承上层。"
      >
        <div className="vstack" style={{ gap: 8, padding: '10px 0' }}>
          {/* 层① 书组默认层 */}
          <div
            className="card-flat"
            style={{ border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px' }}
            data-testid="layer-group-default"
          >
            <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="hstack" style={{ gap: 6 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                    层① {SOURCE_LAYER_LABEL.GROUP_DEFAULT}
                  </span>
                  <span
                    className={`chip ${visibilityChipClass(groupDefault)}`}
                    style={{ fontSize: 9.5, padding: '0 6px' }}
                    title={VISIBILITY_META[groupDefault].desc}
                  >
                    ● {visibilityLabel(groupDefault)}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 3 }}>
                  {LAYER_SCOPE.GROUP_DEFAULT} · {VISIBILITY_META[groupDefault].desc}
                </div>
              </div>
              <button
                className="btn btn-sm"
                disabled={!canChangeStatus || groupVisibilityMutation.isPending}
                title={!canChangeStatus ? FORBIDDEN_ROLE_TITLE : '改书组默认受众档（层①）'}
                style={!canChangeStatus ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                onClick={() => setVisibilityDialog({ layer: 'GROUP_DEFAULT' })}
              >
                <Icons.IconEdit size={11} />
                改书组默认
              </button>
            </div>
          </div>

          {/* 层② 本语言版覆盖 —— 回显**原值**（含 null=未覆盖），不合成（A6 §15.3 / §15.1 的「本口径限列表面」）*/}
          <div
            className="card-flat"
            style={{ border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px' }}
            data-testid="layer-novel"
          >
            <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="hstack" style={{ gap: 6 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                    层② {SOURCE_LAYER_LABEL.NOVEL}（{locale.toUpperCase()}）
                  </span>
                  {novelOverride === null ? (
                    <span
                      className="chip"
                      style={{ fontSize: 9.5, padding: '0 6px' }}
                      title={INHERIT_DESC}
                    >
                      未覆盖 · {INHERIT_LABEL}
                    </span>
                  ) : (
                    <span
                      className={`chip ${visibilityChipClass(novelOverride)}`}
                      style={{ fontSize: 9.5, padding: '0 6px' }}
                      title={VISIBILITY_META[novelOverride].desc}
                    >
                      ● {visibilityLabel(novelOverride)}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 3 }}>
                  {LAYER_SCOPE.NOVEL} ·{' '}
                  {novelOverride === null
                    ? `本行没有自己的设置，当前跟随层① 解析为「${visibilityLabel(rowVisibility)}」`
                    : VISIBILITY_META[novelOverride].desc}
                </div>
              </div>
              <button
                className="btn btn-sm"
                disabled={statusWriteDisabled || novelVisibilityMutation.isPending}
                title={statusWriteDisabledTitle ?? '改本语言版受众档（层②）'}
                style={statusWriteDisabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                onClick={() => setVisibilityDialog({ layer: 'NOVEL' })}
              >
                <Icons.IconEdit size={11} />
                改本语言版
              </button>
            </div>
          </div>
        </div>

        {/* 下架原因池（§11 `global_reason_i18n`）：per-App 下架原因单点引用它，reason 不上提书组 */}
        {(rowVisibility === 'DELISTED' || Object.keys(globalReason).length > 0) && (
          <div
            className="card-flat"
            style={{
              padding: '12px 14px',
              background: rowVisibility === 'DELISTED' ? 'var(--neg-soft)' : 'var(--warn-soft)',
              borderColor: 'transparent',
              margin: '0 0 10px',
            }}
          >
            <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: 'var(--bg-elev)',
                  color: rowVisibility === 'DELISTED' ? 'var(--neg)' : 'var(--warn)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icons.IconBan size={14} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="hstack" style={{ gap: 8 }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>
                    {rowVisibility === 'DELISTED'
                      ? `本语言版已下架（来自${novelOverride === 'DELISTED' ? '层②' : '层①'}）`
                      : '下架原因池（当前未下架，保留备查）'}
                  </span>
                </div>
                <div
                  style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2, marginBottom: 8 }}
                >
                  下架原因 · 客户端按用户语言展示，未维护时 fallback 英文 ·
                  <span className="mono"> novel_app_overrides </span>无 reason 列，App
                  级下架原因单点引用本池
                </div>
                <div className="vstack" style={{ gap: 3 }}>
                  {Object.entries(globalReason)
                    .filter(([, v]) => v)
                    .sort(([a], [b]) => (a === 'en' ? -1 : b === 'en' ? 1 : a.localeCompare(b)))
                    .map(([k, v]) => (
                      <div key={k} className="hstack" style={{ gap: 6, alignItems: 'flex-start' }}>
                        <span
                          className="chip"
                          style={{ fontSize: 9.5, padding: '0 5px', flexShrink: 0 }}
                        >
                          <span className="mono">{k.toUpperCase()}</span>
                          <span style={{ color: 'var(--fg-faint)', marginLeft: 3 }}>
                            {langName(k) || k.toUpperCase()}
                          </span>
                        </span>
                        <span style={{ fontSize: 12 }}>{v}</span>
                      </div>
                    ))}
                  {Object.keys(globalReason).length === 0 && (
                    <span style={{ fontSize: 11.5, color: 'var(--warn)' }}>
                      未填写下架原因 —— 改档为「下架」时必须补上英文原因
                    </span>
                  )}
                </div>
              </div>
              <button
                className="btn btn-sm"
                disabled={statusWriteDisabled || novelVisibilityMutation.isPending}
                title={statusWriteDisabledTitle ?? '编辑下架原因（写入本语言版原因池）'}
                style={{
                  flexShrink: 0,
                  ...(statusWriteDisabled ? { opacity: 0.55, cursor: 'not-allowed' } : {}),
                }}
                onClick={() => setVisibilityDialog({ layer: 'NOVEL' })}
              >
                <Icons.IconEdit size={11} />
                编辑原因
              </button>
            </div>
          </div>
        )}
      </Section>

      {/* 层③：逐 App 生效档 + 来源层（A6 §10 行集 = 投放名单 ∪ orphan override） */}
      <Section
        title="各 App 生效档 · 层③"
        sub={`本书已在 ${totalApps} 个 App 投放 · 当前 ${reachableCount} 个进得了发现面 · 每行标注当前值来自哪一层`}
        action={
          <div className="hstack" style={{ gap: 6 }}>
            {overriddenApps.length > 0 && (
              <button
                className="btn btn-sm"
                disabled={statusWriteDisabled || appRemoveOverrideMutation.isPending}
                title={statusWriteDisabledTitle ?? '删除全部 App 级覆盖行，回落继承层②'}
                style={statusWriteDisabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                onClick={clearAllOverrides}
              >
                <Icons.IconCheckCircle size={11} />
                清除全部 App 覆盖（{overriddenApps.length}）
              </button>
            )}
          </div>
        }
      >
        <div className="vstack" style={{ gap: 8, padding: '10px 0' }}>
          {statusApps.map((row) => {
            const appId = row.app_id;
            const app = row.app;
            const effective = row.effective_visibility;
            const reach = discoveryReach(effective);
            const rowPending =
              pendingApp === appId &&
              (appOverrideMutation.isPending || appRemoveOverrideMutation.isPending);
            // 市场展示：前 3 个国家 + 余量计数（设计稿 market.slice(0,3)）
            const markets = app?.market ?? [];
            const marketStr =
              markets
                .slice(0, 3)
                .map((m) => fmtCountry(m))
                .join('/') + (markets.length > 3 ? ` +${markets.length - 3}` : '');
            return (
              <div
                key={appId}
                className="card-flat"
                style={{ border: '1px solid var(--border)', borderRadius: 7, padding: '9px 12px' }}
              >
                <div className="hstack" style={{ gap: 10 }}>
                  {app && <AppIcon app={app} size="sm" style={{ flexShrink: 0 }} />}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="hstack" style={{ gap: 6 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 500 }}>{app?.name ?? appId}</span>
                      <span
                        className={`chip ${visibilityChipClass(effective)}`}
                        style={{ fontSize: 9.5, padding: '0 6px' }}
                        title={VISIBILITY_META[effective].desc}
                      >
                        ● {visibilityLabel(effective)}
                      </span>
                      {/* 值来自哪一层（§11 `source_layer`）：运营据此判断该改哪一层 */}
                      <span
                        className="chip"
                        style={{ fontSize: 9, padding: '0 5px', opacity: 0.8 }}
                        title={SOURCE_LAYER_HINT[row.source_layer]}
                        data-testid={`source-layer-${appId}`}
                      >
                        来自 {SOURCE_LAYER_LABEL[row.source_layer]}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                        {DISCOVERY_REACH_LABEL[reach]}
                      </span>
                    </div>
                    <div
                      className="mono"
                      style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 2 }}
                    >
                      {appId}
                      {app ? ` · ${app.platform}` : ''}
                      {marketStr ? ` · ${marketStr}` : ''}
                    </div>
                    {/* 上架时间（launched_at = 端点 published_at 派生）+ 有覆盖时追加变更时间/操作人（override 真值，缺省不显）*/}
                    <div
                      className="mono"
                      style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 1 }}
                    >
                      <Icons.IconCheckCircle
                        size={8}
                        style={{ verticalAlign: 'middle', marginRight: 2, color: 'var(--pos)' }}
                      />
                      上架 {row.launched_at ? fmtDate(row.launched_at) : '—'}
                      {row.has_override && row.changed_at && (
                        <span style={{ marginLeft: 5, color: 'var(--warn)' }}>
                          · 覆盖 {fmtDateTime(row.changed_at)}
                          {row.changed_by ? ` by ${row.changed_by}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="hstack" style={{ gap: 6, flexShrink: 0 }}>
                    <button
                      className="btn btn-sm"
                      disabled={statusWriteDisabled || rowPending}
                      title={
                        statusWriteDisabledTitle ?? '为这一个 App 单独配置受众档（层③，四档全开）'
                      }
                      style={
                        statusWriteDisabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined
                      }
                      onClick={() =>
                        setVisibilityDialog({
                          layer: 'APP',
                          appId,
                          appName: app?.name ?? appId,
                        })
                      }
                    >
                      <Icons.IconEdit size={11} />
                      {row.has_override ? '改本 App 覆盖' : '单独配置本 App'}
                    </button>
                    {row.has_override && (
                      <button
                        className="btn btn-sm"
                        disabled={statusWriteDisabled || rowPending}
                        title={statusWriteDisabledTitle ?? '删除本 App 覆盖行，回落继承层②'}
                        style={
                          statusWriteDisabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined
                        }
                        onClick={() => {
                          setPendingApp(appId);
                          appRemoveOverrideMutation.mutate({ appId, appName: app?.name ?? appId });
                        }}
                      >
                        <Icons.IconCheckCircle size={11} />
                        移除覆盖
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {totalApps === 0 && (
            <div
              style={{
                padding: '14px 0',
                fontSize: 12,
                color: 'var(--fg-muted)',
                textAlign: 'center',
              }}
            >
              本书暂未投放到任何 App · 投放成员由书的语言版本 ∈ App 支持语言派生
            </div>
          )}
        </div>
        <div
          className="card-flat"
          style={{
            padding: '10px 12px',
            background: 'var(--info-soft)',
            borderColor: 'transparent',
            marginTop: 4,
          }}
        >
          <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
            <Icons.IconInfo size={12} style={{ color: 'var(--info)', marginTop: 2 }} />
            <span style={{ fontSize: 11.5, color: 'var(--fg-muted)', lineHeight: 1.55 }}>
              「单独配置本 App」写的是 App 覆盖行（层③），四档全开 ——
              上层不是「公开」时在这里配「公开」是**放开**这一个 App，与「移除覆盖」（回落继承上层）
              是两个不同操作，不可互相代替。
            </span>
          </div>
        </div>
      </Section>

      {visibilityDialog && (
        <VisibilityDialog
          layer={visibilityDialog.layer}
          scope={
            visibilityDialog.layer === 'GROUP_DEFAULT'
              ? `书组《${book.displayTitle}》全部语言版本`
              : visibilityDialog.layer === 'NOVEL'
                ? `《${book.displayTitle}》${locale.toUpperCase()} 版`
                : `${visibilityDialog.appName ?? ''}（本 App 覆盖）`
          }
          current={dialogCurrent}
          groupDefault={groupDefault}
          // 层① 的原因会 merge 进书组**每个**语言版本，故不能拿选中行的 globalReason 当初值
          //（那会把这一行的文案当成整组的默认文案）；整组下架必须由运营显式填一次。
          initialReason={visibilityDialog.layer === 'GROUP_DEFAULT' ? undefined : globalReason}
          pending={dialogPending}
          onCancel={() => setVisibilityDialog(null)}
          onConfirm={submitVisibilityDialog}
        />
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          tone={confirm.tone}
          icon={confirm.icon}
          requireTyping={confirm.requireTyping}
          confirmLabel={confirm.confirmLabel}
          cancelLabel={confirm.cancelLabel}
          onConfirm={() => confirm.onConfirm?.()}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// =========================================================
// 受众档写弹窗（三层共用：选档 + `DELISTED` 时填原因）
// =========================================================

/**
 * 三层写共用一个弹窗，按 `layer` 只改两件事：**给不给「继承上层」这一项**，以及标题里的层号。
 * 三层的档位闭集本身完全相同（层③ 自 dd#956 起也是四档全开），拆成三个组件只会让「加一档要改三处」。
 *
 * - 层①（`GROUP_DEFAULT`）**不给**「继承」——三层里唯一 NOT NULL 的一层，`null` 不是合法入参（§12.1）。
 * - 层②（`NOVEL`）给「继承」= 请求体 `visibility: null`，清除本层覆盖回落层①；与显式配「公开」
 *   语义不同（§12.2），故两项并列、不合并。
 * - 层③（`APP`）给「继承」时走的是 §13.2 DELETE（删覆盖行），不是 POST `visibility: null` ——
 *   本弹窗只负责 POST，故层③ 也不给该项；「移除覆盖」是行上的独立按钮。
 *
 * **原因**：选到 `DELISTED`、或层② 选「继承」而层① 恰是 `DELISTED`（回落后 effective 仍是下架，§12.2）
 * 时必填。UI 沿用后台既有的「中英必填」口径（契约底线只要求 `en` 非空，这里更严，不放宽）。
 */
function VisibilityDialog({
  layer,
  scope,
  current,
  groupDefault,
  initialReason,
  pending,
  onCancel,
  onConfirm,
}: {
  layer: 'GROUP_DEFAULT' | 'NOVEL' | 'APP';
  scope: string;
  /** 当前值；`null` = 本层未覆盖（层② / 未配置的层③）*/
  current: NovelVisibility | null;
  /** 层① 当前值，用于「继承」项的副标题与回落后是否要原因的判定 */
  groupDefault: NovelVisibility;
  initialReason?: Record<string, string>;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: (next: NovelVisibility | null, reason: Record<string, string>) => void;
}) {
  const allowInherit = layer === 'NOVEL';
  const [next, setNext] = useState<NovelVisibility | null>(current);
  const [values, setValues] = useState<Record<string, string>>(initialReason ?? { zh: '', en: '' });
  const { data: sys } = useSysConfig();
  const enabledLangs = sys.languages.filter((l) => l.enabled).map((l) => l.language_code);

  // 回落继承后层① 若是 DELISTED，该行 effective 仍是下架 → 同样要 en 原因（§12.2「置 null 的 DELISTED 前置」）
  const needsReason = next === 'DELISTED' || (next === null && groupDefault === 'DELISTED');
  const canConfirm =
    !pending &&
    (!allowInherit ? next !== null : true) &&
    (!needsReason || (Boolean((values.zh || '').trim()) && Boolean((values.en || '').trim())));
  const layerTitle =
    layer === 'GROUP_DEFAULT'
      ? `层① ${SOURCE_LAYER_LABEL.GROUP_DEFAULT}`
      : layer === 'NOVEL'
        ? `层② ${SOURCE_LAYER_LABEL.NOVEL}`
        : `层③ ${SOURCE_LAYER_LABEL.APP}`;

  return (
    <div className="dialog-backdrop" onClick={onCancel}>
      <div
        className="dialog"
        style={{ width: 580, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 7,
              background: 'var(--info-soft)',
              color: 'var(--info)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.IconEye size={16} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, fontSize: 14 }}>{layerTitle} · 选择可见受众档</div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>
              {scope} · {LAYER_SCOPE[layer]}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onCancel}>
            <Icons.IconX size={14} />
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 18 }}>
          <div className="vstack" style={{ gap: 6 }}>
            {allowInherit && (
              <label
                className="card-flat"
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  padding: '9px 11px',
                  border: `1px solid ${next === null ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 7,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="visibility"
                  checked={next === null}
                  onChange={() => setNext(null)}
                  style={{ marginTop: 2 }}
                />
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 500 }}>{INHERIT_LABEL}</span>
                  <span style={{ fontSize: 11, color: 'var(--fg-muted)', display: 'block' }}>
                    {INHERIT_DESC} · 当前书组默认为「{visibilityLabel(groupDefault)}」
                  </span>
                </span>
              </label>
            )}
            {VISIBILITY_OPTIONS.map((v) => (
              <label
                key={v}
                className="card-flat"
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  padding: '9px 11px',
                  border: `1px solid ${next === v ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 7,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="visibility"
                  checked={next === v}
                  onChange={() => setNext(v)}
                  style={{ marginTop: 2 }}
                />
                <span style={{ minWidth: 0 }}>
                  <span className="hstack" style={{ gap: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 500 }}>{visibilityLabel(v)}</span>
                    <span className={`chip ${visibilityChipClass(v)}`} style={{ fontSize: 9 }}>
                      {VISIBILITY_META[v].audience}
                    </span>
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--fg-muted)', display: 'block' }}>
                    {VISIBILITY_META[v].desc}
                  </span>
                </span>
              </label>
            ))}
          </div>
          {needsReason && (
            <div style={{ marginTop: 14 }}>
              {/* 原因为短句 → 单行 input（去 multiline）；中英 placeholder 与 autoFocus 对齐设计稿 */}
              <MultiLangFormRows
                label="下架原因"
                value={values}
                onChange={setValues}
                availableLangs={enabledLangs}
                placeholderMap={{
                  zh: '如：版权到期 / 内容审核中 / 运营策略调整',
                  en: 'e.g. Rights expired / Under review / Operational adjustment',
                }}
                autoFocusFirst
              />
              <div
                className="card-flat"
                style={{
                  padding: '10px 12px',
                  background: 'var(--neg-soft)',
                  borderColor: 'transparent',
                  marginTop: 10,
                }}
              >
                <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
                  <Icons.IconAlert size={12} style={{ color: 'var(--neg)', marginTop: 2 }} />
                  <span style={{ fontSize: 11.5, color: 'var(--fg)', lineHeight: 1.55 }}>
                    {layer === 'GROUP_DEFAULT'
                      ? '书组默认层落「下架」会 merge 原因到该书组每个语言版本，任一版本仍缺英文原因即整笔拒绝、不部分生效。'
                      : next === null
                        ? '书组默认层当前是「下架」，本语言版清除覆盖后仍是下架状态，故仍需英文原因。'
                        : '下架后含已付费 / 已购 / 已买断用户一律不可获取。原因将记录到操作日志，并按用户语言展示（未维护时 fallback 英文）。'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button className="btn" disabled={pending} onClick={onCancel}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!canConfirm}
            // 只在原因编辑器可见（needsReason）时提交原因：非 DELISTED 档编辑器是隐藏的，
            // 此时把 initialReason 原样送上去，层① 会把它 merge 进书组每个语言版本、
            // 覆盖各行原有的原因文本。
            onClick={() => onConfirm(next, needsReason ? values : {})}
            style={{
              opacity: canConfirm ? 1 : 0.5,
              background: next === 'DELISTED' ? 'var(--neg)' : undefined,
              borderColor: next === 'DELISTED' ? 'var(--neg)' : undefined,
            }}
          >
            <Icons.IconCheck size={11} />
            {pending
              ? '处理中…'
              : next === null
                ? `确认改为${INHERIT_LABEL}`
                : `确认改为${visibilityLabel(next)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// =========================================================
// 日志 tab（运营操作 → admin_audit_logs；系统事件 → novel_system_events）
// =========================================================
interface OpLog {
  operation?: unknown;
  id: string; // audit 行稳定 id：展开态 key，避免页内 index 跨翻页错位
  time: string;
  sortAt: string; // ISO，全部 tab 两类合并排序用
  who: string;
  type: string;
  summary: string;
  diff: AuditFieldDiff[]; // 统一显示规则：raw 字段 diff，渲染走共享 AuditDiffTable（见 ~/lib/audit-display）
  action: string; // raw action code（无 diff 时 AuditDiffTable 据此 humanize 兜底文案）
  fieldRefs: Record<string, string> | null; // 后端 field_refs（*_id 值→业务名）
  kind: 'op';
}
interface SysLog {
  id: string;
  time: string;
  sortAt: string;
  source: string;
  event: string;
  eventType: string;
  result: string;
  kind: 'sys';
}
type LogItem = OpLog | SysLog;

const SYS_EVENT_TYPE_LABEL: Record<string, string> = {
  scheduled_publish: '定时发布',
  auto_collect_update: '自动采集',
  status_change: '状态切换',
};

// ---- 运营操作日志渲染辅助（admin_audit_logs 真实投影 → 视图模型，接 ADM-BOK-S015）----
const BOOK_LOG_PAGE_LIMIT = 20;

function BookLogs({ novelId }: { novelId: string }) {
  // 审计 diff 里的采集来源字段同样限 root（ADM-BOK-S004 展示口径）：日志 tab 本身开到 operator+，
  // 而 root 存过一次 IMPORTED 书就会在 before/after 里留下 source_external_id / source_provider 原值。
  const canViewSource = useCanViewBookSource();
  const [typeFilter, setTypeFilter] = useState<'all' | 'op' | 'sys'>('all');
  const [rangeFilter, setRangeFilter] = useState<'7d' | '30d' | 'all'>('30d');
  const [page, setPage] = useState(1);
  // 展开态按 audit 行稳定 id（非页内 index）——分页/换 tab 后 id 失配自然不展开，避免错行展开。
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [exporting, setExporting] = useState(false);

  // 时间窗下推服务端：近 7/30 天 → from = now - N 天；全部 → 不传 from（service 按 from ≤ created_at 过滤）。
  // 必须 useMemo([rangeFilter])：否则每渲染 Date.now() 变 → from 变 → queryKey 变 → 无限 refetch。
  const from = useMemo(
    () =>
      rangeFilter === 'all'
        ? undefined
        : new Date(Date.now() - (rangeFilter === '7d' ? 7 : 30) * 86_400_000).toISOString(),
    [rangeFilter],
  );
  // 运营操作 + 系统事件各查一次：pill 计数取服务端 total；列表按 typeFilter 取对应页。
  const opQuery = { type: 'operation' as const, ...(from ? { from } : {}) };
  const sysQuery = { type: 'system' as const, ...(from ? { from } : {}) };
  // 全部：两路都钉第 1 页（API 无 mixed offset，禁止用双路第 N 页假装全局时间线）。
  const opPage = typeFilter === 'op' ? page : 1;
  const sysPage = typeFilter === 'sys' ? page : 1;
  const {
    data: opItems,
    isLoading: opLoading,
    isError: opError,
    refetch: refetchOp,
    total: opTotal,
  } = useBookOperationLogs(novelId, opQuery, opPage, BOOK_LOG_PAGE_LIMIT);
  const {
    data: sysItems,
    isLoading: sysLoading,
    isError: sysError,
    refetch: refetchSys,
    total: sysTotal,
  } = useBookOperationLogs(novelId, sysQuery, sysPage, BOOK_LOG_PAGE_LIMIT);
  const isSys = typeFilter === 'sys';
  const isAll = typeFilter === 'all';
  const isLoading = isAll ? opLoading || sysLoading : isSys ? sysLoading : opLoading;
  const isError = isAll ? opError || sysError : isSys ? sysError : opError;
  const refetch = () => {
    if (isAll) {
      void refetchOp();
      return refetchSys();
    }
    return isSys ? refetchSys() : refetchOp();
  };
  // 全部：pill/字幕用两类 COUNT 之和；列表只合并各路最新一页，不分页。
  const activeTotal = isAll ? opTotal + sysTotal : isSys ? sysTotal : opTotal;
  const pageTotal = isAll ? 0 : activeTotal;

  // 越界页防卡空表（同书籍列表 clamp 范式）：total 收缩后停在超出真实总页数的 page，
  // 后端返 data=[] 且分页器卸载 → 运营卡空页。全部钉第 1 页，不 clamp。
  useEffect(() => {
    if (isAll || isLoading || isError) return;
    const maxPage = Math.max(1, Math.ceil(pageTotal / BOOK_LOG_PAGE_LIMIT));
    if (page > maxPage) setPage(maxPage);
  }, [isAll, pageTotal, page, isLoading, isError]);

  // 真实审计行 → 渲染视图模型（运营操作）。服务端已按 created_at DESC 排序，直接用。
  const OPS: LogItem[] = opItems.map((o: BookOperationLogItem) => ({
    id: o.id,
    kind: 'op' as const,
    time: auditOperationTimeLabel(o.created_at, o.after),
    sortAt: o.created_at,
    who: o.actor_nickname || o.actor_email || '—',
    type: humanizeAuditAction(o.action).label,
    // 事件列文案：人性化动作 + 对象展示名（与 settings 全局操作日志同口径；chapters→「书名·章节标题」尤其有用）
    summary: o.target_name
      ? `${auditOperationSummary(o.after) ?? humanizeAuditAction(o.action).label} · ${o.target_name}`
      : (auditOperationSummary(o.after) ?? humanizeAuditAction(o.action).label),
    // 非 root：把来源 identity 的变更行从 diff 里剔掉。**在这里剔而不是在渲染层**——
    // `AuditDiffTable` 与摘要文案吃的是同一份 diff，只挡渲染会让摘要仍漏出原值。
    // 与页面其余来源门控同为**展示层**，不是响应裁剪（后端仍按契约下发，见 book-rbac.ts 注释）。
    diff: deriveAuditDiff(o.before, o.after).filter(
      (d) => canViewSource || !BOOK_SOURCE_AUDIT_FIELDS.has(d.field),
    ),
    action: o.action,
    fieldRefs: o.field_refs,
    operation: auditOperationContext(o.after),
  }));
  const SYS: LogItem[] = sysItems.map((s: SystemEventLogItem) => ({
    id: s.id,
    kind: 'sys' as const,
    time: fmtDateTime(s.occurred_at),
    sortAt: s.occurred_at,
    source: s.trigger_source,
    event: s.summary,
    eventType: s.event_type,
    result: s.result === 'success' ? '成功' : s.result === 'failed' ? '失败' : s.result,
  }));

  // 'sys'/'op' 各一路当前页；'all' 合并两路第 1 页（不是全集混合分页）。
  const filtered = isSys
    ? SYS
    : typeFilter === 'op'
      ? OPS
      : [...OPS, ...SYS].sort((a, b) => (a.sortAt < b.sortAt ? 1 : a.sortAt > b.sortAt ? -1 : 0));
  const showRows = !isError && !isLoading;

  const toggle = (id: string) => {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  };

  // CSV 由服务端按当前 from/type 全量生成（上限 5000 行，超限走 X-Export-Truncated 透出）。
  // 全部：两套列不能 mixed，连下 operation + system 两份（S015 导出当前筛选）。
  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    const types: Array<'operation' | 'system'> = isAll
      ? ['operation', 'system']
      : [isSys ? 'system' : 'operation'];
    const date = new Date().toISOString().slice(0, 10);
    const label = (t: 'operation' | 'system') => (t === 'system' ? '系统事件' : '运营操作');
    const okTypes: Array<'operation' | 'system'> = [];
    const failTypes: Array<'operation' | 'system'> = [];
    let truncated = false;
    let lastErr: unknown;
    try {
      for (const exportType of types) {
        try {
          const result = await booksApi.downloadLogsCsv(
            novelId,
            { type: exportType, ...(from ? { from } : {}) },
            `book-${novelId}-logs-${exportType}-${date}.csv`,
          );
          okTypes.push(exportType);
          if (result.truncated) truncated = true;
        } catch (e) {
          failTypes.push(exportType);
          lastErr = e;
        }
      }
      if (failTypes.length && okTypes.length) {
        toast(
          `已导出${okTypes.map(label).join('、')}，${failTypes.map(label).join('、')}失败，请重试`,
          {
            tone: 'warn',
            icon: 'IconDownload',
          },
        );
      } else if (failTypes.length) {
        toast(lastErr instanceof Error ? `导出失败：${lastErr.message}` : '导出失败', {
          tone: 'neg',
          icon: 'IconAlert',
        });
      } else if (truncated) {
        toast('超导出上限，仅导出最新 5000 条，请缩窄时间范围', {
          tone: 'warn',
          icon: 'IconDownload',
        });
      } else {
        toast(
          isAll
            ? '已导出运营操作与系统事件两份 CSV'
            : isSys
              ? '已导出系统事件 CSV'
              : '已导出操作日志 CSV',
          { tone: 'pos', icon: 'IconDownload' },
        );
      }
    } finally {
      setExporting(false);
    }
  };

  const TypePill = ({
    id,
    label,
    count,
  }: {
    id: 'all' | 'op' | 'sys';
    label: string;
    count: number;
  }) => (
    <button
      onClick={() => {
        setTypeFilter(id);
        setPage(1);
      }}
      className="focusable"
      style={{
        padding: '5px 12px',
        borderRadius: 999,
        border: typeFilter === id ? '1px solid var(--accent)' : '1px solid var(--border)',
        background: typeFilter === id ? 'var(--accent-soft)' : 'var(--bg-elev)',
        color: typeFilter === id ? 'var(--accent-fg)' : 'var(--fg-muted)',
        font: 'inherit',
        fontSize: 11.5,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
      }}
    >
      {label}
      <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
        {count}
      </span>
    </button>
  );

  const RangePill = ({ id, label }: { id: '7d' | '30d' | 'all'; label: string }) => (
    <button
      onClick={() => {
        // 范围变 → from 变 → 重新查询；页码回第一页，避免停留在新范围越界的页。
        setRangeFilter(id);
        setPage(1);
      }}
      className="focusable"
      style={{
        padding: '5px 12px',
        borderRadius: 999,
        border: rangeFilter === id ? '1px solid var(--accent)' : '1px solid var(--border)',
        background: rangeFilter === id ? 'var(--accent-soft)' : 'var(--bg-elev)',
        color: rangeFilter === id ? 'var(--accent-fg)' : 'var(--fg-muted)',
        font: 'inherit',
        fontSize: 11.5,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );

  const opCount = opTotal;
  const sysCount = sysError ? null : sysTotal;
  const allCount = opError || sysError ? null : opTotal + sysTotal;

  return (
    <Section
      title="操作与事件日志"
      sub={`按时间倒序 · 共 ${activeTotal} 条（当前语言版本${
        isSys
          ? '系统事件'
          : isAll
            ? `运营操作 + 系统事件，列表各取最新 ${BOOK_LOG_PAGE_LIMIT} 条合并`
            : '运营操作'
      }）${from ? ` · 起 ${fmtDateTime(from)}（客户端时钟解析）` : ' · 全部时间'} · 排查 / 追溯参考`}
      action={
        <button className="btn btn-sm" onClick={exportCsv} disabled={exporting}>
          <Icons.IconDownload size={11} />
          {exporting ? '导出中…' : '导出 CSV'}
        </button>
      }
    >
      <div className="hstack" style={{ gap: 10, padding: '10px 0', flexWrap: 'wrap' }}>
        <div className="hstack" style={{ gap: 5 }}>
          <span className="label" style={{ marginRight: 4 }}>
            类型
          </span>
          <TypePill id="all" label="全部" count={allCount ?? 0} />
          <TypePill id="op" label="运营操作" count={opCount} />
          <TypePill id="sys" label="系统事件" count={sysCount ?? 0} />
        </div>
        <div style={{ width: 1, height: 18, background: 'var(--border)' }} />
        <div className="hstack" style={{ gap: 5 }}>
          <span className="label" style={{ marginRight: 4 }}>
            时间
          </span>
          <RangePill id="7d" label="近 7 天" />
          <RangePill id="30d" label="近 30 天" />
          <RangePill id="all" label="全部" />
        </div>
      </div>

      {/* 契约按当前 per-locale novel 精确投影；切换语言时组件随 novelId 重新查询。 */}
      <div style={{ fontSize: 10.5, color: 'var(--fg-faint)', paddingBottom: 6 }}>
        {isSys
          ? '当前语言版本的系统事件；切换语言可查看对应版本日志'
          : isAll
            ? `默认「全部」各取两类最新 ${BOOK_LOG_PAGE_LIMIT} 条按时间合并（接口无混合分页）；更多请切类型翻页`
            : '当前语言版本及其章节的运营操作；切换语言可查看对应版本日志'}
      </div>

      <Table className="tbl" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ width: 160 }}>时间</th>
            <th style={{ width: 100 }}>类型</th>
            <th style={{ width: 130 }}>来源 / 操作人</th>
            <th>事件</th>
            <th style={{ width: 36 }} />
          </tr>
        </thead>
        <tbody>
          {/* 空 tbody 三态：加载失败（区别于「暂无」）/ 加载中 / 真空（total=0） */}
          {isError ? (
            <tr>
              <td colSpan={5} style={{ textAlign: 'center', color: 'var(--neg)', padding: 24 }}>
                <div style={{ marginBottom: 10 }}>
                  日志加载失败 —— 这不是「暂无日志」，请重试，或按响应 X-Request-Id 与服务端日志对账
                </div>
                {/* 错误态保留逃生入口：失败时 opTotal 退化为 0 → 底部分页器卸载，故此处给重试 + 回第 1 页，
                    避免运营卡在 page≥2 的错误页（retry:1 耗尽 + 不 refetchOnFocus，codex #171 W-2 P2）。 */}
                <div className="hstack" style={{ gap: 8, justifyContent: 'center' }}>
                  <button className="btn btn-sm" onClick={() => refetch()}>
                    <Icons.IconRefresh size={11} />
                    重试
                  </button>
                  {page > 1 && (
                    <button className="btn btn-sm btn-ghost" onClick={() => setPage(1)}>
                      回第 1 页
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ) : isLoading ? (
            <tr>
              <td
                colSpan={5}
                style={{ textAlign: 'center', color: 'var(--fg-faint)', padding: 24 }}
              >
                加载中…
              </td>
            </tr>
          ) : filtered.length === 0 ? (
            <tr>
              <td
                colSpan={5}
                style={{ textAlign: 'center', color: 'var(--fg-faint)', padding: 24 }}
              >
                {isSys
                  ? '该时间范围内暂无系统事件日志'
                  : isAll
                    ? '该时间范围内暂无操作与事件日志'
                    : '该时间范围内暂无运营操作日志'}
              </td>
            </tr>
          ) : null}
          {showRows
            ? filtered.map((it) => {
                const isOp = it.kind === 'op';
                const isOpen = expanded.has(it.id);
                return (
                  <Fragment key={it.id}>
                    <tr
                      onClick={() => isOp && toggle(it.id)}
                      style={{ cursor: isOp ? 'pointer' : 'default' }}
                    >
                      <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                        {it.time}
                      </td>
                      <td>
                        <span
                          className={`chip ${isOp ? 'chip-accent' : 'chip-info'}`}
                          style={{ fontSize: 10 }}
                        >
                          {isOp ? '运营操作' : (SYS_EVENT_TYPE_LABEL[it.eventType] ?? it.eventType)}
                        </span>
                      </td>
                      <td>
                        {it.kind === 'op' ? (
                          <div className="hstack" style={{ gap: 6 }}>
                            <div
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: 999,
                                background: `oklch(0.6 0.14 ${(it.who.charCodeAt(0) * 47) % 360})`,
                                color: 'white',
                                fontSize: 10,
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {it.who[0]?.toUpperCase()}
                            </div>
                            <span className="mono" style={{ fontSize: 11 }}>
                              {it.who}
                            </span>
                          </div>
                        ) : (
                          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                            {it.source}
                          </span>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'normal' }}>
                        <div style={{ fontSize: 12, fontWeight: isOp ? 500 : 400 }}>
                          {it.kind === 'op' ? (
                            it.summary
                          ) : (
                            <span
                              className="mono"
                              style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}
                            >
                              {it.event}
                            </span>
                          )}
                        </div>
                        {it.kind === 'sys' && (
                          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>
                            {it.result}
                          </div>
                        )}
                        {it.kind === 'op' && !isOpen && (
                          <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2 }}>
                            {it.diff.length} 项字段变更 · 点击展开 diff
                          </div>
                        )}
                      </td>
                      <td>
                        {isOp && (
                          <span style={{ color: 'var(--fg-faint)' }}>
                            {isOpen ? (
                              <Icons.IconChevUp size={12} />
                            ) : (
                              <Icons.IconChevDown size={12} />
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                    {it.kind === 'op' && isOpen && (
                      <tr>
                        <td colSpan={5} style={{ background: 'var(--bg-subtle)', padding: 0 }}>
                          <div style={{ padding: '10px 14px' }}>
                            {/* 统一日志显示规则：字段名/enum/*_id 值友好化（共享 ~/lib/audit-display）*/}
                            <AuditDiffTable
                              diff={it.diff}
                              fieldRefs={it.fieldRefs}
                              action={it.action}
                              operation={it.operation}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            : null}
        </tbody>
      </Table>
      {pageTotal > BOOK_LOG_PAGE_LIMIT && (
        <Pagination
          total={pageTotal}
          page={page}
          pageSize={BOOK_LOG_PAGE_LIMIT}
          onChange={setPage}
          totalMode="exact"
        />
      )}
    </Section>
  );
}
